import { useState, useEffect, useRef, useCallback, type ChangeEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    ButtonGroup,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import CalculateIcon from '@mui/icons-material/Calculate';
import RoofingIcon from '@mui/icons-material/Roofing';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TableChartIcon from '@mui/icons-material/TableChart';
import ArticleIcon from '@mui/icons-material/Article';
import SaveIcon from '@mui/icons-material/Save';
import LayersIcon from '@mui/icons-material/Layers';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Confirm, useDataProvider, useGetIdentity, useGetOne, useNotify } from 'react-admin';
import { EditableEstimateSection } from './EditableEstimateSection';
import { CrmLeadInput } from './CrmLeadInput';
import { AddressAutocomplete } from '@/components/AddressAutocomplete';
import { ContractorOwnerAssignControl } from '@/components/ContractorOwnerAssignControl';
import { ResourceNotFound } from '@/components/misc/ResourceNotFound';
import { authedFetch } from '@/providers/loginWithEmail/dataProvider';
import {
    CONTRACT_SIGNING_VOID_MESSAGE,
    isContractSigningActiveError,
} from '@/modules/projects/contract/contractSigningApi';
import { computeClientPricing } from './commissionPricing';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PeopleIcon from '@mui/icons-material/People';
import { QuoteAttachmentsTab } from './QuoteAttachmentsTab';
import { QuoteRelatedPeopleTab } from './QuoteRelatedPeopleTab';
import { ArchitectEngineerTab } from './ArchitectEngineerTab';
import {
    SiteHistoryPanel,
    aggregateSiteHistoryQuotes,
    selectableProjectsByAddress,
    type EstimatorProjectByAddress,
} from './SiteHistoryPanel';
import { saveProject, loadSavedProject, type SavedProject } from 'architect-software';
import {
    type QuoteAttachment,
    fileRefsToQuoteAttachments,
    fetchQuoteAttachmentBlob,
    isImageAttachment,
    toEstimatorFileRef,
} from '@/utils/quote_attachment_utils';
import { searchUsersByName } from '@/utils/user_search_utils';
import {
    findOrCreateContractorOwnerByName,
    getContractorOwner,
} from '@/utils/contractor_owner_utils';
import { ContractorOwner, Lead, Quote, User, UserType } from '@/types';
import { useBrowserTitle } from '@/utils/useBrowserTitle';
import type { CrmDataProvider } from '@/providers/types';
import { applyLeadToClientInfo } from './leadClientInfo';
import { projectPhasePath } from '@/modules/global_search/phaseRoutes';
import { quoteStatuses } from '@/root/defaultConfiguration';
import {
    type SteepRoofState,
    type FlatRoofState,
    defaultSteepRoof,
    defaultFlatRoof,
    steepRoofsFromDraft,
    flatRoofsFromDraft,
    steepScalarBindings,
    flatScalarBindings,
    steepRoofToApi,
    flatRoofToApi,
    totalSteepArea,
    totalFlatArea,
    totalWoodQuantities,
    weightedMarginPct,
} from './roofState';
import {
    assertUiDraftMatchesLocalStorageKeys,
    buildUiDraft,
    draftFromLegacyEstimationData,
} from './uiDraft';
import {
    fetchFirstProjectQuoteDraft,
    type ProjectQuoteListRow,
} from './projectQuotePrefill';
import { defaultPermitsHtml, defaultWoodHtml, scopeLinesToHtml } from './clientEstimateScopeHtml';
import { flatSystemLabel, roofSystemDisplay, steepSystemLabel } from './clientEstimateRoofSystem';
import {
    applySectionMargin,
    marginForJobAndOther,
    marginForRoofId,
} from './estimatorMargin';

// ── Right panel tab ids ───────────────────────────────────────────────────────

type RightTabId =
    | 'engineer'
    | 'cost'
    | 'attachments'
    | 'client-estimate'
    | 'related-people'
    | 'system-spec';

const TAB_COST = 'cost' as const;
const TAB_ATTACHMENTS = 'attachments' as const;
const TAB_CLIENT_ESTIMATE = 'client-estimate' as const;
const TAB_RELATED_PEOPLE = 'related-people' as const;
const TAB_SYSTEM_SPEC = 'system-spec' as const;
const TAB_ENGINEER = 'engineer' as const;

// ── Constants ─────────────────────────────────────────────────────────────────

const WORK_TYPES = ['Re-Roof', 'New Construction', 'Repairs'] as const;
type WorkType = typeof WORK_TYPES[number];

const ROOF_TYPES = ['Slope Roof', 'Flat Roof', 'Mixed Roof'] as const;
type RoofType = typeof ROOF_TYPES[number];

interface SteepSystemOption { id: string; label: string; ready: boolean; }
interface FlatSystemOption  { id: string; label: string; ready: boolean; }

const STEEP_SYSTEMS: SteepSystemOption[] = [
    { id: 'SHINGLE', label: 'Shingle', ready: true },
    { id: 'TILE',    label: 'Tile',    ready: true },
    { id: 'METAL',   label: 'Metal',   ready: true },
];

const FLAT_MEMBRANE_SYSTEMS: FlatSystemOption[] = [
    { id: 'BUILDUP',   label: 'GAF Built-Up',                      ready: true },
    { id: 'POLYGLASS', label: 'Polyglass Built-Up Self Adhered',    ready: true },
    { id: 'TPO',       label: 'GAF TPO',                           ready: true },
];

const FLAT_WATERPROOFING_SYSTEMS: FlatSystemOption[] = [
    { id: 'WATERPROOF-SB', label: 'Waterproofing Surface Seal SB', ready: true },
    { id: 'TREMCO',        label: 'Tremco Waterproofing',          ready: true },
    { id: 'HYDROSTOP',     label: 'Liquid Hydrostop',              ready: true },
];

const FLAT_SYSTEMS: FlatSystemOption[] = [
    ...FLAT_MEMBRANE_SYSTEMS,
    ...FLAT_WATERPROOFING_SYSTEMS,
];

interface BuildupInterPlyOption { id: string; label: string; description: string; }
interface BuildupCapOption      { id: string; label: string; description: string; }
interface BuildupInsOption      { id: string; label: string; description: string; }

const BUILDUP_INTERPLY_OPTIONS: BuildupInterPlyOption[] = [
    { id: 'BUILDUP-IP-R20',   label: 'Ruberoid 20 Smooth',      description: 'SBS-modified membrane, 20 mil — 140 SQFT/roll. Standard inter-ply.' },
    { id: 'BUILDUP-IP-SMOOTH',label: 'Ruberoid Mop Smooth',      description: 'SBS-modified smooth membrane — 95 SQFT/roll.' },
    { id: 'BUILDUP-IP-SM15',  label: 'Ruberoid Mop Smooth 1.5', description: 'SBS-modified 1.5 mm smooth membrane — 140 SQFT/roll.' },
];

const BUILDUP_CAP_OPTIONS: BuildupCapOption[] = [
    { id: 'BUILDUP-CAP-STD',    label: 'GAF Mineral Sur Cap (Standard)',    description: 'Standard mineral-surfaced cap sheet. 100 SQFT/roll.' },
    { id: 'BUILDUP-CAP-ENERGY', label: 'GAF Energy Mineral Sur Cap',        description: 'Cool-roof energy mineral cap. Lap seams painted + Topcoat Surface Seal.' },
];

const BUILDUP_EDGE_METALS = [
    { id: 'GAL', label: 'Galvalume', description: 'Standard galvalume steel flat edge. Most common spec.' },
    { id: 'COP', label: 'Copper',    description: 'Premium copper flat edge. Superior longevity.' },
] as const;

const BUILDUP_EDGE_SIZES = [
    { id: '3X3', label: '3×3', description: 'Standard profile — most residential flat roofs.' },
    { id: '4X4', label: '4×4', description: 'Wide profile — deeper fascia or architectural detail.' },
] as const;

const BUILDUP_PARAPET_METALS = [
    { id: 'GAL', label: 'Galvalume', description: 'Standard galvalume steel flashing.' },
    { id: 'COP', label: 'Copper',    description: 'Premium copper flashing.' },
] as const;

const BUILDUP_INS_OPTIONS: BuildupInsOption[] = [
    { id: '',                  label: 'None',                         description: 'Non-insulated — NOA System 1. #75 → inter-ply → cap.' },
    { id: 'BUILDUP-INS-POLY1', label: 'EnergyGuard Polyiso 1"',       description: 'NOA System 2. +Polyiso 1" mopped + Stratavent loose-laid. $12/board (16 sqft).' },
    { id: 'BUILDUP-INS-POLY2', label: 'EnergyGuard Polyiso 2"',       description: 'NOA System 2. +Polyiso 2" mopped + Stratavent loose-laid. $18/board (16 sqft).' },
    { id: 'BUILDUP-INS-POLY3', label: 'EnergyGuard Polyiso 3"',       description: 'NOA System 2. +Polyiso 3" mopped + Stratavent loose-laid. $24/board (16 sqft).' },
];

const TPO_INS_OPTIONS = [
    { id: 'NONE',      label: 'No Tapered Insulation',   slope: 0,    description: 'Flat base ISO only. Auto-qty: flat area ÷ 32 sqft/board.' },
    { id: 'TAPERED-8', label: 'Tapered — 1/8″ per ft',  slope: 0.05, description: 'Minimum positive drainage slope. Auto-qty: flat base × 1.05 factor.' },
    { id: 'TAPERED-4', label: 'Tapered — 1/4″ per ft',  slope: 0.15, description: 'Standard slope. Recommended for most flat roofs. Auto-qty: flat base × 1.15 factor.' },
];

const FLAT_SUBSTRATE_OPTIONS = [
    { id: 'WOOD',     label: 'Wood',     description: 'Wood deck / plywood sheathing. Standard residential spec.',           ready: true  },
    { id: 'CONCRETE', label: 'Concrete', description: 'Concrete deck. Matrix 307 primer, mopped base sheet, $1.50/sqft labor. NOA 25-0926.06.', ready: true },
    { id: 'METAL',    label: 'Metal',    description: 'Metal deck. Requires mechanical attachment or special adhesive.',      ready: false },
] as const;

interface UnderlaymentOption {
    id: string;
    label: string;
    description: string;
}

interface TileTypeOption {
    id: string;
    label: string;
    description: string;
}

const SHINGLE_UNDERLAYMENT_OPTIONS: UnderlaymentOption[] = [
    {
        id: 'SHINGLE-UL-1',
        label: '2× ASTM D226 #30',
        description: 'Two layers of mechanically fastened #30 felt with Tin Caps and 1¼″ Ring Shank Nails. Standard spec.',
    },
    {
        id: 'SHINGLE-UL-2',
        label: 'Tamko Moisture Guard SA',
        description: 'Single layer of Tamko Moisture Guard self-adhered underlayment. Enhanced waterproofing, no mechanical fasteners required.',
    },
    {
        id: 'SHINGLE-UL-3',
        label: 'Polyglass IR-Xe SA',
        description: 'Single layer of Polyglass IR-Xe self-adhered high-performance underlayment. Premium protection, fire-rated.',
    },
];

const TILE_TYPE_OPTIONS: TileTypeOption[] = [
    {
        id: 'TILE-TYPE-CONC',
        label: 'Concrete Tile',
        description: 'Boral concrete tile. Standard weight. Available in flat, S, and low-profile styles.',
    },
    {
        id: 'TILE-TYPE-CLAY',
        label: 'Clay Tile',
        description: 'Premium clay tile. Lighter weight, superior longevity. Available in S-tile and 2-piece barrel profiles.',
    },
];

const TILE_UNDERLAYMENT_OPTIONS: UnderlaymentOption[] = [
    {
        id: 'TILE-UL-1',
        label: 'Standard 1-Ply',
        description: 'Polyglass TU-Plus self-adhered cap sheet only. Standard warranty spec.',
    },
    {
        id: 'TILE-UL-2',
        label: 'Anchor 2-Ply',
        description: 'Polyglass Polyanchor mechanically fastened base sheet + TU-Plus cap sheet.',
    },
    {
        id: 'TILE-UL-3',
        label: 'MTS 2-Ply',
        description: 'Polyglass MTS self-adhered base sheet + TU-Plus cap sheet.',
    },
    {
        id: 'TILE-UL-4',
        label: 'Anchor + MTS 3-Ply',
        description: 'Polyanchor base + Polyglass MTS interply + TU-Plus cap sheet. Full triple-layer system.',
    },
    {
        id: 'TILE-UL-5',
        label: 'Double-Cap 3-Ply',
        description: 'Polyglass MTS base + two layers of TU-Plus cap sheet. Premium protection.',
    },
];

const TILE_FLASHING_METALS = [
    { id: 'GAL', label: 'Galvalume', description: 'Standard galvalume steel. Excellent corrosion resistance, most common spec.' },
    { id: 'COP', label: 'Copper',    description: 'Premium copper flashing. Superior longevity, natural patina finish.' },
] as const;

const TILE_FLASHING_EDGE_SIZES = [
    { id: '3X3', label: '3×3', description: 'Standard profile — suitable for most residential eave details.' },
    { id: '4X4', label: '4×4', description: 'Wide profile — used for deeper fascia boards or architectural details.' },
] as const;

const METAL_TYPE_OPTIONS = [
    { id: 'GAL', systemId: 'METAL-TYPE-GAL', label: 'Galvalume',  description: 'Galvalume standing seam metal panels (Drexel DMC 150 SS 24ga). Standard spec.', ready: true  },
    { id: 'ALU', systemId: 'METAL-TYPE-ALU', label: 'Aluminum',   description: 'Aluminum standing seam metal panels. Lightweight, corrosion-resistant.',          ready: true  },
] as const;

const METAL_UNDERLAYMENT_OPTIONS = [
    {
        id: 'METAL-UL-1PLY',
        label: '1-Ply — Polystick XFR',
        description: 'Single self-adhered XFR underlayment. Standard metal roofing spec.',
    },
    {
        id: 'METAL-UL-3PLY',
        label: '3-Ply — 2× ASTM D226 + XFR',
        description: '2 layers of mechanically fastened ASTM D226 #30 felt + Polystick XFR cap sheet. Premium system.',
    },
];

// ── Tile dynamic scope builders ───────────────────────────────────────────────

const TILE_BASE_SCOPE = [
    'Remove the existing roofing system down to sheathing or smooth workable surface. Haul away all roofing debris. Premises to be left clean upon completion.',
    'Install all new Lead Stack Flashing to all Plumbing Projections and Galvanized Exhaust Vents to all Ventilation Projections.',
];

const TILE_UL_SCOPE: Record<string, string> = {
    'TILE-UL-1': 'Install Polyglass TU-Plus self-adhering cap sheet over the entire roof area per manufacturer specifications.',
    'TILE-UL-2': 'Mechanically fasten Polyglass Polyanchor base sheet with Tin Caps and Ring Shank Nails. Install Polyglass TU-Plus self-adhering cap sheet over the entire roof area per manufacturer specifications.',
    'TILE-UL-3': 'Install Polyglass MTS self-adhering base sheet followed by Polyglass TU-Plus self-adhering cap sheet over the entire roof area per manufacturer specifications.',
    'TILE-UL-4': 'Mechanically fasten Polyglass Polyanchor base sheet with Tin Caps and Ring Shank Nails. Install Polyglass MTS self-adhering interply sheet followed by Polyglass TU-Plus self-adhering cap sheet per manufacturer specifications.',
    'TILE-UL-5': 'Install Polyglass MTS self-adhering base sheet followed by two layers of Polyglass TU-Plus self-adhering cap sheet over the entire roof area per manufacturer specifications.',
};

const TILE_TYPE_SCOPE: Record<string, string> = {
    'TILE-TYPE-CONC': 'Furnish and install Concrete Tile — Manufacturer: Boral. Tile Color: STATE IN WRITING. Install with polyurethane foam adhesive and mortar. Saw cut valleys for a clean tile finish. Install Galvalume Tile Termination Metal along all perimeter edges. Paint all lead stack flashing and vents to match tile color.',
    'TILE-TYPE-CLAY': 'Furnish and install Clay Tile. Tile Color: STATE IN WRITING. Install with polyurethane foam adhesive and mortar. Saw cut valleys for a clean tile finish. Finish all perimeter edges with matching concrete mortar. Paint all lead stack flashing and vents to match tile color.',
};

function buildTileFlashingScope(metal: string, size: string): string {
    const edgeSize = size === '4X4' ? '4×4' : '3×3';
    if (metal === 'COP') {
        return `Install Copper Drip Edge (${edgeSize}) to all eaves and perimeter edges. Install Galvalume Ridge Metal at all ridges. Install Copper Roll Valley Metal at all valleys. Install Copper Counter Flashing at all wall junctions where necessary.`;
    }
    return `Install Powder Coated Galvalume Drip Edge (${edgeSize}) to all eaves and perimeter edges. Install Galvalume Ridge Metal at all ridges. Install 26GA Galvalume Roll Valley Metal at all valleys. Install Galvalume Counter Flashing at all wall junctions where necessary.`;
}

function buildTileScopeLines(
    underlaymentId: string,
    tileTypeId: string,
    flashingMetal: string,
    flashingSize: string,
): string[] {
    return [
        ...TILE_BASE_SCOPE,
        TILE_UL_SCOPE[underlaymentId] ?? 'Install underlayment per manufacturer specifications.',
        buildTileFlashingScope(flashingMetal, flashingSize),
        TILE_TYPE_SCOPE[tileTypeId] ?? 'Furnish and install tile per manufacturer specifications.',
    ];
}

// ── Metal dynamic scope builders ──────────────────────────────────────────────

const METAL_UL_SCOPE: Record<string, string> = {
    'METAL-UL-1PLY': 'Install Polystick XFR self-adhering underlayment over the entire roof area per manufacturer specifications.',
    'METAL-UL-3PLY': 'Mechanically fasten 2 layers of Tamko #30 ASTM D226 Felt with Tin Caps and Ring Shank Nails. Install Polystick XFR self-adhering cap sheet over the entire roof area per manufacturer specifications.',
};

const METAL_TYPE_SCOPE: Record<string, string> = {
    'METAL-TYPE-GAL': 'Metal Roofing: Furnish and install Galvalume Standing Seam Metal Panels — Drexel DMC 150 SS 24ga over the entire roof area. Install matching 24ga Galvalume Ridge Cap at all ridges. Color: STATE IN WRITING.',
    'METAL-TYPE-ALU': 'Metal Roofing: Furnish and install Aluminum Standing Seam Metal Panels over the entire roof area. Install matching Aluminum Ridge Cap at all ridges. Color: STATE IN WRITING.',
};

function buildMetalScopeLines(underlaymentId: string, metalTypeSystemId: string): string[] {
    return [
        'Remove the above-described roofing down to sheathing or smooth workable surface. Haul away all roofing debris. Premises to be left clean.',
        'Install all new Lead Stack Flashing to all Plumbing Projections and Galvanized Exhaust Vents to all Ventilation Projections.',
        METAL_UL_SCOPE[underlaymentId] ?? 'Install underlayment per manufacturer specifications.',
        'Install Powder Coated Galvanized Drip Edge to all eaves and perimeter terminations. Furnish and install 26 Gauge galvanized roll valley metal at all valleys. Install Counter Flashing at all wall junctions where necessary.',
        METAL_TYPE_SCOPE[metalTypeSystemId] ?? 'Furnish and install metal roofing per manufacturer specifications.',
    ];
}

// ── Shingle dynamic scope builders ───────────────────────────────────────────

const SHINGLE_UL_SCOPE: Record<string, string> = {
    'SHINGLE-UL-1': 'Tin Cap 2 plies of #30 ASTM D226 Felt to sheathing with 1¼″ Ring Shank Nails and 1 5/8″ Tin Tags.',
    'SHINGLE-UL-2': 'Install Tamko Moisture Guard self-adhered underlayment over the entire roof area per manufacturer specifications.',
    'SHINGLE-UL-3': 'Install Polyglass IR-Xe self-adhered high-performance underlayment over the entire roof area per manufacturer specifications.',
};

function buildShingleScopeLines(underlaymentId: string): string[] {
    return [
        'Remove the above-described roofing down to sheathing or smooth workable surface. Haul away all roofing debris. Premises to be left clean.',
        SHINGLE_UL_SCOPE[underlaymentId] ?? 'Install underlayment per manufacturer specifications.',
        'Install all new Galvanized Exhaust Vents and Lead Stack Flashing to all Plumbing Projections.',
        'Install Powder Coated Galvanized Drip Edge to all eaves and perimeter terminations. Install Counter Flashing at all wall junctions where necessary.',
        'Shingle: Installation of desired 3D Architectural Shingle. Manufacturer: TAMKO Titan 160 mph. Color of Shingle: STATE IN WRITING.',
    ];
}

// ── Flat dynamic scope builders ───────────────────────────────────────────────

function buildFlatScopeLines(
    flatSystemId: string,
    substrate: string,
    insId: string,
    r20Qty: number,
    smoothQty: number,
    sm15Qty: number,
    capId: string,
    edgeMetal: string,
    edgeSize: string,
    // Polyglass
    polyglassIpQty = 1,
    polyglassEdgeMetal = 'GAL',
    polyglassEdgeSize = '3X3',
    // TPO sub-selections
    tpoAttachment = 'HM',
    tpoCoverboard = false,
    // WP SB warranty
    wpSbWarranty = '10',
): string[] {
    const subLabel = substrate === 'WOOD' ? 'wood deck' : substrate === 'CONCRETE' ? 'concrete deck' : 'metal deck';

    if (flatSystemId === 'BUILDUP') {
        const lines: string[] = [
            `Remove existing flat roofing system down to the ${subLabel}. Haul away all roofing debris. Premises to be left clean upon completion.`,
            substrate === 'CONCRETE'
                ? 'Apply GAF Primer Matrix 307 to the entire concrete deck surface.'
                : 'Mechanically fasten (1) ply of GAF GAFGLAS #75 Base Sheet with BUR Coil Nails and Tin Caps over the entire flat roof area.',
        ];

        if (insId === 'BUILDUP-INS-POLY1') {
            lines.push('Install EnergyGuard Polyiso 1″ rigid insulation boards fully adhered (mopped) to the base sheet with Type IV hot steep asphalt. Install Stratavent Perforated vent sheet loose-laid over the Polyiso layer.');
        } else if (insId === 'BUILDUP-INS-POLY2') {
            lines.push('Install EnergyGuard Polyiso 2″ rigid insulation boards fully adhered (mopped) to the base sheet with Type IV hot steep asphalt. Install Stratavent Perforated vent sheet loose-laid over the Polyiso layer.');
        } else if (insId === 'BUILDUP-INS-POLY3') {
            lines.push('Install EnergyGuard Polyiso 3″ rigid insulation boards fully adhered (mopped) to the base sheet with Type IV hot steep asphalt. Install Stratavent Perforated vent sheet loose-laid over the Polyiso layer.');
        }

        const plies: string[] = [];
        if (r20Qty > 0)    plies.push(`(${r20Qty}) ${r20Qty > 1 ? 'plies' : 'ply'} of Ruberoid 20 Smooth`);
        if (smoothQty > 0) plies.push(`(${smoothQty}) ${smoothQty > 1 ? 'plies' : 'ply'} of Ruberoid Mop Smooth`);
        if (sm15Qty > 0)   plies.push(`(${sm15Qty}) ${sm15Qty > 1 ? 'plies' : 'ply'} of Ruberoid Mop Smooth 1.5`);
        if (plies.length > 0) {
            lines.push(`Install ${plies.join(', ')} fully mopped in Type IV hot steep asphalt.`);
        }

        const capLabel = capId === 'BUILDUP-CAP-ENERGY'
            ? 'GAF Energy Mineral Surface Cap Sheet (lap seams painted with Energy Cap Lap Cement)'
            : 'GAF Mineral Surface Cap Sheet';
        lines.push(`Install (1) ply of ${capLabel} mopped with Type IV hot steep asphalt as the final surfacing layer.`);

        lines.push('Install Perlite Cant Strip at all parapet-to-roof junctions.');
        const edgeLabel = `${edgeMetal === 'COP' ? 'Copper' : 'Galvalume'} ${edgeSize === '4X4' ? '4×4' : '3×3'} Flat Edge Metal`;
        lines.push(`Install new ${edgeLabel} with 1¼″ Ring Shank Nails along all perimeter edges. Install L-Flashing and Counter Flashing at all wall junctions and parapets.`);
        lines.push('Install all new Lead Stack Flashing to all Plumbing Projections.');
        return lines;
    }

    if (flatSystemId === 'POLYGLASS') {
        const ipLabel = polyglassIpQty === 1 ? '(1) ply' : `(${polyglassIpQty}) plies`;
        const pgEdgeLabel = `${polyglassEdgeMetal === 'COP' ? 'Copper' : 'Galvalume'} ${polyglassEdgeSize === '4X4' ? '4×4' : '3×3'} Flat Edge Metal`;
        return [
            `Remove existing flat roofing system down to the ${subLabel}. Haul away all roofing debris. Premises to be left clean upon completion.`,
            'Mechanically fasten (1) ply of Polyglass Elastobase Base Sheet with Coil Nails and Tin Caps over the entire flat roof area.',
            `Install ${ipLabel} of Polyglass Elastoflex self-adhered SBS-modified inter-ply membrane.`,
            'Install (1) ply of Polyglass Polyflex self-adhered SBS-modified granulated cap sheet as the final surfacing layer.',
            'Install Perlite Cant Strip at all parapet-to-roof junctions.',
            `Install new ${pgEdgeLabel} with 1¼″ Ring Shank Nails along all perimeter edges. Install L-Flashing and Counter Flashing at all wall junctions and parapets.`,
            'Install all new Lead Stack Flashing to all Plumbing Projections.',
        ];
    }

    if (flatSystemId === 'HYDROSTOP') {
        const lines: string[] = [
            'Clean premises. Pressure clean the entire concrete deck surface.',
            'Apply GAF Hydrostop Primer and Multipurpose Primer to the entire surface.',
            'Apply Barrier Guard over the primed surface.',
            'Apply Portland Cement to all parapet walls (1 bag per 10 LF).',
            'Apply Sure Bond to cracks, joints, and penetrations.',
            'Apply (1st coat) GAF Premium Acrylic Hydrostop Base Coat to the entire surface.',
            'Install GAF Hydrostop Premium Coat Fabric embedded into the wet base coat.',
            'Apply (2nd coat) GAF Premium Acrylic Hydrostop Base Coat over the fabric.',
            'Apply (1st), (2nd), and (3rd) finish coats of GAF Premium Acrylic Hydrostop Topcoat.',
            'Apply Elastomeric Sealant and Silicone to all joints, seams, and penetrations.',
        ];
        return lines;
    }

    if (flatSystemId === 'TPO') {
        const isHM = tpoAttachment === 'HM';
        const lines: string[] = [
            'Remove existing flat roofing system down to the concrete deck. Haul away all roofing debris. Premises to be left clean upon completion.',
        ];
        if (isHM) {
            lines.push('Apply GAF Primer Matrix 307 to the entire concrete deck surface.');
            lines.push('Mechanically fasten (1) ply of GAFGLAS Ply 4 base sheet over the entire flat roof area.');
            lines.push('Install Tapered ISO and Flat Base ISO insulation boards fully adhered (mopped) with Type IV hot steep asphalt.');
        } else {
            lines.push('Install Tapered ISO and Flat Base ISO insulation boards fully adhered with OlyBond 500 two-part adhesive.');
        }
        if (tpoCoverboard) {
            lines.push('Install SECUROCK Gypsum-Fiber cover board (4×8) over the insulation layer for added protection and smooth substrate.');
        }
        lines.push(
            'Install EverGuard .060 TPO membrane fully adhered with EverGuard Bonding Adhesive over the entire roof surface.',
            'Clean all seams with EverGuard TPO Seam Cleaner and heat-weld all field and perimeter seams.',
            'Install Term Bar, Masonry Anchors, Stucco Stop/Counterflashing, and Cover Tape along all parapet walls.',
            'Apply H/S Prem Coat Fabric and U/C Prem Brush Acrylic at all wall and parapet transitions.',
            'Install GAF TPO Vent Boots at all round penetrations. Apply Water Block Mastic at all penetrations and drains.',
            'Install Roof Drain 4" (w/ strainer) at all drain locations. Install Scuppers as specified.',
            'Apply Flexseal Lap Sealant at all field seam edges.',
        );
        return lines;
    }

    if (flatSystemId === 'WATERPROOF-SB') {
        const is20 = wpSbWarranty === '20';
        const lines: string[] = [
            'Clean premises. Pressure clean the entire concrete deck surface.',
            'Run Scaraface machine over entire concrete surface to open the substrate and improve adhesion.',
            'Apply (1) unit of GAF Multipurpose Primer Part A&B (5 Gallon) per 1,100 SFT to the prepared surface.',
            is20
                ? 'Apply (1) unit of GAF Surface Seal SB (5 Gallon) per 100 SFT as waterproofing membrane (20-year system).'
                : 'Apply (1) unit of GAF Surface Seal SB (5 Gallon) per 160 SFT as waterproofing membrane.',
            'Embed GAF Mesh at all parapet walls and transitions (1 roll per 100 LF of parapet).',
            'Apply Flexseal Caulking to all joints, cracks, seams, and penetrations (1 unit per 100 LF of parapet).',
        ];
        return lines;
    }

    if (flatSystemId === 'TREMCO') {
        const lines: string[] = [
            'Clean premises. Pressure clean the entire concrete deck surface.',
            'Apply (1) TREMCO Vulkem #171 5GL quick dry primer to the entire surface.',
            'Apply TREMCO Vulkem #350 5GL Base Coat over the primed surface.',
            'Apply TREMCO Vulkem #351 5GL Finish Coat as the final waterproofing layer.',
        ];
        return lines;
    }

    return ['Flat roof scope of work to be confirmed.'];
}

function buildMachineryScopeLine(
    useScaraface: boolean,
    useTelehandler: boolean,
    useBoomLift: boolean,
    useBathroom: boolean,
): string | null {
    const mach: string[] = [];
    if (useScaraface)   mach.push('Scaraface machine');
    if (useTelehandler) mach.push('Telehandler');
    if (useBoomLift)    mach.push('Boom Lift');
    if (useBathroom)    mach.push('Portable Bathroom');
    return mach.length > 0 ? `Machinery on site: ${mach.join(', ')}.` : null;
}

function buildClientEstimateScopeLines(opts: {
    steepRoofs?: SteepRoofState[];
    flatRoofs?: FlatRoofState[];
    flatUseScaraface: boolean;
    flatUseTelehandler: boolean;
    flatUseBoomLift: boolean;
    flatUseBathroom: boolean;
}): string[] {
    const steepScopeLines = (opts.steepRoofs ?? []).flatMap(r => {
        const lines = r.systemId === 'SHINGLE'
            ? buildShingleScopeLines(r.shingleUnderlaymentId)
            : r.systemId === 'TILE'
            ? buildTileScopeLines(r.tileUnderlaymentId, r.tileTypeId, r.tileFlashingMetal, r.tileFlashingSize)
            : r.systemId === 'METAL'
            ? buildMetalScopeLines(r.metalUnderlaymentId, r.metalTypeId)
            : (SCOPE_TEXT[r.systemId] ?? []);
        return lines.length ? [`${r.label}:`, ...lines.map(l => `  ${l}`)] : [];
    });
    const flatScopeLines = (opts.flatRoofs ?? []).flatMap(r => {
        if (r.systemId === 'NONE-FLAT') return [];
        const lines = buildFlatScopeLines(
            r.systemId, r.substrate,
            r.builtupInsulationId, r.builtupR20Qty, r.builtupSmoothQty, r.builtupSm15Qty, r.builtupCapId,
            r.builtupEdgeMetal, r.builtupEdgeSize,
            r.polyglassIpQty, r.polyglassEdgeMetal, r.polyglassEdgeSize,
            r.tpoAttachment, r.tpoCoverboard, r.wpSbWarranty,
        );
        return lines.length ? [`${r.label}:`, ...lines.map(l => `  ${l}`)] : [];
    });
    const machineryLine = buildMachineryScopeLine(
        opts.flatUseScaraface, opts.flatUseTelehandler, opts.flatUseBoomLift, opts.flatUseBathroom,
    );
    const combined = [...steepScopeLines, ...flatScopeLines, ...(machineryLine ? [machineryLine] : [])];
    return combined.length > 0 ? combined : ['Scope of work to be confirmed.'];
}

/** After Calculate: auto-apply fresh HTML unless the user customized away from the last baseline. */
function reconcileSectionHtmlAfterCalc(
    current: string,
    generatedBaseline: string,
    nextGenerated: string,
): { display: string; generated: string; pending: string | null } {
    if (!current.trim() || current === generatedBaseline) {
        return { display: nextGenerated, generated: nextGenerated, pending: null };
    }
    if (nextGenerated === current) {
        return { display: current, generated: generatedBaseline, pending: null };
    }
    return { display: current, generated: generatedBaseline, pending: nextGenerated };
}

const MACHINERY_OPTIONS = [
    { key: 'scaraface',   label: 'Scaraface Machine', price: '$2,000 flat' },
    { key: 'telehandler', label: 'Telehandler',       price: '$2,500 flat' },
    { key: 'boom',        label: 'Boom Lift',         price: '$1,000 flat' },
    { key: 'bathroom',    label: 'Portable Bathroom', price: '$600 flat' },
] as const;

const SCOPE_TEXT: Record<string, string[]> = {
    'SHINGLE-2PLY': [
        'Remove the above-described roofing down to Sheathing or smooth workable surface. Haul away all roofing debris. Premises to be left clean.',
        'Tin Cap 2 ply of #30 ASTM Felt to Sheathing with 1¼" Ring Shank Nails and 1 5/8" Tin Tags.',
        'Install all new Galvanized Exhaust Vents and Lead Stack Flashing to Plumbing Projections.',
        'Install the following Termination Flashing: Powder Coated Galvanized Eave Drip to Eaves. Stop Flashing to Wall Junctions. Counter Flashing where necessary.',
        'Shingle: Installation of desired 3D Shingle. Manufacturer: TAMKO Titan 160 mph. Color of Shingle: STATE IN WRITING.',
    ],
    'TILE': [
        'Get Existing roof system down to smooth workable surface. Haul away all roofing debris. Premises to be left clean.',
        'Install all new Galvanized Exhaust Vents and Lead Stack Flashing to Plumbing Projections.',
        'Install the following Termination Flashing: Powder Coated Galvanized Eave Drip to Eaves. Stop Flashing to Wall Junctions. Counter Flashing where necessary.',
        'Furnish and install 26 Gauge 16" Wide galvanized metals at all valleys. Terminations to be installed with Plastic Cement, Ring Shank Nails and Asphalt Primers.',
        'Cap Sheet: Install Polyglass TU-Plus SBS modified self-adhering membrane per Manufacturer Specifications.',
        'Tile: Manufacture: Boral. Tile Color: SPECIFY IN WRITING. Install standard tile with polyurethane foam adhesive and mortar. Saw Cut valleys for a clean tile finish. Paint all lead stack flashing & vents to match tile color. Install hip ridge metal along ridges. Finish tile eaves, gables, valley with matching concrete mortar.',
    ],
};

// ── API types ─────────────────────────────────────────────────────────────────

interface LineItem {
    item_id: string; name: string; line_type: string;
    unit: string; unit_cost: number; quantity: number; total: number;
}
interface SectionResult {
    roof_id?: string;
    label?: string;
    system_id: string; system_name: string; lines: LineItem[];
    materials_total: number; labor_total: number;
    machinery_total: number; hard_cost: number;
}
interface EstimateResult {
    steep_roofs?: SectionResult[];
    flat_roofs?: SectionResult[];
    job?: SectionResult | null;
    steep: SectionResult | null; flat: SectionResult | null;
    combined_hard_cost: number; overhead_pct: number; overhead_amount: number;
    profit_pct: number; profit_amount: number; total: number; price_per_sqft: number;
}

interface ClientInfo {
    clientName: string; address: string; city: string;
    state: string; zipCode: string; email: string;
    phone: string; folio: string; yearBuilt: string;
    propertyType: string;
}

type ClientInfoMode = 'search' | 'create';
type ClientNameSource = 'owner' | 'contractor';

function effectiveContractor(
    owner: ContractorOwner | null,
    contractor: ContractorOwner | null,
    useAsContractor: boolean,
): ContractorOwner | null {
    return useAsContractor ? owner : (contractor ?? owner);
}

/** Name / email / phone shown on Client Estimate (email+phone always contractor). */
function syncEstimateClientContacts(
    prev: ClientInfo,
    owner: ContractorOwner | null,
    contractor: ContractorOwner | null,
    useAsContractor: boolean,
    nameSource: ClientNameSource,
): ClientInfo {
    const effective = effectiveContractor(owner, contractor, useAsContractor);
    const namePerson = nameSource === 'contractor' ? effective : owner;
    return {
        ...prev,
        clientName: namePerson?.name ?? '',
        email: effective?.email ?? '',
        phone: effective?.phone_number ?? '',
    };
}

function ownerFromProjectRow(row: EstimatorProjectByAddress): ContractorOwner | null {
    if (row.property_owner_id == null && !row.client_name?.trim()) return null;
    return {
        id: row.property_owner_id ?? 0,
        name: row.client_name || '',
        company_name: '',
        email: row.email || '',
        phone_number: row.phone || '',
    };
}

function contractorFromProjectRow(row: EstimatorProjectByAddress): ContractorOwner | null {
    if (row.contractor_id == null) return null;
    if (
        row.property_owner_id != null &&
        Number(row.contractor_id) === Number(row.property_owner_id)
    ) {
        return ownerFromProjectRow(row);
    }
    return {
        id: row.contractor_id,
        name: row.contractor_name || '',
        company_name: '',
        email: '',
        phone_number: '',
    };
}

function contactsFromProjectRow(row: EstimatorProjectByAddress): {
    owner: ContractorOwner | null;
    contractor: ContractorOwner | null;
    useAsContractor: boolean;
} {
    const owner = ownerFromProjectRow(row);
    const useAsContractor =
        row.contractor_id == null ||
        row.property_owner_id == null ||
        Number(row.contractor_id) === Number(row.property_owner_id);
    return {
        owner,
        contractor: useAsContractor ? null : contractorFromProjectRow(row),
        useAsContractor,
    };
}

/** Slim contact snapshot for localStorage / ui_draft (id + display fields only). */
function slimContractorOwner(person: ContractorOwner | null | undefined): ContractorOwner | null {
    if (!person || typeof person !== 'object') return null;
    const id = Number(person.id);
    const name = String(person.name ?? '').trim();
    if (!name && !(id > 0)) return null;
    return {
        id: Number.isFinite(id) ? id : 0,
        name,
        company_name: String(person.company_name ?? ''),
        email: String(person.email ?? ''),
        phone_number: String(person.phone_number ?? ''),
    };
}

interface ExtraLine {
    id: string;
    name: string;
    line_type: 'Material' | 'Labor' | 'Machinery';
    unit: string;
    quantity: number;
    unit_cost: number;
    section: 'steep' | 'flat' | 'other';
}

function sectionHardCost(
    section: SectionResult,
    overrides: Record<string, number>,
    unitCostOverrides: Record<string, number>,
    disabledItemIds: string[] = [],
): number {
    let hardCost = 0;
    for (const line of section.lines) {
        if (disabledItemIds.includes(line.item_id)) continue;
        const qty  = overrides[line.item_id]      ?? Math.ceil(line.quantity);
        const cost = unitCostOverrides[line.item_id] ?? line.unit_cost;
        hardCost += qty * cost;
    }
    return hardCost;
}

function areaForRoofId(
    roofId: string | undefined,
    steepRoofs: SteepRoofState[],
    flatRoofs: FlatRoofState[],
): number {
    const steep = steepRoofs.find(r => r.id === roofId);
    if (steep) return steep.area;
    const flat = flatRoofs.find(r => r.id === roofId);
    if (flat) return flat.area;
    return 0;
}

function computeAdjusted(
    result: EstimateResult | null,
    overrides: Record<string, number>,
    unitCostOverrides: Record<string, number>,
    extras: ExtraLine[],
    steepRoofs: SteepRoofState[],
    flatRoofs: FlatRoofState[],
    disabledItemIds: string[] = [],
): { adjustedHardCost: number; adjustedTotal: number; adjustedOverhead: number; adjustedProfit: number } {
    if (!result) return { adjustedHardCost: 0, adjustedTotal: 0, adjustedOverhead: 0, adjustedProfit: 0 };

    let adjustedHardCost = 0;
    let adjustedOverhead = 0;
    let adjustedProfit = 0;

    const roofSections = [
        ...(result.steep_roofs ?? (result.steep ? [result.steep] : [])),
        ...(result.flat_roofs ?? (result.flat ? [result.flat] : [])),
    ];
    for (const section of roofSections) {
        const hard = sectionHardCost(section, overrides, unitCostOverrides, disabledItemIds);
        const m = marginForRoofId(section.roof_id, steepRoofs, flatRoofs);
        const { overhead, profit } = applySectionMargin(hard, m.overhead_pct, m.profit_pct);
        adjustedHardCost += hard;
        adjustedOverhead += overhead;
        adjustedProfit += profit;
    }

    if (result.job) {
        const hard = sectionHardCost(result.job, overrides, unitCostOverrides, disabledItemIds);
        const m = marginForJobAndOther(steepRoofs, flatRoofs);
        const { overhead, profit } = applySectionMargin(hard, m.overhead_pct, m.profit_pct);
        adjustedHardCost += hard;
        adjustedOverhead += overhead;
        adjustedProfit += profit;
    }

    for (const ex of extras) {
        const hard = ex.quantity * ex.unit_cost;
        const m = ex.section === 'steep'
            ? weightedMarginPct(steepRoofs)
            : ex.section === 'flat'
            ? weightedMarginPct(flatRoofs)
            : marginForJobAndOther(steepRoofs, flatRoofs);
        const { overhead, profit } = applySectionMargin(hard, m.overhead_pct, m.profit_pct);
        adjustedHardCost += hard;
        adjustedOverhead += overhead;
        adjustedProfit += profit;
    }

    return {
        adjustedHardCost,
        adjustedOverhead,
        adjustedProfit,
        adjustedTotal: adjustedHardCost + adjustedOverhead + adjustedProfit,
    };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const toTitleCase = (name: string) =>
    name.toLowerCase().split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

interface CostBreakdownLineSnapshot {
    item_id: string;
    name: string;
    line_type: string;
    unit: string;
    calculated_quantity: number;
    calculated_unit_cost: number;
    calculated_total: number;
    quantity: number;
    unit_cost: number;
    total: number;
}

interface CostBreakdownSectionSnapshot {
    system_id: string;
    system_name: string;
    lines: CostBreakdownLineSnapshot[];
    materials_total: number;
    labor_total: number;
    machinery_total: number;
    hard_cost: number;
    adjusted_hard_cost: number;
}

function buildCostBreakdownSection(
    section: SectionResult | null | undefined,
    overrides: Record<string, number>,
    unitCostOverrides: Record<string, number>,
    disabledItemIds: string[] = [],
): CostBreakdownSectionSnapshot | null {
    if (!section) return null;
    const lines: CostBreakdownLineSnapshot[] = section.lines
        .filter(line => !disabledItemIds.includes(line.item_id))
        .map(line => {
        const quantity = overrides[line.item_id] ?? Math.ceil(line.quantity);
        const unit_cost = unitCostOverrides[line.item_id] ?? line.unit_cost;
        return {
            item_id: line.item_id,
            name: line.name,
            line_type: line.line_type,
            unit: line.unit,
            calculated_quantity: line.quantity,
            calculated_unit_cost: line.unit_cost,
            calculated_total: line.total,
            quantity,
            unit_cost,
            total: quantity * unit_cost,
        };
    });
    const adjusted_hard_cost = lines.reduce((sum, l) => sum + l.total, 0);
    return {
        system_id: section.system_id,
        system_name: section.system_name,
        lines,
        materials_total: section.materials_total,
        labor_total: section.labor_total,
        machinery_total: section.machinery_total,
        hard_cost: section.hard_cost,
        adjusted_hard_cost,
    };
}

function buildEstimationData(params: {
    clientInfo: ClientInfo;
    clientInfoMode: ClientInfoMode;
    selectedProjectId: number | null;
    selectedProjectNo: number | null;
    selectedOwner: ContractorOwner | null;
    selectedContractor: ContractorOwner | null;
    selectedCrmLead?: Lead | null;
    useAsContractor: boolean;
    clientNameSource?: ClientNameSource;
    workType: WorkType;
    roofType: RoofType | null;
    stories: number;
    showSteep: boolean;
    showFlat: boolean;
    steepSystemId: string;
    steepLabel: string;
    flatSystemId: string;
    flatLabel: string;
    flatSubstrate: string;
    flatScuppers: number;
    flatDrains: number;
    shingleUnderlaymentId: string;
    builtupInsulationId: string;
    builtupR20Qty: number;
    builtupSmoothQty: number;
    builtupSm15Qty: number;
    builtupCapId: string;
    builtupEdgeMetal: string;
    builtupEdgeSize: string;
    builtupParapetMetal: string;
    builtupParapetCoping: boolean;
    polyglassIpQty: number;
    polyglassEdgeMetal: string;
    polyglassEdgeSize: string;
    flatUseScaraface: boolean;
    flatUseTelehandler: boolean;
    flatUseBoomLift: boolean;
    flatUseBathroom: boolean;
    wpSbWarranty: string;
    tileUnderlaymentId: string;
    tileTypeId: string;
    tileFlashingMetal: string;
    tileFlashingSize: string;
    metalUnderlaymentId: string;
    metalTypeId: string;
    metalTypeSystemId: string;
    result: EstimateResult | null;
    lineOverrides: Record<string, number>;
    unitCostOverrides: Record<string, number>;
    extraLines: ExtraLine[];
    disabledItemIds?: string[];
    adjustedHardCost: number;
    adjustedOverhead: number;
    adjustedProfit: number;
    adjustedTotal: number;
    clientPrice: number;
    commissionPct: number;
    commissionAmount: number;
    netToCompany: number;
    steepRoofs?: SteepRoofState[];
    flatRoofs?: FlatRoofState[];
    attachments?: QuoteAttachment[];
    relatedQuoteFiles?: QuoteAttachment[];
    viewFiles?: QuoteAttachment[];
    activeSteepIdx?: number;
    activeFlatIdx?: number;
    uiDraft?: ReturnType<typeof buildUiDraft>;
    architectProject?: SavedProject | null;
    scopeHtml?: string;
    woodHtml?: string;
    permitsHtml?: string;
    permitsHidden?: boolean;
    completionDays?: number;
}) {
    const totalArea =
        (params.showSteep ? totalSteepArea(params.steepRoofs ?? []) : 0) +
        (params.showFlat ? totalFlatArea(params.flatRoofs ?? []) : 0);
    const marginRoofs = [
        ...(params.showSteep ? (params.steepRoofs ?? []) : []),
        ...(params.showFlat ? (params.flatRoofs ?? []) : []),
    ];
    const weightedMargin = weightedMarginPct(marginRoofs);
    const woodTotals = totalWoodQuantities(
        params.steepRoofs, params.flatRoofs,
        { includeSteep: params.showSteep, includeFlat: params.showFlat },
    );

    const steepBreakdown = buildCostBreakdownSection(
        params.result?.steep,
        params.lineOverrides,
        params.unitCostOverrides,
        params.disabledItemIds,
    );
    const flatBreakdown = buildCostBreakdownSection(
        params.result?.flat,
        params.lineOverrides,
        params.unitCostOverrides,
        params.disabledItemIds,
    );

    const sectionExtras = (section: ExtraLine['section']) =>
        params.extraLines.filter(e => e.section === section);

    return {
        version: 1,
        saved_at: new Date().toISOString(),
        client_information: {
            client_name: params.clientInfo.clientName,
            address: params.clientInfo.address,
            city: params.clientInfo.city,
            state: params.clientInfo.state,
            zip_code: params.clientInfo.zipCode,
            email: params.clientInfo.email,
            phone: params.clientInfo.phone,
            folio: params.clientInfo.folio,
            year_built: params.clientInfo.yearBuilt,
            property_type: params.clientInfo.propertyType,
            client_info_mode: params.clientInfoMode,
            project_id: params.selectedProjectId,
            project_no: params.selectedProjectNo,
            property_owner_id: params.selectedOwner?.id ? Number(params.selectedOwner.id) : null,
            contractor_id: params.useAsContractor
                ? (params.selectedOwner?.id ? Number(params.selectedOwner.id) : null)
                : (params.selectedContractor?.id ? Number(params.selectedContractor.id) : null),
            use_as_contractor: params.useAsContractor,
            client_name_source: params.clientNameSource ?? 'owner',
            lead_id: params.selectedCrmLead?.id ? Number(params.selectedCrmLead.id) : null,
            property_owner: slimContractorOwner(params.selectedOwner),
            contractor: params.useAsContractor
                ? slimContractorOwner(params.selectedOwner)
                : slimContractorOwner(params.selectedContractor),
        },
        project_context: {
            work_type: params.workType,
            roof_type: params.roofType ?? '',
            stories: params.stories,
        },
        roof_system: {
            show_steep: params.showSteep,
            show_flat: params.showFlat,
            steep_system_id: params.steepSystemId,
            steep_label: params.steepLabel,
            flat_system_id: params.flatSystemId,
            flat_label: params.flatLabel,
            roof_system: roofSystemDisplay({
                showSteep: params.showSteep,
                showFlat: params.showFlat,
                steepSystemId: params.steepSystemId,
                flatSystemId: params.flatSystemId,
                steepLabel: params.steepLabel,
                flatLabel: params.flatLabel,
            }),
            flat_substrate: params.flatSubstrate,
            flat_scuppers: params.flatScuppers,
            flat_drains: params.flatDrains,
            shingle_underlayment_id: params.shingleUnderlaymentId,
            tile_underlayment_id: params.tileUnderlaymentId,
            tile_type_id: params.tileTypeId,
            tile_flashing_metal: params.tileFlashingMetal,
            tile_flashing_size: params.tileFlashingSize,
            metal_underlayment_id: params.metalUnderlaymentId,
            metal_type_id: params.metalTypeId,
            metal_type_system_id: params.metalTypeSystemId,
            builtup_insulation_id: params.builtupInsulationId,
            builtup_r20_qty: params.builtupR20Qty,
            builtup_smooth_qty: params.builtupSmoothQty,
            builtup_sm15_qty: params.builtupSm15Qty,
            builtup_cap_id: params.builtupCapId,
            builtup_edge_metal: params.builtupEdgeMetal,
            builtup_edge_size: params.builtupEdgeSize,
            buildup_edge_id: `BUILDUP-EDGE-${params.builtupEdgeMetal}-${params.builtupEdgeSize}`,
            builtup_parapet_metal: params.builtupParapetMetal,
            builtup_parapet_coping: params.builtupParapetCoping,
            polyglass_ip_qty: params.polyglassIpQty,
            polyglass_edge_metal: params.polyglassEdgeMetal,
            polyglass_edge_size: params.polyglassEdgeSize,
            polyglass_edge_id: `BUILDUP-EDGE-${params.polyglassEdgeMetal}-${params.polyglassEdgeSize}`,
            flat_use_scaraface: params.flatUseScaraface,
            flat_use_telehandler: params.flatUseTelehandler,
            flat_use_boom_lift: params.flatUseBoomLift,
            flat_use_bathroom: params.flatUseBathroom,
            wp_sb_warranty: params.wpSbWarranty,
        },
        measurements: {
            steep_area: totalSteepArea(params.steepRoofs ?? []),
            steep_edge: (params.steepRoofs ?? []).reduce((s, r) => s + r.edge, 0),
            ridge: (params.steepRoofs ?? []).reduce((s, r) => s + r.ridge, 0),
            valley: (params.steepRoofs ?? []).reduce((s, r) => s + r.valley, 0),
            steep_parapet: (params.steepRoofs ?? []).reduce((s, r) => s + r.parapet, 0),
            flat_area: totalFlatArea(params.flatRoofs ?? []),
            flat_edge: (params.flatRoofs ?? []).reduce((s, r) => s + r.edge, 0),
            flat_parapet: (params.flatRoofs ?? []).reduce((s, r) => s + r.parapet, 0),
            wood_plywood_sheets: woodTotals.plywoodSheets,
            wood_lumber_pieces: woodTotals.lumberPieces,
            plumbing_projections: [...(params.steepRoofs ?? []), ...(params.flatRoofs ?? [])]
                .reduce((s, r) => s + r.plumbingProjections, 0),
            ventilation_projections: [...(params.steepRoofs ?? []), ...(params.flatRoofs ?? [])]
                .reduce((s, r) => s + r.ventilationProjections, 0),
            overhead_pct: weightedMargin.overhead_pct,
            profit_pct: weightedMargin.profit_pct,
        },
        cost_sheet: {
            calculation: params.result,
            cost_breakdown: {
                steep: steepBreakdown,
                flat: flatBreakdown,
                steep_additional_items: sectionExtras('steep'),
                flat_additional_items: sectionExtras('flat'),
            },
            additional_items: params.extraLines.filter(e => e.section === 'other'),
            internal_summary: {
                hard_cost: params.adjustedHardCost,
                overhead_pct: params.result?.overhead_pct ?? 0,
                overhead_amount: params.adjustedOverhead,
                profit_pct: params.result?.profit_pct ?? 0,
                profit_amount: params.adjustedProfit,
                subtotal_before_rounding: params.adjustedTotal,
                price_before_commission: Math.ceil(params.adjustedTotal),
                client_price: params.clientPrice,
                commission_pct: params.commissionPct,
                commission_amount: params.commissionAmount,
                net_to_company: params.netToCompany,
                margin_pct:
                    params.clientPrice > 0
                        ? Math.round(
                              ((params.clientPrice - params.adjustedHardCost) / params.clientPrice) * 100,
                          )
                        : 0,
                price_per_sqft: totalArea > 0 ? params.clientPrice / totalArea : null,
            },
        },
        attachments: (params.attachments ?? []).map(toEstimatorFileRef),
        related_quote_files: (params.relatedQuoteFiles ?? []).map(toEstimatorFileRef),
        view_files: (params.viewFiles ?? []).map(toEstimatorFileRef),
        architect_project: params.architectProject ?? null,
        ui_draft: params.uiDraft ?? null,
        scope_html: params.scopeHtml ?? '',
        wood_html: params.woodHtml ?? '',
        permits_html: params.permitsHtml ?? '',
        permits_hidden: Boolean(params.permitsHidden),
        completion_days: params.completionDays ?? 60,
    };
}

async function callCalculate(payload: object): Promise<EstimateResult> {
    const res = await authedFetch('/estimator/calculate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Calculation failed');
    return res.json();
}

// ── Small form components ─────────────────────────────────────────────────────

const SectionCard = ({
    step, title, children, complete,
}: { step: number; title: string; children: React.ReactNode; complete?: boolean }) => (
    <Card variant="outlined" sx={{ mb: 2, borderColor: complete ? 'success.light' : 'divider' }}>
        <CardContent sx={{ pb: '16px !important' }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                <Box sx={{
                    width: 26, height: 26, borderRadius: '50%',
                    bgcolor: complete ? 'success.main' : 'primary.main',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                    {complete
                        ? <CheckCircleIcon sx={{ fontSize: 16, color: 'white' }} />
                        : <Typography variant="caption" color="white" fontWeight={700}>{step}</Typography>}
                </Box>
                <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
            </Stack>
            {children}
        </CardContent>
    </Card>
);

const SystemPicker = <T extends { id: string; label: string; ready: boolean }>({
    options, value, onChange, lockedIds = {}, onLockedClick,
}: {
    options: T[];
    value: string;
    onChange: (id: string) => void;
    /** Map of option id → note to show when that option is substrate-locked. */
    lockedIds?: Record<string, string>;
    onLockedClick?: (id: string) => void;
}) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {options.map(opt => {
            const lockNote = lockedIds[opt.id];
            const isLocked = Boolean(lockNote);
            const isDisabled = !opt.ready || isLocked;
            return (
                <Box key={opt.id}
                    onClick={() => {
                        if (isLocked) { onLockedClick?.(opt.id); return; }
                        if (opt.ready) onChange(opt.id);
                    }}
                    sx={{
                        flex: 1, textAlign: 'center',
                        px: 1.5, py: 0.75, borderRadius: 1.5, border: 2,
                        borderColor: value === opt.id ? 'primary.main' : isLocked ? 'warning.light' : 'divider',
                        bgcolor: value === opt.id ? 'primary.main'
                               : isLocked ? 'warning.50'
                               : opt.ready ? 'background.paper' : 'grey.100',
                        color: value === opt.id ? 'white' : isDisabled ? 'text.disabled' : 'text.primary',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        transition: 'all 0.12s',
                        '&:hover': !isDisabled && value !== opt.id ? { borderColor: 'primary.light' } : {},
                    }}>
                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13 }}>{opt.label}</Typography>
                    {isLocked && (
                        <Typography variant="caption" sx={{ fontSize: 10, color: 'warning.dark' }}>
                            {lockNote}
                        </Typography>
                    )}
                    {!opt.ready && !isLocked && (
                        <Typography variant="caption" sx={{ fontSize: 10, color: 'text.disabled' }}>
                            Coming soon
                        </Typography>
                    )}
                </Box>
            );
        })}
    </Box>
);

const NumField = ({
    label, value, unit, onChange, required,
}: { label: string; value: number; unit: string; onChange: (v: number) => void; required?: boolean }) => (
    <TextField
        label={label} type="number" size="small"
        value={value || ''}
        onChange={e => onChange(parseFloat(e.target.value) || 0)}
        InputProps={{ endAdornment: <InputAdornment position="end">{unit}</InputAdornment> }}
        inputProps={{ min: 0, step: 1 }}
        required={required} fullWidth
    />
);

const SummaryRow = ({
    label, value, bold, color, size = 'body2',
}: { label: string; value: number; bold?: boolean; color?: string; size?: 'body2' | 'h6' }) => (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant={size} fontWeight={bold ? 700 : 400} color={color ?? 'text.secondary'}>
            {label}
        </Typography>
        <Typography variant={size} fontWeight={bold ? 700 : 400} fontFamily="monospace" color={color ?? 'text.secondary'}>
            {fmt(value)}
        </Typography>
    </Stack>
);

// ── Cost Sheet (internal view) ────────────────────────────────────────────────

const TYPE_COLORS: Record<string, 'primary' | 'success' | 'warning'> = {
    Material: 'primary', Labor: 'success', Machinery: 'warning',
};

const EditableSectionBreakdown = ({
    section, title, sectionKey, overrides, onOverride,
    unitCostOverrides, onUnitCostOverride, onResetLine,
    disabledItemIds, onDisableCatalog, onEnableCatalog,
    extras, onAdd, onUpdate, onRemove,
    overhead_pct, profit_pct,
    areaSqft = 0,
    commissionPct = 0,
}: {
    section: SectionResult; title: string; sectionKey: ExtraLine['section'];
    overrides: Record<string, number>;
    onOverride: (itemId: string, qty: number) => void;
    unitCostOverrides: Record<string, number>;
    onUnitCostOverride: (itemId: string, cost: number) => void;
    onResetLine: (itemId: string) => void;
    disabledItemIds: string[];
    onDisableCatalog: (itemId: string) => void;
    onEnableCatalog: (itemId: string) => void;
    extras: ExtraLine[];
    onAdd: () => void;
    onUpdate: (id: string, field: keyof ExtraLine, value: string | number) => void;
    onRemove: (id: string) => void;
    overhead_pct: number;
    profit_pct: number;
    /** Roof area for Price / sqft (omit for job-site / non-roof sections). */
    areaSqft?: number;
    commissionPct?: number;
}) => {
    const sectionExtras = extras.filter(e => e.section === sectionKey);
    const activeLines = section.lines.filter(l => !disabledItemIds.includes(l.item_id));
    const adjustedSectionTotal = activeLines.reduce((sum, l) => {
        return sum + (overrides[l.item_id] ?? Math.ceil(l.quantity)) * (unitCostOverrides[l.item_id] ?? l.unit_cost);
    }, 0) + sectionExtras.reduce((s, e) => s + e.quantity * e.unit_cost, 0);
    const { overhead: sectionOverhead, profit: sectionProfit } = applySectionMargin(
        adjustedSectionTotal, overhead_pct, profit_pct,
    );
    const sectionAdjustedTotal = adjustedSectionTotal + sectionOverhead + sectionProfit;
    const { clientPrice: sectionClientPrice } = computeClientPricing(
        sectionAdjustedTotal,
        commissionPct,
    );

    const pricePerSqft = areaSqft > 0 ? sectionClientPrice / areaSqft : null;

    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
                sx={{ mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}>
                {title} — {section.system_name}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell sx={{ fontWeight: 700, fontSize: 12, py: 0.75 }}>Item</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, fontSize: 12, py: 0.75, width: 36 }}>T</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12, py: 0.75, width: 130 }}>Qty</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12, py: 0.75, width: 80 }}>$/Unit</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12, py: 0.75, width: 90 }}>Total</TableCell>
                            <TableCell sx={{ width: 72 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {/* Calculated lines — editable qty (disabled lines stay visible, excluded from totals) */}
                        {section.lines.map(line => {
                            const disabled = disabledItemIds.includes(line.item_id);
                            const qty       = overrides[line.item_id]         ?? Math.ceil(line.quantity);
                            const unitCost  = unitCostOverrides[line.item_id] ?? line.unit_cost;
                            const lineTotal = qty * unitCost;
                            const modified  = !disabled && (overrides[line.item_id] !== undefined || unitCostOverrides[line.item_id] !== undefined);
                            return (
                                <TableRow
                                    key={line.item_id}
                                    hover={!disabled}
                                    sx={disabled
                                        ? { bgcolor: 'action.hover', opacity: 0.55 }
                                        : modified ? { bgcolor: 'warning.50' } : {}}
                                >
                                    <TableCell sx={{ fontSize: 12, py: 0.25 }}>
                                        <Stack direction="row" alignItems="center" spacing={0.75}>
                                            <span style={disabled ? { textDecoration: 'line-through' } : undefined}>{line.name}</span>
                                            {disabled && (
                                                <Chip label="Off" size="small" variant="outlined"
                                                    sx={{ fontSize: 9, height: 18 }} />
                                            )}
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="center" sx={{ py: 0.25 }}>
                                        <Chip label={line.line_type[0]} size="small" title={line.line_type}
                                            color={TYPE_COLORS[line.line_type] ?? 'default'}
                                            variant="outlined" sx={{ fontSize: 10, height: 18, minWidth: 22 }} />
                                    </TableCell>
                                    <TableCell align="right" sx={{ py: 0.25 }}>
                                        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5}>
                                            <TextField
                                                type="number" size="small" variant="outlined"
                                                value={qty}
                                                disabled={disabled}
                                                onChange={e => onOverride(line.item_id, parseFloat(e.target.value) || 0)}
                                                inputProps={{ min: 0, step: 0.5, style: { textAlign: 'right', padding: '2px 6px', fontSize: 12, width: 70 } }}
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                                            />
                                            <Typography sx={{ fontSize: 11, color: 'text.disabled', minWidth: 28 }}>{line.unit}</Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="right" sx={{ py: 0.25 }}>
                                        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5}>
                                            {!disabled && unitCostOverrides[line.item_id] !== undefined && (
                                                <Typography sx={{ fontSize: 9, color: 'success.main', fontWeight: 700, lineHeight: 1 }}>DEAL</Typography>
                                            )}
                                            <TextField
                                                type="number" size="small" variant="outlined"
                                                value={unitCost}
                                                disabled={disabled}
                                                onChange={e => onUnitCostOverride(line.item_id, parseFloat(e.target.value) || 0)}
                                                inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', padding: '2px 6px', fontSize: 12, width: 68 } }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 1,
                                                        ...(!disabled && unitCostOverrides[line.item_id] !== undefined && { '& fieldset': { borderColor: 'success.main' } }),
                                                    },
                                                }}
                                            />
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="right" sx={{
                                        fontFamily: 'monospace', fontSize: 12, py: 0.25, fontWeight: 600,
                                        color: disabled ? 'text.disabled' : modified ? 'warning.dark' : 'inherit',
                                        textDecoration: disabled ? 'line-through' : undefined,
                                    }}>
                                        {fmt(lineTotal)}
                                    </TableCell>
                                    <TableCell sx={{ py: 0.25 }}>
                                        <Stack direction="row" spacing={0} justifyContent="flex-end">
                                            {!disabled && modified && (
                                                <IconButton size="small" title="Reset to calculated"
                                                    onClick={() => onResetLine(line.item_id)}
                                                    sx={{ p: 0.25, color: 'text.disabled' }}>
                                                    ↺
                                                </IconButton>
                                            )}
                                            <IconButton
                                                size="small"
                                                title={disabled ? 'Include in cost' : 'Exclude from cost'}
                                                onClick={() => disabled
                                                    ? onEnableCatalog(line.item_id)
                                                    : onDisableCatalog(line.item_id)}
                                                sx={{ p: 0.25, color: disabled ? 'text.disabled' : 'text.secondary' }}
                                            >
                                                {disabled
                                                    ? <VisibilityOffIcon sx={{ fontSize: 16 }} />
                                                    : <VisibilityIcon sx={{ fontSize: 16 }} />}
                                            </IconButton>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })}

                        {/* Extra lines */}
                        {sectionExtras.map(extra => (
                            <TableRow key={extra.id} sx={{ bgcolor: 'success.50' }}>
                                <TableCell sx={{ py: 0.25 }}>
                                    <TextField size="small" variant="standard" placeholder="Description"
                                        value={extra.name}
                                        onChange={e => onUpdate(extra.id, 'name', e.target.value)}
                                        inputProps={{ style: { fontSize: 12 } }}
                                        sx={{ minWidth: 140 }} />
                                </TableCell>
                                <TableCell align="center" sx={{ py: 0.25 }}>
                                    <Select size="small" value={extra.line_type} variant="standard"
                                        onChange={e => onUpdate(extra.id, 'line_type', e.target.value)}
                                        sx={{ fontSize: 11, minWidth: 60 }}>
                                        <MenuItem value="Material"><Chip label="M" size="small" color="primary" variant="outlined" sx={{ fontSize: 10, height: 18, cursor: 'pointer' }} /></MenuItem>
                                        <MenuItem value="Labor"><Chip label="L" size="small" color="success" variant="outlined" sx={{ fontSize: 10, height: 18, cursor: 'pointer' }} /></MenuItem>
                                        <MenuItem value="Machinery"><Chip label="Mc" size="small" color="warning" variant="outlined" sx={{ fontSize: 10, height: 18, cursor: 'pointer' }} /></MenuItem>
                                    </Select>
                                </TableCell>
                                <TableCell sx={{ py: 0.25 }}>
                                    <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5}>
                                        <TextField size="small" type="number" variant="outlined"
                                            value={extra.quantity}
                                            onChange={e => onUpdate(extra.id, 'quantity', parseFloat(e.target.value) || 0)}
                                            inputProps={{ min: 0, step: 1, style: { textAlign: 'right', padding: '2px 6px', fontSize: 12, width: 70 } }}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }} />
                                        <TextField size="small" variant="standard" placeholder="unit"
                                            value={extra.unit}
                                            onChange={e => onUpdate(extra.id, 'unit', e.target.value)}
                                            inputProps={{ style: { fontSize: 11, width: 34 } }} />
                                    </Stack>
                                </TableCell>
                                <TableCell align="right" sx={{ py: 0.25 }}>
                                    <TextField size="small" type="number" variant="outlined"
                                        value={extra.unit_cost}
                                        onChange={e => onUpdate(extra.id, 'unit_cost', parseFloat(e.target.value) || 0)}
                                        inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', padding: '2px 6px', fontSize: 12, width: 72 } }}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }} />
                                </TableCell>
                                <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 12, py: 0.25, fontWeight: 600, color: 'success.dark' }}>
                                    {fmt(extra.quantity * extra.unit_cost)}
                                </TableCell>
                                <TableCell sx={{ py: 0.25 }}>
                                    <IconButton size="small" onClick={() => onRemove(extra.id)} sx={{ p: 0.25, color: 'error.light' }}>
                                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}

                        {/* Section subtotal */}
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell colSpan={4} sx={{ fontSize: 12, py: 0.75, fontWeight: 700 }}>
                                Subtotal
                            </TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, py: 0.75 }}>
                                {fmt(adjustedSectionTotal)}
                            </TableCell>
                            <TableCell sx={{ py: 0.75 }} />
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Stack spacing={0.5} sx={{ mt: 1, px: 0.5 }}>
                <SummaryRow
                    label={`Overhead (${Math.round(overhead_pct * 100)}%)`}
                    value={sectionOverhead}
                />
                <SummaryRow
                    label={`Profit (${Math.round(profit_pct * 100)}%)`}
                    value={sectionProfit}
                />
                {pricePerSqft != null && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                            Price
                            <Typography component="span" variant="caption" color="text.disabled" sx={{ mx: 0.75 }}>
                                ($ {sectionClientPrice.toLocaleString()})
                            </Typography>
                             / sqft
                            <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.75 }}>
                                ({areaSqft.toLocaleString()} sqft)
                            </Typography>
                        </Typography>
                        <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                            ${pricePerSqft.toFixed(2)} / sqft
                        </Typography>
                    </Stack>
                )}
            </Stack>

            {/* Add extra line button */}
            <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={onAdd}
                sx={{ mt: 0.75, fontSize: 12, color: 'success.main', textTransform: 'none' }}>
                Add line to {title}
            </Button>
        </Box>
    );
};

// ── BUILDUP System Spec Tab ────────────────────────────────────────────────────

interface BuildupSpecProps {
    flatSystemId: string;
    flatSubstrate: string;
    insId: string;
    r20Qty: number; smoothQty: number; sm15Qty: number;
    capId: string;
    edgeMetal: string; edgeSize: string;
    parapetMetal: string; parapetCoping: boolean;
    flatEdge: number; flatParapet: number;
    plumbingProjections: number;
}

const LAYER_BULLET = (n: number, color: string) => (
    <Box sx={{
        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
        bgcolor: color, display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'white' }}>{n}</Typography>
    </Box>
);

const SpecRow = ({ n, color, title, items, note }: {
    n: number; color: string; title: string; items: string[]; note?: string;
}) => (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
        {LAYER_BULLET(n, color)}
        <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={700} sx={{ mb: 0.25 }}>{title}</Typography>
            {items.map((item, i) => (
                <Typography key={i} variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.6 }}>
                    • {item}
                </Typography>
            ))}
            {note && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'info.main', fontStyle: 'italic' }}>
                    ℹ {note}
                </Typography>
            )}
        </Box>
    </Stack>
);

const BuildupSystemCard = ({
    flatSystemId, flatSubstrate, insId,
    r20Qty, smoothQty, sm15Qty,
    capId, edgeMetal, edgeSize,
    parapetMetal, parapetCoping,
    flatEdge, flatParapet, plumbingProjections,
}: BuildupSpecProps) => {
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfError, setPdfError] = useState<string | null>(null);

    const handleGenerateNoa = async () => {
        setPdfLoading(true);
        setPdfError(null);
        if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null); }
        try {
            const noa_system = insId ? 2 : 1;
            const cap_type = capId === 'BUILDUP-CAP-ENERGY' ? 'energy' : 'standard';
            const res = await authedFetch('/estimator/noa-pdf', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    noa_system,
                    cap_type,
                    insulation: insId || 'none',
                    has_smooth_ply: smoothQty > 0 || sm15Qty > 0,
                    sm15_qty: sm15Qty,
                    flat_substrate: flatSubstrate,
                }),
            });
            if (!res.ok) { const t = await res.text(); throw new Error(t); }
            const blob = await res.blob();
            setPdfUrl(URL.createObjectURL(blob));
        } catch (e: any) {
            setPdfError(e?.message ?? 'Unknown error');
        } finally {
            setPdfLoading(false);
        }
    };
    if (flatSystemId !== 'BUILDUP') {
        return (
            <Box sx={{ p: 3, textAlign: 'center', color: 'text.disabled' }}>
                <LayersIcon sx={{ fontSize: 48, opacity: 0.2, mb: 1 }} />
                <Typography variant="body2">System specifications available for GAF Built-Up systems.</Typography>
            </Box>
        );
    }

    const isConc = flatSubstrate === 'CONCRETE';
    const noaSystem = isConc
        ? insId
            ? 'System 2 — Insulated Concrete · A(6) · pp. 18–19'
            : 'System 1 — Non-Insulated Concrete · F(1) · p. 29'
        : insId
            ? 'System 2 — Insulated · A(12) · pp. 31–32'
            : sm15Qty > 0
                ? 'System 1 — Non-Insulated · E(6) · pp. 51–52'
                : 'System 1 — Non-Insulated · E(5) · pp. 49–50';
    const substrateLabel = flatSubstrate === 'CONCRETE' ? 'Concrete Deck' : flatSubstrate === 'METAL' ? 'Metal Deck' : 'Wood Deck (Plywood Sheathing)';
    const edgeMetalLabel = edgeMetal === 'COP' ? 'Copper' : 'Galvalume';
    const edgeSizeLabel  = edgeSize === '4X4' ? '4×4' : '3×3';
    const parapetMetalLabel = parapetMetal === 'COP' ? 'Copper' : 'Galvalume';

    const insLabel = (() => {
        if (!insId) return null;
        const thick = insId === 'BUILDUP-INS-POLY1' ? '1"' : insId === 'BUILDUP-INS-POLY2' ? '2"' : '3"';
        return thick;
    })();

    const interPlyItems: string[] = [];
    if (r20Qty > 0)    interPlyItems.push(`(${r20Qty}) ${r20Qty > 1 ? 'plies' : 'ply'} — Ruberoid 20 Smooth (140 sqft/roll)`);
    if (smoothQty > 0) interPlyItems.push(`(${smoothQty}) ${smoothQty > 1 ? 'plies' : 'ply'} — Ruberoid Mop Smooth (95 sqft/roll)`);
    if (sm15Qty > 0)   interPlyItems.push(`(${sm15Qty}) ${sm15Qty > 1 ? 'plies' : 'ply'} — Ruberoid Mop Smooth 1.5 (140 sqft/roll)`);
    const totalPlies = r20Qty + smoothQty + sm15Qty;

    const isEnergyCap = capId === 'BUILDUP-CAP-ENERGY';

    let layerNum = 1;

    return (
        <Stack spacing={2}>
            {/* Header card */}
            <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                <CardContent sx={{ py: '14px !important' }}>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                        <Box>
                            <Typography variant="subtitle2" fontWeight={700}>GAF Built-Up (Hot Mop) System</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {flatSubstrate === 'CONCRETE' ? 'NOA No. 25-0926.06' : 'NOA No. 25-0926.01'} · GAF Ruberoid · {substrateLabel.split(' ')[0]} Deck
                            </Typography>
                            <Box sx={{ mt: 0.75 }}>
                                <Chip
                                    label={noaSystem}
                                    size="small"
                                    color={insId ? 'info' : 'default'}
                                    variant="outlined"
                                    sx={{ fontSize: 11, height: 20 }}
                                />
                            </Box>
                        </Box>
                        <LayersIcon sx={{ color: 'text.disabled', fontSize: 28 }} />
                    </Stack>
                </CardContent>
            </Card>

            {/* Assembly layers */}
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="caption" fontWeight={700} color="text.secondary"
                        sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1.5 }}>
                        Assembly — Bottom to Top
                    </Typography>
                    <Stack spacing={2} divider={<Divider />}>

                        {/* Layer 1 — Deck */}
                        <SpecRow n={layerNum++} color="#78909c" title={`Deck — ${flatSubstrate.charAt(0) + flatSubstrate.slice(1).toLowerCase()}`}
                            items={[substrateLabel]}
                        />

                        {/* Layer 2 — Primer (concrete only) */}
                        {flatSubstrate === 'CONCRETE' && (
                            <SpecRow n={layerNum++} color="#795548" title="Concrete Primer"
                                items={[
                                    'GAF Primer Matrix 307 — applied to concrete deck at ~1 pail / 300 sqft',
                                    'Promotes adhesion between concrete substrate and mopped base sheet',
                                ]}
                                note="Required for all concrete deck systems — NOA No. 25-0926.06"
                            />
                        )}

                        {/* Layer — Base Sheet (wood only; concrete goes directly primer → inter-ply) */}
                        {flatSubstrate !== 'CONCRETE' && (
                            <SpecRow n={layerNum++} color="#5c6bc0" title="Base Sheet (Anchor Sheet)"
                                items={[
                                    'GAF GAFGLAS #75 Base Sheet — mechanically fastened to wood deck',
                                    'BUR Coil Nails 1¼″ + Tin Caps 1⅝″ at 8″ o.c. field / 8″ o.c. laps',
                                ]}
                                note="NOA 25-0926.01 System E(5/6) / A(12) — mechanically attached per RAS 117."
                            />
                        )}

                        {/* Layer 3 — Insulation (conditional) */}
                        {insLabel && (
                            <SpecRow n={layerNum++} color="#26a69a" title={`Insulation — EnergyGuard Polyiso ${insLabel}`}
                                items={[
                                    `EnergyGuard Polyiso ${insLabel} rigid boards (4×4 = 16 sqft each), fully adhered with Type IV hot steep asphalt`,
                                    'Stratavent Perforated Loose-Laid venting sheet on top of Polyiso (no asphalt)',
                                    'Extra Type IV Asphalt for mopping insulation: 1 unit per 300 sqft',
                                    `Polyiso installation labor: $0.50/sqft`,
                                ]}
                                note="NOA System 2. Stratavent allows moisture vapor to escape and prevents pressure buildup under the membrane."
                            />
                        )}

                        {/* Layer — Inter-Plies */}
                        <SpecRow n={layerNum++} color="#ef6c00" title={`Inter-Ply Membrane${totalPlies > 1 ? 's' : ''} (${totalPlies} ${totalPlies > 1 ? 'plies' : 'ply'})`}
                            items={interPlyItems.length > 0 ? interPlyItems : ['No inter-ply selected']}
                            note="Each ply fully adhered (hot-mopped) with Type IV steep asphalt. Minimum 1 ply required."
                        />

                        {/* Layer — Cap Sheet */}
                        <SpecRow n={layerNum++} color="#c62828" title="Cap Sheet (Top Surface)"
                            items={[
                                isEnergyCap
                                    ? 'GAF Energy Mineral Sur Cap Sheet (100 sqft/roll), hot-mopped'
                                    : 'GAF Mineral Sur Cap Sheet (100 sqft/roll), hot-mopped',
                                ...(isEnergyCap ? [
                                    'Lap seams sealed with Energy Cap Lap Cement ($0.20/sqft)',
                                    'GAF Topcoat Surface Seal applied (1 bucket / 500 sqft, $300/bucket)',
                                ] : []),
                            ]}
                            note={isEnergyCap ? 'Cool-roof rated. Topcoat Surface Seal is required per NOA for Energy cap sheet.' : undefined}
                        />
                    </Stack>
                </CardContent>
            </Card>

            {/* Perimeter details */}
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="caption" fontWeight={700} color="text.secondary"
                        sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1.5 }}>
                        Perimeter Details
                    </Typography>
                    <Stack spacing={1.25}>
                        {flatEdge > 0 && (
                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', mt: 0.75, flexShrink: 0 }} />
                                <Box>
                                    <Typography variant="body2" fontWeight={600}>Drip Edge — {flatEdge} LF</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {edgeMetalLabel} {edgeSizeLabel} Flat Edge Metal (1 piece / 10 LF)
                                    </Typography>
                                </Box>
                            </Stack>
                        )}
                        {flatParapet > 0 && (
                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main', mt: 0.75, flexShrink: 0 }} />
                                <Box>
                                    <Typography variant="body2" fontWeight={600}>Parapet — {flatParapet} LF</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        {parapetMetalLabel} L-Flashing (1 piece / 10 LF) + Counter Flashing (1 piece / 10 LF)
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        GAF Perlite Cant 1″×4″ at all parapet-to-roof junctions (1 LF / 1 LF)
                                    </Typography>
                                    {parapetCoping && (
                                        <Chip label="+ Coping Cap" size="small" color="warning" variant="outlined"
                                            sx={{ fontSize: 10, height: 18, mt: 0.5 }} />
                                    )}
                                </Box>
                            </Stack>
                        )}
                        {flatEdge === 0 && flatParapet === 0 && (
                            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                                No edge or parapet measurements entered.
                            </Typography>
                        )}
                    </Stack>
                </CardContent>
            </Card>

            {/* Supplemental items */}
            {plumbingProjections > 0 && (
                <Card variant="outlined">
                    <CardContent sx={{ py: '12px !important' }}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary"
                            sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1 }}>
                            Penetrations
                        </Typography>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'text.disabled', flexShrink: 0 }} />
                            <Typography variant="caption" color="text.secondary">
                                {plumbingProjections} × Plumbing Projection — Lead Stack Flashing with hot asphalt collar
                            </Typography>
                        </Stack>
                    </CardContent>
                </Card>
            )}

            {/* NOA Package Generator */}
            <Card variant="outlined" sx={{ borderColor: 'primary.light', bgcolor: 'primary.50' }}>
                <CardContent sx={{ py: '14px !important' }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Box>
                            <Typography variant="subtitle2" fontWeight={700}>City Submission Package</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {flatSubstrate === 'CONCRETE' ? 'NOA No. 25-0926.06' : 'NOA No. 25-0926.01'} · Cover + System Pages + Limitations · Highlighted layers
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            size="small"
                            disabled={pdfLoading}
                            onClick={handleGenerateNoa}
                            startIcon={pdfLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
                            sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap', ml: 2 }}
                        >
                            {pdfLoading ? 'Generating…' : 'Generate NOA Package'}
                        </Button>
                    </Stack>

                    {pdfError && (
                        <Alert severity="error" sx={{ mt: 1, fontSize: 12 }}>{pdfError}</Alert>
                    )}

                    {pdfUrl && (
                        <Box sx={{ mt: 1.5 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                                    Preview — {insId ? 'System A(12)' : sm15Qty > 0 ? 'System E(6)' : 'System E(5)'} · Pages {insId ? '31–32' : sm15Qty > 0 ? '51–52' : '49–50'}
                                </Typography>
                                <Button
                                    size="small"
                                    component="a"
                                    href={pdfUrl}
                                    download="NOA_Package.pdf"
                                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12 }}
                                >
                                    ↓ Download PDF
                                </Button>
                            </Stack>
                            <Box
                                component="iframe"
                                src={pdfUrl}
                                sx={{
                                    width: '100%',
                                    height: 520,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    display: 'block',
                                }}
                                title="NOA Package Preview"
                            />
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Stack>
    );
};

// ── Client Estimate Document ──────────────────────────────────────────────────

const HR = () => (
    <Typography sx={{ fontSize: 12, color: '#aaa', my: 1 }}>
        -----------------------------------------------------------------------------------
    </Typography>
);

const AttachmentImagePreview = ({ attachment }: { attachment: QuoteAttachment }) => {
    const [src, setSrc] = useState<string | null>(null);

    useEffect(() => {
        let objectUrl: string | null = null;
        let cancelled = false;
        fetchQuoteAttachmentBlob(attachment)
            .then(blob => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setSrc(objectUrl);
            })
            .catch(() => {
                if (!cancelled) setSrc(null);
            });
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [attachment]);

    if (!src) return null;
    return (
        <Box sx={{ textAlign: 'center', my: 1 }}>
            <Box
                component="img"
                src={src}
                alt=""
                sx={{
                    display: 'block',
                    width: 'auto',
                    maxWidth: '100%',
                    mx: 'auto',
                }}
            />
        </Box>
    );
};

const ClientEstimateDoc = ({
    result, clientPrice, clientInfo, workType, roofType, stories,
    steepSystemId, flatSystemId, showSteep, showFlat, steepRoofs, flatRoofs, inputs,
    flatSubstrate,
    shingleUnderlaymentId,
    tileUnderlaymentId, tileTypeId, tileFlashingMetal, tileFlashingSize,
    metalUnderlaymentId, metalTypeId,
    builtupInsulationId, builtupTaperedInsId, builtupTaperedIsoQty, builtupFlatIsoQty,
    builtupR20Qty, builtupSmoothQty, builtupSm15Qty, builtupCapId,
    builtupEdgeMetal, builtupEdgeSize,
    contractEstimateNumber,
    polyglassIpQty, polyglassEdgeMetal, polyglassEdgeSize,
    flatUseScaraface, flatUseTelehandler, flatUseBoomLift, flatUseBathroom,
    wpSbWarranty,
    tpoAttachment, tpoCoverboard,
    tpoTaperedIsoQty, tpoFlatIsoQty,
    attachments = [],
    scopeHtml = '',
    scopeEditing = false,
    pendingScopeHtml = null,
    onScopeEditStart,
    onScopeEditSave,
    onScopeEditCancel,
    onApplyPendingScope,
    woodHtml = '',
    woodEditing = false,
    pendingWoodHtml = null,
    onWoodEditStart,
    onWoodEditSave,
    onWoodEditCancel,
    onApplyPendingWood,
    permitsHtml = '',
    permitsHidden = false,
    permitsEditing = false,
    pendingPermitsHtml = null,
    onPermitsEditStart,
    onPermitsEditSave,
    onPermitsEditCancel,
    onApplyPendingPermits,
    onPermitsHiddenChange,
    completionDays = 60,
    onCompletionDaysChange,
    clientNameSource = 'owner',
    onClientNameSourceChange,
    ownerName = '',
    contractorName = '',
}: {
    result: EstimateResult; clientPrice: number; clientInfo: ClientInfo; workType: WorkType;
    roofType: RoofType; stories: number; steepSystemId: string;
    flatSystemId: string; showSteep: boolean; showFlat: boolean;
    steepRoofs?: SteepRoofState[];
    flatRoofs?: FlatRoofState[];
    inputs: {
        steep_area?: number; flat_area?: number;
        wood_plywood_sheets?: number; wood_lumber_pieces?: number;
    };
    flatSubstrate: string;
    shingleUnderlaymentId: string;
    tileUnderlaymentId: string; tileTypeId: string;
    tileFlashingMetal: string; tileFlashingSize: string;
    metalUnderlaymentId: string; metalTypeId: string;
    builtupInsulationId: string; builtupTaperedInsId: string;
    builtupTaperedIsoQty: number; builtupFlatIsoQty: number;
    builtupR20Qty: number;
    builtupSmoothQty: number; builtupSm15Qty: number; builtupCapId: string;
    builtupEdgeMetal: string; builtupEdgeSize: string;
    contractEstimateNumber: number | null;
    polyglassIpQty: number; polyglassEdgeMetal: string; polyglassEdgeSize: string;
    flatUseScaraface: boolean; flatUseTelehandler: boolean;
    flatUseBoomLift: boolean; flatUseBathroom: boolean;
    wpSbWarranty: string;
    tpoAttachment: string; tpoCoverboard: boolean;
    tpoTaperedIsoQty: number; tpoFlatIsoQty: number;
    attachments?: QuoteAttachment[];
    scopeHtml?: string;
    scopeEditing?: boolean;
    pendingScopeHtml?: string | null;
    onScopeEditStart?: () => void;
    onScopeEditSave?: (html: string) => void;
    onScopeEditCancel?: () => void;
    onApplyPendingScope?: () => void;
    woodHtml?: string;
    woodEditing?: boolean;
    pendingWoodHtml?: string | null;
    onWoodEditStart?: () => void;
    onWoodEditSave?: (html: string) => void;
    onWoodEditCancel?: () => void;
    onApplyPendingWood?: () => void;
    permitsHtml?: string;
    permitsHidden?: boolean;
    permitsEditing?: boolean;
    pendingPermitsHtml?: string | null;
    onPermitsEditStart?: () => void;
    onPermitsEditSave?: (html: string) => void;
    onPermitsEditCancel?: () => void;
    onApplyPendingPermits?: () => void;
    onPermitsHiddenChange?: (hidden: boolean) => void;
    completionDays?: number;
    onCompletionDaysChange?: (days: number) => void;
    clientNameSource?: ClientNameSource;
    onClientNameSourceChange?: (source: ClientNameSource) => void;
    ownerName?: string;
    contractorName?: string;
}) => {
    const today      = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
    const steepLabel = steepSystemLabel(steepSystemId);
    const flatLabel = flatSystemLabel(flatSystemId);
    const roofSystem = roofSystemDisplay({
        showSteep, showFlat, steepSystemId, flatSystemId, steepLabel, flatLabel,
    });
    const scopeLines = buildClientEstimateScopeLines({
        steepRoofs, flatRoofs,
        flatUseScaraface, flatUseTelehandler, flatUseBoomLift, flatUseBathroom,
    });
    const totalArea  = (showSteep ? totalSteepArea(steepRoofs ?? []) : 0) + (showFlat ? totalFlatArea(flatRoofs ?? []) : 0);
    const { plywoodSheets: totalWoodPlywood, lumberPieces: totalWoodLumber } = totalWoodQuantities(
        steepRoofs, flatRoofs, { includeSteep: showSteep, includeFlat: showFlat },
    );

    const fmtMoney = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const b = '1px solid #ccc';
    const th: React.CSSProperties = { border: b, padding: '4px 8px', fontSize: 12, backgroundColor: '#f9f9f9', fontWeight: 700, textAlign: 'left' };
    const td: React.CSSProperties = { border: b, padding: '4px 8px', fontSize: 12 };
    const label: React.CSSProperties = { ...td, color: '#444', width: '15%', whiteSpace: 'nowrap' };
    const val:   React.CSSProperties = { ...td, fontWeight: 600, width: '35%' };

    const contractEstimateLabel =
        contractEstimateNumber != null ? String(contractEstimateNumber) : 'TBD';

    const ownerLabel = ownerName.trim() || clientInfo.clientName.trim();
    const contractorLabel = contractorName.trim();
    const canChooseClientName =
        !!onClientNameSourceChange &&
        !!ownerLabel &&
        !!contractorLabel &&
        ownerLabel.toLowerCase() !== contractorLabel.toLowerCase();

    const clientNameCell = canChooseClientName ? (
        <Box
            sx={{
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                px: 0.5,
                py: 0.25,
                bgcolor: 'action.hover',
                '&:hover': { borderColor: 'primary.main' },
            }}
        >
            <Box
                component="select"
                value={clientNameSource}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                    const v = e.target.value;
                    if (v === 'owner' || v === 'contractor') onClientNameSourceChange?.(v);
                }}
                aria-label="Client name on estimate"
                sx={{
                    width: '100%',
                    border: 'none',
                    bgcolor: 'transparent',
                    fontSize: 12,
                    fontStyle: 'italic',
                    fontWeight: 400,
                    fontFamily: 'inherit',
                    textAlign: 'center',
                    color: 'primary.main',
                    cursor: 'pointer',
                    outline: 'none',
                    py: 0.25,
                }}
            >
                <option value="owner">{ownerLabel}</option>
                <option value="contractor">{contractorLabel}</option>
            </Box>
        </Box>
    ) : (
        clientInfo.clientName || '—'
    );

    return (
        <Paper variant="outlined" sx={{
            p: 3, bgcolor: 'transparent', width: '100%',
            fontFamily: 'Arial, sans-serif',
        }}>
            {/* ── Title: plain centered bold, no box ── */}
            <div style={{ textAlign: 'center', fontWeight: 700, fontSize: 16, letterSpacing: 1, marginBottom: 12 }}>
                CONTRACT - ESTIMATE
            </div>

            {/* ── Two-column: [Date table + Company info] | [Logo] ── */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8 }}>
                <tbody>
                    <tr>
                        {/* LEFT: date table */}
                        <td style={{ width: '55%', verticalAlign: 'top', paddingRight: 16 }}>
                            <table style={{ borderCollapse: 'collapse', width: 'auto', marginBottom: 10 }}>
                                <tbody>
                                    <tr>
                                        <td style={{ border: b, padding: '5px 14px', fontSize: 12 }}>Date:</td>
                                        <td style={{ border: b, padding: '5px 14px', fontSize: 12, textAlign: 'right', minWidth: 90 }}>{today}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ border: b, padding: '5px 14px', fontSize: 12 }}>Contract- Estimate:</td>
                                        <td style={{ border: b, padding: '5px 14px', fontSize: 12, textAlign: 'right' }}>{contractEstimateLabel}</td>
                                    </tr>
                                </tbody>
                            </table>
                            {/* Company contact info in its own bordered box */}
                            <div style={{ border: b, padding: '8px 12px', fontSize: 12, lineHeight: 1.8, color: '#333' }}>
                                <span style={{ color: '#1155CC', textDecoration: 'underline' }}>www.mrt.ai</span><br />
                                Phone: 786-375-1725 / 305-742-9375<br />
                                Email: <span style={{ color: '#1155CC' }}>admin@mrtconstruction.com</span><br />
                                Licensed #: CCC13323341
                            </div>
                        </td>
                        {/* RIGHT: logo */}
                        <td style={{ width: '45%', verticalAlign: 'middle', textAlign: 'center', paddingLeft: 16 }}>
                            <img
                                src="/mrt-logo.png"
                                alt="MRT Construction"
                                style={{ width: 150, height: 'auto', filter: 'invert(1)', mixBlendMode: 'multiply' }}
                            />
                            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, marginTop: 4, color: '#1a1a2e' }}>
                                MRT CONSTRUCTION
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* ── Client info: centered labels, italic values ── */}
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                    <tr>
                        <td style={{ ...label, textAlign: 'center' }}>Client:</td>
                        <td style={{ ...val, textAlign: 'center', fontStyle: 'italic', fontWeight: 400 }}>
                            {clientNameCell}
                        </td>
                        <td style={{ ...label, textAlign: 'center' }}>Contractor:</td>
                        <td style={{ ...val, textAlign: 'center', fontStyle: 'italic', fontWeight: 400 }}>
                            MRT Roofing LLC
                        </td>
                    </tr>
                    {[
                        [{ l: 'Job Site Address:', v: clientInfo.address || '—' }, { l: 'Date:', v: today }],
                        [{ l: 'Job City:', v: clientInfo.city || '—' }, { l: 'Email:', v: clientInfo.email || '—' }],
                        [{ l: 'State:', v: clientInfo.state || 'FL' }, { l: 'Zip Code:', v: clientInfo.zipCode || '—' }],
                        [{ l: 'Folio:', v: clientInfo.folio || '—' }, { l: 'Year Built', v: clientInfo.yearBuilt || '—' }],
                        [{ l: 'Property Description:', v: clientInfo.propertyType || 'Residential' }, { l: 'Phone:', v: clientInfo.phone || '—' }],
                        [{ l: 'Number of Stories:', v: String(stories) }, { l: 'Type of Work:', v: workType }],
                        [{ l: 'Roof Types:', v: roofType }, { l: 'Roof System:', v: roofSystem }],
                    ].map((row, ri) => (
                        <tr key={ri}>
                            <td style={{ ...label, textAlign: 'center' }}>{row[0].l}</td>
                            <td style={{ ...val, textAlign: 'center', fontStyle: 'italic', fontWeight: 400 }}>{row[0].v}</td>
                            <td style={{ ...label, textAlign: 'center' }}>{row[1].l}</td>
                            <td style={{ ...val, textAlign: 'center', fontStyle: 'italic', fontWeight: 400 }}>{row[1].v}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* ── Spec header + SPECS table (omit when no area) ── */}
            {totalArea > 0 && (
                <>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                            <tr>
                                <td style={{ border: b, textAlign: 'center', padding: '5px', fontWeight: 700, fontSize: 13 }}>
                                    CONTRACT- ESTIMATE SPECIFICATION
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8 }}>
                        <thead>
                            <tr>
                                <th style={{ ...th, border: b }}>SPECS:</th>
                                <th style={{ ...th, border: b, width: 90, textAlign: 'center' }}></th>
                                <th style={{ ...th, border: b, width: 110, textAlign: 'center' }}></th>
                            </tr>
                            <tr>
                                <th style={{ ...th, border: b, fontWeight: 400 }}>Description:</th>
                                <th style={{ ...th, border: b, textAlign: 'center' }}>Area:</th>
                                <th style={{ ...th, border: b, textAlign: 'center' }}>Price:</th>
                            </tr>
                        </thead>
                        <tbody>
                            {showSteep && totalSteepArea(steepRoofs ?? []) > 0 && (
                                <tr>
                                    <td style={td}>{steepLabel}</td>
                                    <td style={{ ...td, textAlign: 'center' }}>{totalSteepArea(steepRoofs ?? []).toLocaleString()}</td>
                                    <td style={{ ...td, textAlign: 'right' }}></td>
                                </tr>
                            )}
                            {showFlat && totalFlatArea(flatRoofs ?? []) > 0 && (
                                <tr>
                                    <td style={td}>Flat</td>
                                    <td style={{ ...td, textAlign: 'center' }}>{totalFlatArea(flatRoofs ?? []).toLocaleString()}</td>
                                    <td style={{ ...td, textAlign: 'right' }}></td>
                                </tr>
                            )}
                            <tr>
                                <td style={{ ...td, fontWeight: 700 }}>Total</td>
                                <td style={{ ...td, textAlign: 'center', fontWeight: 700 }}>{totalArea.toLocaleString()}</td>
                                <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>$ {clientPrice.toLocaleString()}</td>
                            </tr>
                        </tbody>
                    </table>
                </>
            )}

            <HR />

            {/* ── Scope ── */}
            <EditableEstimateSection
                editing={scopeEditing}
                html={scopeHtml}
                label="Scope"
                pendingHtml={pendingScopeHtml}
                onApplyPending={onApplyPendingScope}
                onEditStart={onScopeEditStart}
                onEditSave={html => onScopeEditSave?.(html)}
                onEditCancel={() => onScopeEditCancel?.()}
                header={
                    <>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>
                            Roofing System: {steepLabel}
                        </Typography>
                        {showSteep && <Typography sx={{ fontSize: 12, mb: 0.5 }}>On {roofType}:</Typography>}
                    </>
                }
                fallback={
                    <Box component="ul" sx={{ pl: 2.5, mt: 0, mb: 0 }}>
                        {scopeLines.map((line, i) => (
                            <Box component="li" key={i} sx={{ fontSize: 12, mb: 0.5, lineHeight: 1.6 }}>{line}</Box>
                        ))}
                    </Box>
                }
            />

            <HR />

            {/* ── Permits ── */}
            {permitsHidden ? (
                <Box
                    sx={{
                        mb: 1,
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 1,
                        bgcolor: 'action.hover',
                    }}
                >
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        Permits (hidden from estimate)
                    </Typography>
                    <IconButton
                        size="small"
                        aria-label="Show permits"
                        title="Show permits"
                        onClick={() => onPermitsHiddenChange?.(false)}
                    >
                        <VisibilityIcon fontSize="small" />
                    </IconButton>
                </Box>
            ) : (
                <EditableEstimateSection
                    editing={permitsEditing}
                    html={permitsHtml}
                    label="Permits"
                    pendingHtml={pendingPermitsHtml}
                    onApplyPending={onApplyPendingPermits}
                    onEditStart={onPermitsEditStart}
                    onEditSave={html => onPermitsEditSave?.(html)}
                    onEditCancel={() => onPermitsEditCancel?.()}
                    header={
                        <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>Permits:</Typography>
                    }
                    headerActions={
                        onPermitsHiddenChange ? (
                            <IconButton
                                size="small"
                                aria-label="Hide permits"
                                title="Hide permits"
                                onClick={() => {
                                    onPermitsEditCancel?.();
                                    onPermitsHiddenChange(true);
                                }}
                            >
                                <VisibilityOffIcon fontSize="small" />
                            </IconButton>
                        ) : undefined
                    }
                    fallback={
                        <Typography sx={{ fontSize: 12 }}>Permit fees by: Owner</Typography>
                    }
                />
            )}

            <HR />

            {/* ── Wood ── */}
            <EditableEstimateSection
                editing={woodEditing}
                html={woodHtml}
                label="Wood"
                pendingHtml={pendingWoodHtml}
                onApplyPending={onApplyPendingWood}
                onEditStart={onWoodEditStart}
                onEditSave={html => onWoodEditSave?.(html)}
                onEditCancel={() => onWoodEditCancel?.()}
                header={
                    <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>Wood:</Typography>
                }
                fallback={
                    <>
                        <Typography sx={{ fontSize: 12 }}>
                            - This contract include Plywood Sheets up to: &nbsp;<strong>{totalWoodPlywood}</strong>&nbsp; Plywood Sheets
                        </Typography>
                        <Typography sx={{ fontSize: 12 }}>
                            - This contract includes trusses or fascias up to: &nbsp;<strong>{totalWoodLumber}</strong>&nbsp; Linear Feet
                        </Typography>
                    </>
                }
            />

            <HR />

            {/* ── Inspections ── */}
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Inspections:</Typography>
            <Typography sx={{ fontSize: 12 }}>
                - A representative of MRT Roofing LLC will be present in all required inspections of scope of work.
            </Typography>

            <HR />

            {/* ── Time ── */}
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Time:</Typography>
            <Typography sx={{ fontSize: 12 }}>Job Completion Date:</Typography>
            <Typography
                component="div"
                sx={{
                    fontSize: 12,
                    display: 'flex',
                    alignItems: 'baseline',
                    flexWrap: 'wrap',
                    gap: 0.75,
                    my: 0.5,
                    lineHeight: 1.6,
                }}
            >
                <span>- Time of completion after starting date:</span>
                <Box
                    component="input"
                    type="number"
                    min={0}
                    step={1}
                    value={completionDays}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const n = Number(e.target.value);
                        if (!Number.isFinite(n)) return;
                        onCompletionDaysChange?.(Math.max(0, Math.floor(n)));
                    }}
                    aria-label="Days to completion after starting date"
                    sx={{
                        width: `${Math.max(2, String(completionDays).length) + 1}ch`,
                        minWidth: '2.5ch',
                        border: 'none',
                        borderBottom: '2px dashed',
                        borderColor: 'primary.light',
                        borderRadius: 0,
                        bgcolor: 'transparent',
                        px: 0.25,
                        py: 0,
                        fontSize: 12,
                        fontWeight: 700,
                        fontFamily: 'inherit',
                        color: 'primary.main',
                        textAlign: 'center',
                        outline: 'none',
                        appearance: 'textfield',
                        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                        },
                        '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'action.hover',
                        },
                        '&:focus': {
                            borderBottomStyle: 'solid',
                            borderColor: 'primary.main',
                            bgcolor: 'action.selected',
                        },
                    }}
                />
                <Typography component="span" sx={{ fontSize: 11, color: 'text.secondary' }}>
                    days
                </Typography>
            </Typography>
            <Typography sx={{ fontSize: 12 }}>- Note: All dates are based on Weather Permitting &amp; Material Availability.</Typography>

            <HR />

            {/* ── Payment ── */}
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Payment:</Typography>
            <Typography sx={{ fontSize: 12 }}>Total Job Cost:</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>$ {clientPrice.toLocaleString()}</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 700, mt: 0.5 }}>Terms of Payment:</Typography>
            {[
                { label: '30% Signing of Contract',        amount: clientPrice * 0.30 },
                { label: '20% Start',                      amount: clientPrice * 0.20 },
                { label: '30% Tin Cap Inspection Passed.', amount: clientPrice * 0.30 },
                { label: '20% Final Inspection Passed.',   amount: clientPrice * 0.20 },
            ].map(row => (
                <Stack key={row.label} direction="row" justifyContent="space-between" sx={{ maxWidth: 400 }}>
                    <Typography sx={{ fontSize: 12 }}>- {row.label}</Typography>
                    <Typography sx={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 600 }}>${Math.ceil(row.amount).toLocaleString()}</Typography>
                </Stack>
            ))}

            <HR />

            {/* ── Terms & Conditions ── */}
            <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>TERMS &amp; CONDITIONS</Typography>
            {[
                { title: 'HIDDEN CONDITIONS:', body: 'It is understood that the contents of this proposal are based upon observable visual conditions. Charges for common unforeseeable conditions will be charged separately through a change order. Some of the typical hidden cost are in the table below.' },
                { title: 'CUSTOMER RESPONSIBILITY:', body: 'Customer shall notify the whereabouts of exposed sheathing, septic tank and drain field to MRT Roofing LLC. Customer shall remove and replace at its own expense all electrical lines, gutters, signs, plumbing, pipes, solar panels, air conditioning units and any other items attached to the roof. Customer shall provide unobstructed ingress and egress to the job site, shall supply water and electrical service for use at the job site. MRT Roofing LLC will not be responsible for any damage to landscaping, screens, screen enclosures, driveways, sidewalks, A/C Units, Aluminum-Fiberglass-Plastic Roofs, Pools, and Pool Pumps.' },
                { title: 'GUTTERS:', body: 'MRT Roofing LLC will remove existing gutters to perform work. MRT Roofing LLC will not be responsible for re-installing any pre-existing/new gutters.' },
                { title: 'REROOFING:', body: 'MRT Roofing LLC will use precaution during inclement weather when reroofing, however, we are not to be held responsible for interior damage to building, including ceiling, paint, furnishings, and personal belongings during progress of work. Customer shall remove vehicles from garage and driveways while work is being commenced. Cover and/or remove all objects within 50 feet of the roof line to protect those objects from falling debris, tar, gravel, or other roofing materials.' },
                { title: 'NATURAL DISASTERS:', body: 'In the event that a storm, hurricane, or any other natural disaster occurs while MRT Roofing LLC is performing work, MRT Roofing LLC shall not be responsible for any additional expenses resulting from securing the jobsite, transporting materials out of the jobsite, or bringing any material down from the roof. These services shall be provided at an additional fee, mutually agreed upon in writing.' },
                { title: 'ACCEPTANCE & CANCELLATION:', body: 'This proposal may be withdrawn at any time prior to written acceptance. This proposal shall become null and void after 30 days from the date hereof if the customer does not sign. The Customer shall have the right to cancel this contract in writing within 3 business days after acceptance. In the event MRT Roofing LLC starts the work and the customer cancels, the customer shall be liable to pay all costs, profits and reasonable attorney fees.' },
                { title: 'DELAYS:', body: 'MRT Roofing LLC shall not be held responsible for damages caused by delays of strikes, riots, acts of God, shortages of labor or materials, war, governmental laws or regulations or any other causes that are beyond reasonable control.' },
                { title: 'PAYMENT:', body: 'Any payment not received within three (3) days of the disbursement schedule shall accrue interest at the maximum allowed by law, or 1.5% per month, not to exceed 18% per annum.' },
                { title: 'WARRANTY:', body: 'MRT Roofing LLC extends to the Customer a warranty on the materials and workmanship of the above-described roof for the period of 7 Years Built-Up / 7 Years Shingle / 12 Years Tile / 14 Years Metal, subject to the terms and conditions stated herein. Any warranties will not be valid until entire contract is paid in full.' },
            ].map(({ title, body }) => (
                <Box key={title} sx={{ mb: 1 }}>
                    <Typography component="span" sx={{ fontSize: 12, fontWeight: 700 }}>{title} </Typography>
                    <Typography component="span" sx={{ fontSize: 12 }}>{body}</Typography>
                    {title === 'HIDDEN CONDITIONS:' && (
                        <table style={{ margin: '10px 0 20px', width: '600px', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={{ ...th, border: b }}>Material</th>
                                    <th style={{ ...th, border: b }}>Material Cost</th>
                                    <th style={{ ...th, border: b }}>Installation Cost</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    ['Plywood', 'Market Price +', '$60 per sheet'],
                                    ['Wood 1 X 2', 'Market Price +', '$2.50 / LFT'],
                                    ['Wood 1 X 4', 'Market Price +', '$6 / LFT'],
                                    ['Wood 1 X 6', 'Market Price +', '$6 / LFT'],
                                    ['Wood 1 X 8', 'Market Price +', '$6 / LFT'],
                                    ['Wood 1 X 10', 'Market Price +', '$6 / LFT'],
                                    ['Wood 1 X 12', 'Market Price +', '$6 / LFT'],
                                    ['Wood 2 X 4', 'Market Price +', '$7 / LFT'],
                                    ['Wood 2 X 6', 'Market Price +', '$7 / LFT'],
                                    ['Wood 2 X 8', 'Market Price +', '$7 / LFT'],
                                    ['Wood 2 X 10', 'Market Price +', '$7 / LFT'],
                                    ['Wood 2 X 12', 'Market Price +', '$7 / LFT'],
                                    ['Extra #30 Felt at Demolition', '', '$0.50 / SF'],
                                    ['Insulation at Demolition', '', '$0.80 / SF'],
                                    ['Extra #30 Felt for Installation', '', '$1.00 / SF'],
                                ].map(([material, materialCost, install]) => (
                                    <tr key={material}>
                                        <td style={td}>{material}</td>
                                        <td style={td}>{materialCost}</td>
                                        <td style={td}>{install}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </Box>
            ))}

            <HR />

            {/* ── Signature ── */}
            <Typography sx={{ fontSize: 12, mb: 1.5, fontStyle: 'italic' }}>
                Accepted by: I hereby certify that I am the owner of the above-described property, or I am the authorized agent for the property owner. I have read all pages, and fully understand its contents.
            </Typography>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                    <tr>
                        <td style={{ width: '50%', paddingRight: 24, verticalAlign: 'bottom' }}>
                            <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>Owner/Agent (circle one):</div>
                            <div style={{ borderBottom: '1px solid #000', height: 32, marginBottom: 4 }}></div>
                            <div style={{ fontSize: 12 }}>Date: {today}</div>
                            <div style={{ fontSize: 12, fontWeight: 700, marginTop: 4 }}>{clientInfo.clientName || '________________________'}</div>
                        </td>
                        <td style={{ width: '50%', paddingLeft: 24, verticalAlign: 'bottom' }}>
                            <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>Authorized Agent of MRT Roofing LLC.</div>
                            <div style={{ borderBottom: '1px solid #000', height: 32, marginBottom: 4 }}></div>
                            <div style={{ fontSize: 12 }}>Date: {today}</div>
                            <div style={{ fontSize: 12, fontWeight: 700, marginTop: 4 }}>Authorized Agent: MRT</div>
                        </td>
                    </tr>
                </tbody>
            </table>

            {attachments.length > 0 && (
                <>
                    <br />
                    <br />
                    {attachments.map(attachment => (
                        <Box key={attachment.id} sx={{ mb: 2 }}>
                            {isImageAttachment(attachment.content_type) ? (
                                <AttachmentImagePreview attachment={attachment} />
                            ) : (
                                <Typography sx={{ fontSize: 12, color: '#555', mt: 0.5 }}>
                                    PDF pages appended at the end of the estimate document.
                                </Typography>
                            )}
                        </Box>
                    ))}
                </>
            )}
        </Paper>
    );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const SAVE_KEY = 'mrt_estimator_draft';
const PRICE_CHANGE_REASON_MIN = 3;
const PRICE_CHANGE_REASON_MAX = 32;

function defaultClientInfo(): ClientInfo {
    return {
        clientName: '', address: '', city: '', state: 'FL', zipCode: '',
        email: '', phone: '', folio: '', yearBuilt: '', propertyType: 'Residential',
    };
}

const EMPTY_UI_DRAFT_INPUTS = {};

function loadDraft() {
    try { return JSON.parse(localStorage.getItem(SAVE_KEY) ?? 'null'); } catch { return null; }
}

export const EstimatorPage = () => {
    const dataProvider = useDataProvider<CrmDataProvider>();
    const navigate = useNavigate();
    const notify = useNotify();
    const [searchParams] = useSearchParams();
    const { identity } = useGetIdentity();
    const isAdmin = Boolean((identity as UserType | undefined)?.administrator);
    const { id: routeQuoteId } = useParams<{ id?: string }>();
    const editingQuoteId = routeQuoteId && routeQuoteId !== 'create' ? Number(routeQuoteId) : null;
    const isEditMode = editingQuoteId != null && !Number.isNaN(editingQuoteId);

    const {
        data: quoteRecord,
        isPending: quoteLoading,
        error: quoteError,
    } = useGetOne<Quote>(
        'quotes',
        { id: editingQuoteId as number },
        { enabled: isEditMode, retry: false },
    );
    useBrowserTitle(isEditMode ? `${quoteRecord?.description} - Edit Quote` : 'New Quote');

    const initialDraft = (() => {
        if (isEditMode) return null;
        return loadDraft();
    })();

    const [workType,      setWorkType]      = useState<WorkType | null>(initialDraft?.workType ?? null);
    const [roofType,      setRoofType]      = useState<RoofType | null>(initialDraft?.roofType ?? null);
    const [stories,       setStories]       = useState<number>(initialDraft?.stories ?? 1);
    const [steepRoofs, setSteepRoofs] = useState<SteepRoofState[]>(() => steepRoofsFromDraft(initialDraft));
    const [flatRoofs, setFlatRoofs] = useState<FlatRoofState[]>(() => flatRoofsFromDraft(initialDraft));
    const [activeSteepIdx, setActiveSteepIdx] = useState(initialDraft?.activeSteepIdx ?? 0);
    const [activeFlatIdx, setActiveFlatIdx] = useState(initialDraft?.activeFlatIdx ?? 0);
    const draftMachScaraface = initialDraft?.flatUseScaraface
        ?? initialDraft?.tremcoUseScaraface ?? initialDraft?.hydrostopUseScaraface
        ?? initialDraft?.wpSbUseScaraface ?? initialDraft?.wpSb10UseScaraface ?? initialDraft?.wpSb20UseScaraface ?? false;
    const draftMachTelehandler = initialDraft?.flatUseTelehandler
        ?? initialDraft?.tremcoUseTelehandler ?? initialDraft?.hydrostopUseTelehandler
        ?? initialDraft?.wpSbUseTelehandler ?? initialDraft?.wpSb10UseTelehandler ?? initialDraft?.wpSb20UseTelehandler ?? false;
    const draftMachBoomLift = initialDraft?.flatUseBoomLift
        ?? initialDraft?.tremcoUseBoomLift ?? initialDraft?.hydrostopUseBoomLift
        ?? initialDraft?.wpSbUseBoomLift ?? initialDraft?.wpSb10UseBoomLift ?? initialDraft?.wpSb20UseBoomLift ?? false;
    const draftMachBathroom = initialDraft?.flatUseBathroom
        ?? initialDraft?.tremcoUseBathroom ?? initialDraft?.hydrostopUseBathroom
        ?? initialDraft?.wpSbUseBathroom ?? initialDraft?.wpSb10UseBathroom ?? initialDraft?.wpSb20UseBathroom ?? false;
    const [flatUseScaraface,   setFlatUseScaraface]   = useState<boolean>(draftMachScaraface);
    const [flatUseTelehandler, setFlatUseTelehandler] = useState<boolean>(draftMachTelehandler);
    const [flatUseBoomLift,    setFlatUseBoomLift]    = useState<boolean>(draftMachBoomLift);
    const [flatUseBathroom,    setFlatUseBathroom]    = useState<boolean>(draftMachBathroom);

    const [clientInfo, setClientInfo] = useState<ClientInfo>(initialDraft?.clientInfo ?? defaultClientInfo());
    const [clientInfoMode, setClientInfoMode] = useState<ClientInfoMode>(initialDraft?.clientInfoMode ?? 'search');
    const [selectedLead, setSelectedLead] = useState<ContractorOwner | null>(() => {
        const draftLead = initialDraft?.selectedLead as ContractorOwner | null | undefined;
        if (draftLead && typeof draftLead === 'object' && draftLead.name) return draftLead;
        const ci = initialDraft?.clientInfo as ClientInfo | undefined;
        if (ci?.clientName?.trim()) {
            return {
                id: 0,
                name: ci.clientName,
                company_name: '',
                email: ci.email || '',
                phone_number: ci.phone || '',
            };
        }
        return null;
    });
    const [selectedContractor, setSelectedContractor] = useState<ContractorOwner | null>(
        () => (initialDraft?.selectedContractor as ContractorOwner | null | undefined) ?? null,
    );
    const [selectedCrmLead, setSelectedCrmLead] = useState<Lead | null>(null);
    const [useAsContractor, setUseAsContractor] = useState<boolean>(
        () => (initialDraft?.useAsContractor as boolean | undefined) ?? true,
    );
    const [clientNameSource, setClientNameSource] = useState<ClientNameSource>(
        () => (initialDraft?.clientNameSource === 'contractor' ? 'contractor' : 'owner'),
    );
    const [selectedProjectId, setSelectedProjectId] = useState<number | null>(initialDraft?.selectedProjectId ?? null);
    const [selectedProjectNo, setSelectedProjectNo] = useState<number | null>(initialDraft?.selectedProjectNo ?? null);
    const [selectedProjectPhase, setSelectedProjectPhase] = useState<string | null>(null);
    const [projectSearchResults, setProjectSearchResults] = useState<EstimatorProjectByAddress[]>([]);
    const [projectSearchLoading, setProjectSearchLoading] = useState(false);
    const [projectSearchError, setProjectSearchError] = useState('');

    const [createdByUser, setCreatedByUser] = useState<User | null>(null);
    const [createdByInput, setCreatedByInput] = useState('');
    const [createdByOptions, setCreatedByOptions] = useState<User[]>([]);
    const [createdByLoading, setCreatedByLoading] = useState(false);
    const createdBySeeded = useRef(false);
    // Set on a user pick this session; an untouched edit-seed must not send created_by.
    const createdByDirty = useRef(false);

    const urlProjectId = (() => {
        const n = Number(searchParams.get('project_id'));
        return Number.isFinite(n) && n > 0 ? n : null;
    })();
    const [projectPrefillLoading, setProjectPrefillLoading] = useState<boolean>(
        () => !isEditMode && urlProjectId != null,
    );
    const projectPrefillDoneRef = useRef<number | null>(null);

    const currentUserOption = useCallback((): User | null => {
        if (identity?.id == null) return null;
        const fullName = (identity as { fullName?: string }).fullName ?? '';
        return {
            id: identity.id,
            name: fullName,
            email: '',
            is_superuser: '',
            is_active: '',
            role_id: 0,
        };
    }, [identity]);

    const createdByLabel = (u: User) =>
        u.name && u.email ? `${u.name} <${u.email}>` : (u.name || u.email);

    const [commissionPct, setCommissionPct] = useState<number>(initialDraft?.commissionPct ?? 0.05);

    const [result,        setResult]        = useState<EstimateResult | null>(initialDraft?.result ?? null);
    const [syncedCalcKey, setSyncedCalcKey] = useState<string | null>(null);
    const [loading,       setLoading]       = useState(false);
    const [savingQuote,   setSavingQuote]   = useState(false);
    const [saveMenuAnchor, setSaveMenuAnchor] = useState<null | HTMLElement>(null);
    const [editFileName, setEditFileName] = useState<string>('');
    const [editHydrated, setEditHydrated] = useState(false);
    const savedPriceBaselineRef = useRef<number | null>(null);
    const [priceReasonOpen, setPriceReasonOpen] = useState(false);
    const [priceReason, setPriceReason] = useState('');
    const [priceReasonError, setPriceReasonError] = useState('');
    const [pendingSaveStatus, setPendingSaveStatus] = useState<number | null>(null);
    const [voidSigningConfirmOpen, setVoidSigningConfirmOpen] = useState(false);
    const [voidSigningPending, setVoidSigningPending] = useState<{
        targetStatus: number;
        priceChangeReason?: string;
    } | null>(null);
    const [rightTab,      setRightTab]      = useState<RightTabId>(() => {
        if (initialDraft?.result) return TAB_CLIENT_ESTIMATE;
        return TAB_ENGINEER;
    });
    const [architectProject, setArchitectProject] = useState<SavedProject | null>(() => {
        const ap = initialDraft?.architectProject as SavedProject | null | undefined;
        if (ap && typeof ap === 'object') {
            try { saveProject(ap); } catch { /* ignore */ }
            return ap;
        }
        return null;
    });
    const [lineOverrides,     setLineOverrides]     = useState<Record<string, number>>(initialDraft?.lineOverrides ?? {});
    const [unitCostOverrides, setUnitCostOverrides] = useState<Record<string, number>>(initialDraft?.unitCostOverrides ?? {});
    const [extraLines,        setExtraLines]        = useState<ExtraLine[]>(initialDraft?.extraLines ?? []);
    const [disabledItemIds,    setDisabledItemIds]    = useState<string[]>(() => {
        if (Array.isArray(initialDraft?.disabledItemIds)) return initialDraft.disabledItemIds;
        // migrate brief removedItemIds drafts from earlier session
        if (Array.isArray(initialDraft?.removedItemIds)) return initialDraft.removedItemIds;
        return [];
    });
    const [attachments,       setAttachments]       = useState<QuoteAttachment[]>(fileRefsToQuoteAttachments(initialDraft?.attachments));
    const [relatedQuoteFiles, setRelatedQuoteFiles] = useState<QuoteAttachment[]>(fileRefsToQuoteAttachments(initialDraft?.relatedQuoteFiles));
    const [viewFiles,         setViewFiles]         = useState<QuoteAttachment[]>(fileRefsToQuoteAttachments(initialDraft?.viewFiles));
    const [scopeHtml, setScopeHtml] = useState<string>(
        typeof initialDraft?.scopeHtml === 'string' ? initialDraft.scopeHtml : '',
    );
    const [scopeEditing, setScopeEditing] = useState(false);
    const [pendingScopeHtml, setPendingScopeHtml] = useState<string | null>(null);
    const [generatedScopeHtml, setGeneratedScopeHtml] = useState(
        typeof initialDraft?.scopeHtml === 'string' ? initialDraft.scopeHtml : '',
    );
    const [woodHtml, setWoodHtml] = useState<string>(
        typeof initialDraft?.woodHtml === 'string' ? initialDraft.woodHtml : '',
    );
    const [woodEditing, setWoodEditing] = useState(false);
    const [pendingWoodHtml, setPendingWoodHtml] = useState<string | null>(null);
    const [generatedWoodHtml, setGeneratedWoodHtml] = useState(
        typeof initialDraft?.woodHtml === 'string' ? initialDraft.woodHtml : '',
    );
    const [permitsHtml, setPermitsHtml] = useState<string>(
        typeof initialDraft?.permitsHtml === 'string' ? initialDraft.permitsHtml : '',
    );
    const [permitsHidden, setPermitsHidden] = useState<boolean>(Boolean(initialDraft?.permitsHidden));
    const [permitsEditing, setPermitsEditing] = useState(false);
    const [pendingPermitsHtml, setPendingPermitsHtml] = useState<string | null>(null);
    const [generatedPermitsHtml, setGeneratedPermitsHtml] = useState(
        typeof initialDraft?.permitsHtml === 'string' ? initialDraft.permitsHtml : '',
    );
    const [completionDays, setCompletionDays] = useState<number>(
        typeof initialDraft?.completionDays === 'number' ? initialDraft.completionDays : 60,
    );

    const applyDraftToState = useCallback((draft: Record<string, unknown>) => {
        const d = draft as Record<string, any>;
        setSyncedCalcKey(null);
        setWorkType(d.workType ?? null);
        setRoofType(d.roofType ?? null);
        setStories(d.stories ?? 1);
        setSteepRoofs(steepRoofsFromDraft(d));
        setFlatRoofs(flatRoofsFromDraft(d));
        setActiveSteepIdx(d.activeSteepIdx ?? 0);
        setActiveFlatIdx(d.activeFlatIdx ?? 0);
        setFlatUseScaraface(Boolean(d.flatUseScaraface));
        setFlatUseTelehandler(Boolean(d.flatUseTelehandler));
        setFlatUseBoomLift(Boolean(d.flatUseBoomLift));
        setFlatUseBathroom(Boolean(d.flatUseBathroom));
        setClientInfo(d.clientInfo ?? defaultClientInfo());
        setClientInfoMode(d.clientInfoMode ?? 'search');
        setSelectedProjectId(d.selectedProjectId ?? null);
        setSelectedProjectNo(d.selectedProjectNo ?? null);
        if (d.selectedLead && typeof d.selectedLead === 'object' && d.selectedLead.name) {
            setSelectedLead(d.selectedLead as ContractorOwner);
        } else if (d.selectedOwner && typeof d.selectedOwner === 'object' && (d.selectedOwner as ContractorOwner).name) {
            // ponytail: accept selectedOwner alias if an older/newer draft used that key
            setSelectedLead(d.selectedOwner as ContractorOwner);
        } else {
            const ci = (d.clientInfo ?? defaultClientInfo()) as ClientInfo;
            setSelectedLead(
                ci.clientName?.trim()
                    ? {
                          id: 0,
                          name: ci.clientName,
                          company_name: '',
                          email: ci.email || '',
                          phone_number: ci.phone || '',
                      }
                    : null,
            );
        }
        const restoredUseAsContractor = d.useAsContractor !== false;
        setUseAsContractor(restoredUseAsContractor);
        setClientNameSource(d.clientNameSource === 'contractor' ? 'contractor' : 'owner');
        if (
            !restoredUseAsContractor &&
            d.selectedContractor &&
            typeof d.selectedContractor === 'object' &&
            d.selectedContractor.name
        ) {
            setSelectedContractor(d.selectedContractor as ContractorOwner);
        } else {
            setSelectedContractor(null);
        }
        setCommissionPct(d.commissionPct ?? 0.05);
        setResult(d.result ?? null);
        setLineOverrides(d.lineOverrides ?? {});
        setUnitCostOverrides(d.unitCostOverrides ?? {});
        setExtraLines(d.extraLines ?? []);
        setDisabledItemIds(
            Array.isArray(d.disabledItemIds) ? d.disabledItemIds
                : Array.isArray(d.removedItemIds) ? d.removedItemIds
                : [],
        );
        setAttachments(fileRefsToQuoteAttachments(d.attachments));
        setRelatedQuoteFiles(fileRefsToQuoteAttachments(d.relatedQuoteFiles));
        setViewFiles(fileRefsToQuoteAttachments(d.viewFiles));
        setScopeHtml(typeof d.scopeHtml === 'string' ? d.scopeHtml : '');
        setGeneratedScopeHtml(typeof d.scopeHtml === 'string' ? d.scopeHtml : '');
        setPendingScopeHtml(null);
        setScopeEditing(false);
        setWoodHtml(typeof d.woodHtml === 'string' ? d.woodHtml : '');
        setGeneratedWoodHtml(typeof d.woodHtml === 'string' ? d.woodHtml : '');
        setPendingWoodHtml(null);
        setWoodEditing(false);
        setPermitsHtml(typeof d.permitsHtml === 'string' ? d.permitsHtml : '');
        setGeneratedPermitsHtml(typeof d.permitsHtml === 'string' ? d.permitsHtml : '');
        setPendingPermitsHtml(null);
        setPermitsHidden(Boolean(d.permitsHidden));
        setPermitsEditing(false);
        setCompletionDays(typeof d.completionDays === 'number' ? d.completionDays : 60);
        const ap = d.architectProject as SavedProject | null | undefined;
        if (ap && typeof ap === 'object') {
            setArchitectProject(ap);
            saveProject(ap);
        } else {
            setArchitectProject(null);
        }
        if (d.result) setRightTab(TAB_CLIENT_ESTIMATE);
        else setRightTab(TAB_ENGINEER);
    }, []);

    useEffect(() => {
        if (!isEditMode || quoteLoading || !quoteRecord || editHydrated) return;
        // getList seeds getOne with QuoteListItem (no price / estimation_data). Wait for real getOne.
        if (!('price' in quoteRecord)) return;
        const ed = quoteRecord.estimation_data as Record<string, unknown> | null | undefined;
        if (!ed) {
            notify('This quote has no estimator snapshot to restore.', { type: 'warning' });
            setEditHydrated(true);
            return;
        }
        const uiDraft = ed.ui_draft;
        if (uiDraft && typeof uiDraft === 'object') {
            applyDraftToState(uiDraft as Record<string, unknown>);
        } else {
            applyDraftToState(draftFromLegacyEstimationData(ed));
            notify('Restored from legacy snapshot — verify roofs before saving.', { type: 'info' });
        }
        const draftScope = (uiDraft && typeof uiDraft === 'object')
            ? (uiDraft as Record<string, unknown>).scopeHtml
            : undefined;
        if (typeof draftScope !== 'string' || !draftScope) {
            if (typeof ed.scope_html === 'string' && ed.scope_html) {
                setScopeHtml(ed.scope_html);
                setGeneratedScopeHtml(ed.scope_html);
            }
        }
        const draft = (uiDraft && typeof uiDraft === 'object')
            ? (uiDraft as Record<string, unknown>)
            : null;
        if (!(typeof draft?.woodHtml === 'string' && draft.woodHtml) && typeof ed.wood_html === 'string' && ed.wood_html) {
            setWoodHtml(ed.wood_html);
            setGeneratedWoodHtml(ed.wood_html);
        }
        if (!(typeof draft?.permitsHtml === 'string' && draft.permitsHtml) && typeof ed.permits_html === 'string' && ed.permits_html) {
            setPermitsHtml(ed.permits_html);
            setGeneratedPermitsHtml(ed.permits_html);
        }
        if (draft?.permitsHidden == null && typeof ed.permits_hidden === 'boolean') {
            setPermitsHidden(ed.permits_hidden);
        }
        if (!(typeof draft?.completionDays === 'number') && typeof ed.completion_days === 'number') {
            setCompletionDays(ed.completion_days);
        }
        const architectFromEd = ed.architect_project as SavedProject | null | undefined;
        if (architectFromEd && typeof architectFromEd === 'object') {
            setArchitectProject(architectFromEd);
            saveProject(architectFromEd);
        }
        setEditFileName(quoteRecord.description || `Quote ${quoteRecord.id}`);
        if (quoteRecord.project_id) setSelectedProjectId(quoteRecord.project_id);
        if (quoteRecord.folder_number != null) setSelectedProjectNo(quoteRecord.folder_number);
        setClientInfoMode('create');
        setEditHydrated(true);

        // Prefer live owner/contractor over ui_draft snapshot.
        if (quoteRecord.property_owner_id) {
            const ownerId = quoteRecord.property_owner_id;
            void getContractorOwner(ownerId)
                .then(owner => {
                    setSelectedLead(owner);
                })
                .catch(() => {
                    setSelectedLead(prev =>
                        prev?.id
                            ? prev
                            : {
                                  id: ownerId,
                                  name: '',
                                  company_name: '',
                                  email: '',
                                  phone_number: '',
                              },
                    );
                });
        }
        if (quoteRecord.contractor_id) {
            const contractorId = quoteRecord.contractor_id;
            const sameAsOwner =
                Number(contractorId) === Number(quoteRecord.property_owner_id);
            setUseAsContractor(sameAsOwner);
            if (!sameAsOwner) {
                void getContractorOwner(contractorId)
                    .then(person => setSelectedContractor(person))
                    .catch(() => {
                        setSelectedContractor({
                            id: contractorId,
                            name: '',
                            company_name: '',
                            email: '',
                            phone_number: '',
                        });
                    });
            } else {
                setSelectedContractor(null);
            }
        } else {
            setUseAsContractor(true);
            setSelectedContractor(null);
        }
    }, [isEditMode, quoteLoading, quoteRecord, editHydrated, applyDraftToState, notify]);

    useEffect(() => {
        if (!isEditMode || !editHydrated || !quoteRecord || !('price' in quoteRecord)) return;
        if (savedPriceBaselineRef.current == null) {
            savedPriceBaselineRef.current = Number(quoteRecord.price) || 0;
        }
    }, [isEditMode, editHydrated, quoteRecord]);

    useEffect(() => {
        if (isEditMode || !isAdmin || createdBySeeded.current) return;
        const me = currentUserOption();
        if (!me) return;
        createdBySeeded.current = true;
        setCreatedByUser(me);
        setCreatedByInput(createdByLabel(me));
    }, [isEditMode, isAdmin, currentUserOption]);

    // Edit mode: pre-fill the Responsible picker with the quote's current owner, once.
    useEffect(() => {
        if (!isEditMode || !isAdmin || !editHydrated || !quoteRecord || createdBySeeded.current) return;
        createdBySeeded.current = true;
        if (!quoteRecord.created_by) return; // legacy created_by=0: leave empty
        const owner: User = {
            id: quoteRecord.created_by,
            name: quoteRecord.creator_name || `User #${quoteRecord.created_by}`,
            email: '',
            is_superuser: '',
            is_active: '',
            role_id: 0,
        };
        setCreatedByUser(owner);
        setCreatedByInput(createdByLabel(owner));
    }, [isEditMode, isAdmin, editHydrated, quoteRecord]);

    useEffect(() => {
        if (!isAdmin) return;
        let cancelled = false;
        setCreatedByLoading(true);
        searchUsersByName(createdByInput)
            .then(opts => {
                if (!cancelled) setCreatedByOptions(opts);
            })
            .catch(() => {
                if (!cancelled) setCreatedByOptions([]);
            })
            .finally(() => {
                if (!cancelled) setCreatedByLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [createdByInput, isAdmin]);

    // Preselect CRM lead from ?lead_id= on quote create (Convert from LeadShow).
    useEffect(() => {
        if (isEditMode) return;
        const raw = searchParams.get('lead_id');
        if (!raw) return;
        const id = Number(raw);
        if (!Number.isFinite(id) || id <= 0) return;
        let cancelled = false;
        const leadPromise = dataProvider.getLead(id);
        const addressPromise = dataProvider
            .getLeadPropertyAddress(id)
            .catch(() => null);
        Promise.all([leadPromise, addressPromise])
            .then(([lead, propertyAddress]) => {
                if (cancelled) return;
                setSelectedCrmLead(lead);
                const addr = propertyAddress ?? {
                    address: '',
                    city: '',
                    state: '',
                    zip_code: '',
                };
                setClientInfo(prev => applyLeadToClientInfo(lead, addr, prev));
                if (propertyAddress === null) {
                    notify('Could not resolve lead property address', { type: 'warning' });
                }
            })
            .catch(() => {
                if (!cancelled) notify('Could not load CRM lead from URL', { type: 'warning' });
            });
        return () => {
            cancelled = true;
        };
    }, [isEditMode, searchParams, notify, dataProvider]);

    const resetEstimatorState = useCallback(() => {
        try { localStorage.removeItem(SAVE_KEY); } catch { /* ignore */ }
        setWorkType(null);
        setRoofType(null);
        setStories(1);
        setSteepRoofs([defaultSteepRoof()]);
        setFlatRoofs([defaultFlatRoof()]);
        setActiveSteepIdx(0);
        setActiveFlatIdx(0);
        setFlatUseScaraface(false); setFlatUseTelehandler(false);
        setFlatUseBoomLift(false); setFlatUseBathroom(false);
        setClientInfo(defaultClientInfo());
        setClientInfoMode('search');
        setSelectedLead(null);
        setSelectedContractor(null);
        setSelectedCrmLead(null);
        setUseAsContractor(true);
        setSelectedProjectId(null);
        setSelectedProjectNo(null);
        setSelectedProjectPhase(null);
        setProjectSearchResults([]);
        setProjectSearchError('');
        const me = currentUserOption();
        setCreatedByUser(me);
        setCreatedByInput(me ? createdByLabel(me) : '');
        setCreatedByOptions([]);
        setCommissionPct(0.05);
        setResult(null);
        setSyncedCalcKey(null);
        setRightTab(TAB_COST);
        setArchitectProject(null);
        setLineOverrides({});
        setUnitCostOverrides({});
        setExtraLines([]);
        setDisabledItemIds([]);
        setAttachments([]);
        setRelatedQuoteFiles([]);
        setViewFiles([]);
        setScopeHtml('');
        setGeneratedScopeHtml('');
        setPendingScopeHtml(null);
        setScopeEditing(false);
        setWoodHtml('');
        setGeneratedWoodHtml('');
        setPendingWoodHtml(null);
        setWoodEditing(false);
        setPermitsHtml('');
        setGeneratedPermitsHtml('');
        setPendingPermitsHtml(null);
        setPermitsHidden(false);
        setPermitsEditing(false);
        setCompletionDays(60);
    }, [currentUserOption]);

    const overrideQty = (itemId: string, qty: number) =>
        setLineOverrides(prev => ({ ...prev, [itemId]: qty }));

    const overrideUnitCost = (itemId: string, cost: number) =>
        setUnitCostOverrides(prev => ({ ...prev, [itemId]: cost }));

    const resetLine = (itemId: string) => {
        setLineOverrides(prev => { const n = { ...prev }; delete n[itemId]; return n; });
        setUnitCostOverrides(prev => { const n = { ...prev }; delete n[itemId]; return n; });
    };

    const disableCatalogLine = (itemId: string) => {
        setDisabledItemIds(prev => prev.includes(itemId) ? prev : [...prev, itemId]);
        resetLine(itemId);
    };

    const enableCatalogLine = (itemId: string) => {
        setDisabledItemIds(prev => prev.filter(id => id !== itemId));
    };

    const addExtraLine = (section: ExtraLine['section']) =>
        setExtraLines(prev => [...prev, {
            id: `extra-${Date.now()}`, name: '', line_type: 'Material',
            unit: 'EA', quantity: 1, unit_cost: 0, section,
        }]);

    const updateExtraLine = (id: string, field: keyof ExtraLine, value: string | number) =>
        setExtraLines(prev => prev.map(l => l.id === id ? { ...l, [field]: value } : l));

    const removeExtraLine = (id: string) =>
        setExtraLines(prev => prev.filter(l => l.id !== id));

    const setClient = (field: keyof ClientInfo) => (value: string) =>
        setClientInfo(prev => ({ ...prev, [field]: value }));

    const activeSteep = steepRoofs[activeSteepIdx] ?? defaultSteepRoof();
    const activeFlat  = flatRoofs[activeFlatIdx] ?? defaultFlatRoof();

    const patchSteepAt = useCallback((idx: number, patch: Partial<SteepRoofState>) => {
        setSteepRoofs(prev => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    }, []);

    const patchFlatAt = useCallback((idx: number, patch: Partial<FlatRoofState>) => {
        setFlatRoofs(prev => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    }, []);

    const addSteepRoof = () => {
        const n = steepRoofs.length + 1;
        setSteepRoofs(prev => [...prev, defaultSteepRoof(`Steep Roof ${n}`)]);
        setActiveSteepIdx(steepRoofs.length);
    };

    const removeSteepRoof = (idx: number) => {
        if (steepRoofs.length <= 1) return;
        setSteepRoofs(prev => prev.filter((_, i) => i !== idx));
        setActiveSteepIdx((i: number) => Math.max(0, i >= idx ? i - 1 : i));
    };

    const addFlatRoof = () => {
        const n = flatRoofs.length + 1;
        setFlatRoofs(prev => [...prev, defaultFlatRoof(`Flat Roof ${n}`)]);
        setActiveFlatIdx(flatRoofs.length);
    };

    const removeFlatRoof = (idx: number) => {
        if (flatRoofs.length <= 1) return;
        setFlatRoofs(prev => prev.filter((_, i) => i !== idx));
        setActiveFlatIdx((i: number) => Math.max(0, i >= idx ? i - 1 : i));
    };

    const {
        steepSystemId, setSteepSystemId,
        shingleUnderlaymentId, setShingleUnderlaymentId,
        tileUnderlaymentId, setTileUnderlaymentId,
        tileTypeId, setTileTypeId,
        tileFlashingMetal, setTileFlashingMetal,
        tileFlashingSize, setTileFlashingSize,
        metalTypeId, setMetalTypeId,
        metalUnderlaymentId, setMetalUnderlaymentId,
    } = steepScalarBindings(activeSteep, p => patchSteepAt(activeSteepIdx, p));

    const {
        flatSystemId, setFlatSystemId,
        flatSubstrate, setFlatSubstrate,
        flatScuppers, setFlatScuppers,
        flatDrains, setFlatDrains,
        builtupInsulationId, setBuiltupInsulationId,
        builtupTaperedInsId, setBuiltupTaperedInsId,
        builtupTaperedIsoQty, setBuiltupTaperedIsoQty,
        builtupFlatIsoQty, setBuiltupFlatIsoQty,
        builtupR20Qty, setBuiltupR20Qty,
        builtupSmoothQty, setBuiltupSmoothQty,
        builtupSm15Qty, setBuiltupSm15Qty,
        builtupCapId, setBuiltupCapId,
        builtupEdgeMetal, setBuiltupEdgeMetal,
        builtupEdgeSize, setBuiltupEdgeSize,
        builtupParapetMetal, setBuiltupParapetMetal,
        builtupParapetCoping, setBuiltupParapetCoping,
        polyglassIpQty, setPolyglassIpQty,
        polyglassEdgeMetal, setPolyglassEdgeMetal,
        polyglassEdgeSize, setPolyglassEdgeSize,
        wpSbWarranty, setWpSbWarranty,
        tpoAttachment, setTpoAttachment,
        tpoCoverboard, setTpoCoverboard,
        tpoFlatBaseInsId, setTpoFlatBaseInsId,
        tpoInsulationId, setTpoInsulationId,
        tpoEdgeMetal, setTpoEdgeMetal,
        tpoEdgeSize, setTpoEdgeSize,
        tpoParapetMetal, setTpoParapetMetal,
        tpoParapetCoping, setTpoParapetCoping,
        tpoTaperedIsoQty, setTpoTaperedIsoQty,
        tpoFlatIsoQty, setTpoFlatIsoQty,
    } = flatScalarBindings(activeFlat, p => patchFlatAt(activeFlatIdx, p));

    /** Active-roof measurement shim for existing Step 3 JSX. */
    const inputs = {
        steep_area: activeSteep.area,
        steep_edge: activeSteep.edge,
        ridge: activeSteep.ridge,
        valley: activeSteep.valley,
        steep_parapet: activeSteep.parapet,
        flat_area: activeFlat.area,
        flat_edge: activeFlat.edge,
        flat_parapet: activeFlat.parapet,
        plumbing_projections: activeFlat.plumbingProjections || activeSteep.plumbingProjections,
        ventilation_projections: activeFlat.ventilationProjections || activeSteep.ventilationProjections,
        wood_plywood_sheets: activeFlat.woodPlywoodSheets || activeSteep.woodPlywoodSheets,
        wood_lumber_pieces: activeFlat.woodLumberPieces || activeSteep.woodLumberPieces,
    };

    const setMeasure = (field: keyof typeof inputs, value: number) => {
        const steepMap: Partial<Record<keyof typeof inputs, keyof SteepRoofState>> = {
            steep_area: 'area', steep_edge: 'edge', ridge: 'ridge', valley: 'valley',
            steep_parapet: 'parapet', plumbing_projections: 'plumbingProjections',
            ventilation_projections: 'ventilationProjections',
            wood_plywood_sheets: 'woodPlywoodSheets', wood_lumber_pieces: 'woodLumberPieces',
        };
        const flatMap: Partial<Record<keyof typeof inputs, keyof FlatRoofState>> = {
            flat_area: 'area', flat_edge: 'edge', flat_parapet: 'parapet',
            plumbing_projections: 'plumbingProjections', ventilation_projections: 'ventilationProjections',
            wood_plywood_sheets: 'woodPlywoodSheets', wood_lumber_pieces: 'woodLumberPieces',
        };
        if (field in steepMap) {
            patchSteepAt(activeSteepIdx, { [steepMap[field]!]: value } as Partial<SteepRoofState>);
        } else if (field in flatMap) {
            patchFlatAt(activeFlatIdx, { [flatMap[field]!]: value } as Partial<FlatRoofState>);
        }
    };

    const showSteep = roofType === 'Slope Roof' || roofType === 'Mixed Roof';
    const showFlat  = roofType === 'Flat Roof'  || roofType === 'Mixed Roof';

    const showRightPanel = true;

    // Fingerprint of left-panel inputs that feed /estimator/calculate (not commission).
    const calcInputsKey = JSON.stringify({
        workType,
        roofType,
        stories,
        steepRoofs: showSteep ? steepRoofs : null,
        flatRoofs: showFlat ? flatRoofs : null,
        flatUseScaraface,
        flatUseTelehandler,
        flatUseBoomLift,
        flatUseBathroom,
    });
    const needsRecalc = Boolean(result && syncedCalcKey !== null && syncedCalcKey !== calcInputsKey);

    useEffect(() => {
        if (!result) {
            setSyncedCalcKey(null);
            return;
        }
        if (syncedCalcKey === null) setSyncedCalcKey(calcInputsKey);
    }, [result, syncedCalcKey, calcInputsKey]);

    useEffect(() => {
        if (!result) setRightTab(TAB_ENGINEER);
    }, [result]);

    const selectedSteepSystem = STEEP_SYSTEMS.find(s => s.id === steepSystemId);
    const isRepairs = workType === 'Repairs';
    const hasArea =
        (showSteep && steepRoofs.some(r => r.area > 0)) ||
        (showFlat && flatRoofs.some(r => r.area > 0));
    const canCalculate =
        workType !== null && roofType !== null &&
        (selectedSteepSystem?.ready || !showSteep) &&
        (isRepairs || hasArea);

    // TPO insulation auto-estimates (null = user override not set → use auto)
    const tpoIsoOption         = TPO_INS_OPTIONS.find(o => o.id === tpoInsulationId) ?? TPO_INS_OPTIONS[2];
    const tpoFlatIsoAutoQty    = Math.ceil(inputs.flat_area / 32);
    const tpoTaperedIsoAutoQty = tpoInsulationId === 'NONE' ? 0
        : Math.ceil(inputs.flat_area / 32 * (1 + tpoIsoOption.slope));
    const resolvedTpoFlatIsoQty    = tpoFlatIsoQty    ?? tpoFlatIsoAutoQty;
    const resolvedTpoTaperedIsoQty = tpoTaperedIsoQty ?? tpoTaperedIsoAutoQty;

    // BUILDUP insulation auto-estimates (ISO boards are 4×4 = 16 sqft each)
    const builtupIsoOption          = TPO_INS_OPTIONS.find(o => o.id === builtupTaperedInsId);
    const builtupFlatIsoAutoQty     = Math.ceil(inputs.flat_area / 16);
    const builtupTaperedIsoAutoQty  = builtupIsoOption
        ? Math.ceil(inputs.flat_area / 16 * (1 + builtupIsoOption.slope))
        : 0;
    const resolvedBuiltupFlatIsoQty    = builtupFlatIsoQty    ?? builtupFlatIsoAutoQty;
    const resolvedBuiltupTaperedIsoQty = builtupTaperedIsoQty ?? builtupTaperedIsoAutoQty;

    const handleCalculate = async () => {
        setLoading(true);
        try {
            const steepPayload = showSteep
                ? steepRoofs.map(r => steepRoofToApi(
                    r,
                    METAL_TYPE_OPTIONS.find(m => m.id === r.metalTypeId)?.systemId ?? 'METAL-TYPE-GAL',
                ))
                : [];
            const flatPayload = showFlat
                ? flatRoofs.map(r => {
                    const tpoOpt = TPO_INS_OPTIONS.find(o => o.id === r.tpoInsulationId) ?? TPO_INS_OPTIONS[2];
                    const buOpt = TPO_INS_OPTIONS.find(o => o.id === r.builtupTaperedInsId);
                    const tpoTaperedAuto = r.tpoInsulationId === 'NONE' ? 0
                        : Math.ceil(r.area / 32 * (1 + tpoOpt.slope));
                    const tpoFlatAuto = Math.ceil(r.area / 32);
                    const buTaperedAuto = buOpt ? Math.ceil(r.area / 16 * (1 + buOpt.slope)) : 0;
                    const buFlatAuto = Math.ceil(r.area / 16);
                    const tapered = r.systemId === 'TPO'
                        ? (r.tpoTaperedIsoQty ?? tpoTaperedAuto)
                        : (r.builtupTaperedIsoQty ?? buTaperedAuto);
                    const flatIso = r.systemId === 'TPO'
                        ? (r.tpoFlatIsoQty ?? tpoFlatAuto)
                        : (r.builtupFlatIsoQty ?? buFlatAuto);
                    return flatRoofToApi(r, tapered, flatIso);
                })
                : [];

            const res = await callCalculate({
                steep_roofs: steepPayload,
                flat_roofs: flatPayload,
                flat_use_scaraface: flatUseScaraface,
                flat_use_telehandler: flatUseTelehandler,
                flat_use_boom_lift: flatUseBoomLift,
                flat_use_bathroom: flatUseBathroom,
            });
            setResult(res);
            setSyncedCalcKey(calcInputsKey);
            const nextScope = scopeLinesToHtml(buildClientEstimateScopeLines({
                steepRoofs, flatRoofs,
                flatUseScaraface, flatUseTelehandler, flatUseBoomLift, flatUseBathroom,
            }));
            const wood = totalWoodQuantities(
                steepRoofs, flatRoofs, { includeSteep: showSteep, includeFlat: showFlat },
            );
            const nextWood = defaultWoodHtml(wood.plywoodSheets, wood.lumberPieces);
            const scopeReconcile = reconcileSectionHtmlAfterCalc(scopeHtml, generatedScopeHtml, nextScope);
            setScopeHtml(scopeReconcile.display);
            setGeneratedScopeHtml(scopeReconcile.generated);
            setPendingScopeHtml(scopeReconcile.pending);
            const woodReconcile = reconcileSectionHtmlAfterCalc(woodHtml, generatedWoodHtml, nextWood);
            setWoodHtml(woodReconcile.display);
            setGeneratedWoodHtml(woodReconcile.generated);
            setPendingWoodHtml(woodReconcile.pending);
            const nextPermits = defaultPermitsHtml();
            const permitsReconcile = reconcileSectionHtmlAfterCalc(
                permitsHtml,
                generatedPermitsHtml,
                nextPermits,
            );
            setPermitsHtml(permitsReconcile.display);
            setGeneratedPermitsHtml(permitsReconcile.generated);
            setPendingPermitsHtml(permitsReconcile.pending);
            setScopeEditing(false);
            setWoodEditing(false);
            setPermitsEditing(false);
            setRightTab(TAB_COST);
        } catch {
            notify('Calculation failed.', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const { adjustedHardCost, adjustedTotal, adjustedOverhead, adjustedProfit } =
    computeAdjusted(result, lineOverrides, unitCostOverrides, extraLines, steepRoofs, flatRoofs, disabledItemIds);

    const jobOtherMargin = marginForJobAndOther(steepRoofs, flatRoofs);
    const otherExtras = extraLines.filter(e => e.section === 'other');
    const otherExtrasTotal = otherExtras.reduce((s, e) => s + e.quantity * e.unit_cost, 0);
    const { overhead: otherOverhead, profit: otherProfit } = applySectionMargin(
        otherExtrasTotal, jobOtherMargin.overhead_pct, jobOtherMargin.profit_pct,
    );

    const { priceBeforeCommission, commissionAmount, clientPrice, netToCompany } =
        computeClientPricing(adjustedTotal, commissionPct);

    const canTemporalSave =
        !!clientInfo.clientName.trim() &&
        !!clientInfo.address.trim() &&
        !!workType;

    const canSaveQuote =
        canCalculate &&
        !!result &&
        !!clientInfo.clientName.trim() &&
        !!clientInfo.address.trim() &&
        clientPrice > 0;

    const showTemporalSave =
        !isEditMode ||
        quoteRecord?.status === quoteStatuses.pending_finish_quoting;

    const showSaveFooter =
        !!result ||
        (rightTab === TAB_ENGINEER && showTemporalSave);

    const handleSaveQuote = async (
        targetStatus: number = quoteStatuses.pending,
        priceChangeReason?: string,
        confirmVoidSigning: boolean = false,
    ) => {
        const isTemporal = targetStatus === quoteStatuses.pending_finish_quoting;
        if (isTemporal) {
            if (!canTemporalSave || !workType) return;
        } else if (!canSaveQuote || !workType || !roofType || !result) {
            return;
        }
        setSaveMenuAnchor(null);
        setSavingQuote(true);
        try {
            // Architect autosave is debounced — prefer the newest localStorage snapshot.
            let architectSnapshot = architectProject;
            if (architectProject?.fileName) {
                const byName = loadSavedProject(architectProject.fileName);
                if (
                    byName &&
                    (!architectSnapshot || byName.savedAt >= (architectSnapshot.savedAt ?? 0))
                ) {
                    architectSnapshot = byName;
                }
            }
            for (const f of viewFiles) {
                const loaded = loadSavedProject(f.filename);
                if (
                    loaded &&
                    (!architectSnapshot || loaded.savedAt >= (architectSnapshot.savedAt ?? 0))
                ) {
                    architectSnapshot = loaded;
                }
            }
            if (architectSnapshot && architectSnapshot !== architectProject) {
                setArchitectProject(architectSnapshot);
            }
            const metalTypeSystemId = METAL_TYPE_OPTIONS.find(m => m.id === metalTypeId)?.systemId ?? 'METAL-TYPE-GAL';
            const steepLabel = steepSystemLabel(steepSystemId);
            const flatLabel = flatSystemLabel(flatSystemId);
            const roofSystem = roofSystemDisplay({
                showSteep, showFlat, steepSystemId, flatSystemId, steepLabel, flatLabel,
            });
            const uiDraft = buildUiDraft({
                clientInfo, clientInfoMode,
                selectedLead: slimContractorOwner(selectedLead),
                selectedContractor: useAsContractor ? null : slimContractorOwner(selectedContractor),
                useAsContractor,
                clientNameSource,
                selectedProjectId, selectedProjectNo,
                workType, roofType, stories,
                steepRoofs, flatRoofs, activeSteepIdx, activeFlatIdx,
                flatUseScaraface, flatUseTelehandler, flatUseBoomLift, flatUseBathroom,
                inputs: EMPTY_UI_DRAFT_INPUTS, commissionPct, result, lineOverrides, unitCostOverrides, extraLines,
                disabledItemIds,
                attachments, relatedQuoteFiles, viewFiles, architectProject: architectSnapshot,
                scopeHtml, woodHtml, permitsHtml, permitsHidden, completionDays,
            });
            assertUiDraftMatchesLocalStorageKeys(uiDraft);
            const woodTotals = totalWoodQuantities(
                steepRoofs, flatRoofs, { includeSteep: showSteep, includeFlat: showFlat },
            );
            const estimation_data = buildEstimationData({
                clientInfo,
                clientInfoMode,
                selectedProjectId,
                selectedProjectNo,
                selectedOwner: selectedLead,
                selectedContractor,
                selectedCrmLead,
                useAsContractor,
                clientNameSource,
                workType,
                roofType,
                stories,
                showSteep,
                showFlat,
                steepSystemId,
                steepLabel,
                flatSystemId,
                flatLabel,
                flatSubstrate,
                flatScuppers,
                flatDrains,
                shingleUnderlaymentId,
                builtupInsulationId,
                builtupR20Qty,
                builtupSmoothQty,
                builtupSm15Qty,
                builtupCapId,
                builtupEdgeMetal,
                builtupEdgeSize,
                builtupParapetMetal,
                builtupParapetCoping,
                polyglassIpQty,
                polyglassEdgeMetal,
                polyglassEdgeSize,
                flatUseScaraface,
                flatUseTelehandler,
                flatUseBoomLift,
                flatUseBathroom,
                wpSbWarranty,
                tileUnderlaymentId,
                tileTypeId,
                tileFlashingMetal,
                tileFlashingSize,
                metalUnderlaymentId,
                metalTypeId,
                metalTypeSystemId,
                steepRoofs,
                flatRoofs,
                result,
                lineOverrides,
                unitCostOverrides,
                extraLines,
                disabledItemIds,
                adjustedHardCost,
                adjustedOverhead,
                adjustedProfit,
                adjustedTotal,
                clientPrice,
                commissionPct,
                commissionAmount,
                netToCompany,
                attachments,
                relatedQuoteFiles,
                viewFiles,
                uiDraft,
                architectProject: architectSnapshot,
                scopeHtml,
                woodHtml,
                permitsHtml,
                permitsHidden,
                completionDays,
            });
            const saveUrlBase = isEditMode
                ? `/estimator/save-quote/${editingQuoteId}`
                : '/estimator/save-quote';
            const saveUrl = confirmVoidSigning
                ? `${saveUrlBase}?confirm_void_signing=true`
                : saveUrlBase;
            const res = await authedFetch(saveUrl, {
                method: isEditMode ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    client_name: clientInfo.clientName,
                    address: clientInfo.address,
                    city: clientInfo.city,
                    state: clientInfo.state,
                    zip_code: clientInfo.zipCode,
                    email: clientInfo.email,
                    phone: clientInfo.phone,
                    folio: clientInfo.folio,
                    year_built: clientInfo.yearBuilt,
                    property_type: clientInfo.propertyType,
                    ...(selectedProjectId != null ? { project_id: selectedProjectId } : {}),
                    ...(selectedLead?.id ? { property_owner_id: Number(selectedLead.id) } : {}),
                    ...(selectedCrmLead?.id ? { lead_id: Number(selectedCrmLead.id) } : {}),
                    ...(useAsContractor && selectedLead?.id
                        ? {
                              contractor_id: Number(selectedLead.id),
                              use_as_contractor: true,
                          }
                        : selectedContractor?.id
                          ? {
                                contractor_id: Number(selectedContractor.id),
                                use_as_contractor: false,
                            }
                          : selectedLead?.id
                            ? {
                                  contractor_id: Number(selectedLead.id),
                                  use_as_contractor: true,
                              }
                            : {}),
                    // Edit: only after a superuser pick this session; an untouched seed must not undo a 3→0 handover.
                    ...(createdByUser != null && (!isEditMode || createdByDirty.current)
                        ? { created_by: Number(createdByUser.id) }
                        : {}),
                    ...(priceChangeReason
                        ? { price_change_reason: priceChangeReason }
                        : {}),
                    work_type: workType,
                    roof_type: roofType ?? '',
                    stories,
                    client_price: clientPrice,
                    calculation_performed: !!result,
                    show_steep: showSteep,
                    show_flat: showFlat,
                    steep_system_id: steepSystemId,
                    flat_system_id: flatSystemId,
                    flat_substrate: flatSubstrate,
                    flat_scuppers: flatScuppers,
                    flat_drains: flatDrains,
                    steep_label: steepLabel,
                    flat_label: flatLabel,
                    roof_system: roofSystem,
                    shingle_underlayment_id: shingleUnderlaymentId,
                    tile_underlayment_id: tileUnderlaymentId,
                    tile_type_id: tileTypeId,
                    tile_flashing_metal: tileFlashingMetal,
                    tile_flashing_size: tileFlashingSize,
                    metal_underlayment_id: metalUnderlaymentId,
                    metal_type_id: metalTypeSystemId,
                    builtup_insulation_id: builtupInsulationId,
                    builtup_r20_qty: builtupR20Qty,
                    builtup_smooth_qty: builtupSmoothQty,
                    builtup_sm15_qty: builtupSm15Qty,
                    builtup_cap_id: builtupCapId,
                    builtup_edge_metal: builtupEdgeMetal,
                    builtup_edge_size: builtupEdgeSize,
                    buildup_edge_id: `BUILDUP-EDGE-${builtupEdgeMetal}-${builtupEdgeSize}`,
                    status: targetStatus,
                    inputs: {
                        steep_area: totalSteepArea(steepRoofs),
                        flat_area: totalFlatArea(flatRoofs),
                        steep_edge: steepRoofs.reduce((s, r) => s + r.edge, 0),
                        ridge: steepRoofs.reduce((s, r) => s + r.ridge, 0),
                        valley: steepRoofs.reduce((s, r) => s + r.valley, 0),
                        steep_parapet: steepRoofs.reduce((s, r) => s + r.parapet, 0),
                        flat_edge: flatRoofs.reduce((s, r) => s + r.edge, 0),
                        flat_parapet: flatRoofs.reduce((s, r) => s + r.parapet, 0),
                        wood_plywood_sheets: woodTotals.plywoodSheets,
                        wood_lumber_pieces: woodTotals.lumberPieces,
                        plumbing_projections: [...steepRoofs, ...flatRoofs].reduce((s, r) => s + r.plumbingProjections, 0),
                        ventilation_projections: [...steepRoofs, ...flatRoofs].reduce((s, r) => s + r.ventilationProjections, 0),
                        overhead_pct: weightedMarginPct([...steepRoofs, ...flatRoofs]).overhead_pct,
                        profit_pct: weightedMarginPct([...steepRoofs, ...flatRoofs]).profit_pct,
                    },
                    scope_html: scopeHtml,
                    wood_html: woodHtml,
                    permits_html: permitsHtml,
                    permits_hidden: permitsHidden,
                    completion_days: completionDays,
                    estimation_data,
                }),
            });
            if (res.status === 401) {
                localStorage.removeItem(import.meta.env.VITE_USER_STORAGE_KEY);
                window.location.href = '/#/login';
                return;
            }
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                if (isContractSigningActiveError(res.status, data)) {
                    // Active DocuSeal signing: ask before voiding it. Retried with confirm_void_signing=true.
                    setVoidSigningPending({ targetStatus, priceChangeReason });
                    setVoidSigningConfirmOpen(true);
                    return;
                }
                const err = data?.errors ?? data?.detail;
                let msg = 'Failed to save quote';
                if (typeof err === 'string') msg = err;
                else if (err && typeof err === 'object') {
                    msg = String(err.root ?? err.price_change_reason ?? Object.values(err)[0] ?? msg);
                }
                notify(msg, { type: 'error' });
                return;
            }
            savedPriceBaselineRef.current = clientPrice;
            if (data.contract_signing_reissued === false) {
                notify(
                    "Signing was voided but could not be re-sent — start it again from the project's Contract tab.",
                    { type: 'warning' },
                );
            }
            const quoteId = data.quote_id ?? editingQuoteId;
            if (isTemporal) {
                if (!isEditMode && quoteId != null) {
                    resetEstimatorState();
                    navigate(`/quotes/${quoteId}`);
                }
                notify('Quote saved temporarily.', { type: 'info' });
                return;
            }
            resetEstimatorState();
            navigate(`/quotes/${quoteId}/show`);
        } catch {
            notify('Failed to save quote.', { type: 'error' });
        } finally {
            setSavingQuote(false);
        }
    };

    const requestSaveQuote = (targetStatus: number = quoteStatuses.pending) => {
        const isTemporal = targetStatus === quoteStatuses.pending_finish_quoting;
        if (isTemporal) {
            if (!canTemporalSave || !workType) return;
        } else if (!canSaveQuote || !workType || !roofType || !result) {
            return;
        }
        const baseline = savedPriceBaselineRef.current;
        if (
            isEditMode
            && baseline != null
            && Number(clientPrice) !== Number(baseline)
        ) {
            setPendingSaveStatus(targetStatus);
            setPriceReason('');
            setPriceReasonError('');
            setPriceReasonOpen(true);
            return;
        }
        void handleSaveQuote(targetStatus);
    };

    const confirmPriceChangeReason = () => {
        const cleaned = priceReason.trim();
        if (
            cleaned.length < PRICE_CHANGE_REASON_MIN
            || cleaned.length > PRICE_CHANGE_REASON_MAX
        ) {
            setPriceReasonError(
                `Reason must be ${PRICE_CHANGE_REASON_MIN}–${PRICE_CHANGE_REASON_MAX} characters.`,
            );
            return;
        }
        if (pendingSaveStatus == null) return;
        setPriceReasonOpen(false);
        void handleSaveQuote(pendingSaveStatus, cleaned);
        setPendingSaveStatus(null);
    };

    // ── Auto-save to localStorage (navigating away and back restores all work) ─
    useEffect(() => {
        if (isEditMode) return;
        try {
            localStorage.setItem(SAVE_KEY, JSON.stringify(buildUiDraft({
                clientInfo, clientInfoMode,
                selectedLead: slimContractorOwner(selectedLead),
                selectedContractor: useAsContractor ? null : slimContractorOwner(selectedContractor),
                useAsContractor,
                clientNameSource,
                selectedProjectId, selectedProjectNo,
                workType, roofType, stories,
                steepRoofs, flatRoofs, activeSteepIdx, activeFlatIdx,
                flatUseScaraface, flatUseTelehandler, flatUseBoomLift, flatUseBathroom,
                inputs: EMPTY_UI_DRAFT_INPUTS, commissionPct, result, lineOverrides, unitCostOverrides, extraLines,
                disabledItemIds,
                attachments, relatedQuoteFiles, viewFiles, architectProject,
                scopeHtml, woodHtml, permitsHtml, permitsHidden, completionDays,
            })));
        } catch { /* quota exceeded — ignore */ }
    }, [isEditMode, clientInfo, clientInfoMode, selectedLead, selectedContractor, useAsContractor, clientNameSource, selectedProjectId, selectedProjectNo, workType, roofType, stories,
        steepRoofs, flatRoofs, activeSteepIdx, activeFlatIdx,
        flatUseScaraface, flatUseTelehandler, flatUseBoomLift, flatUseBathroom,
        commissionPct, result, lineOverrides, unitCostOverrides, extraLines, disabledItemIds, attachments, relatedQuoteFiles, viewFiles, architectProject,
        scopeHtml, woodHtml, permitsHtml, permitsHidden, completionDays]);

    // ── Property lookup (auto-triggered when address + city are filled) ──────
    const [lookupLoading, setLookupLoading] = useState(false);
    const [lookupError,   setLookupError]   = useState('');
    const lookupGenRef = useRef(0);
    const projectSearchGenRef = useRef(0);

    const clearClientFieldsForAddressChange = useCallback((address: string) => {
        setSelectedProjectId(null);
        setSelectedProjectNo(null);
        setSelectedProjectPhase(null);
        setSelectedLead(null);
        setSelectedContractor(null);
        setUseAsContractor(true);
        setClientNameSource('owner');
        setClientInfoMode('search');
        setClientInfo(prev => ({
            ...defaultClientInfo(),
            address,
            city: prev.city,
            state: prev.state,
            zipCode: prev.zipCode,
        }));
    }, []);

    const handleLeadChange = useCallback((owner: ContractorOwner | null) => {
        setSelectedLead(owner);
        setLookupError('');
    }, []);

    // Client Estimate: name from owner|contractor toggle; email/phone always from contractor.
    useEffect(() => {
        setClientInfo(prev =>
            syncEstimateClientContacts(
                prev,
                selectedLead,
                selectedContractor,
                useAsContractor,
                clientNameSource,
            ),
        );
    }, [selectedLead, selectedContractor, useAsContractor, clientNameSource]);

    const applyProjectRow = useCallback((row: EstimatorProjectByAddress) => {
        setClientInfoMode('search');
        setSelectedProjectId(row.id);
        setSelectedProjectNo(row.phase === 'active' ? null : row.project_no);
        setSelectedProjectPhase(row.phase);
        setLookupError('');
        const { owner, contractor, useAsContractor: sameAsOwner } = contactsFromProjectRow(row);
        setSelectedLead(owner);
        setUseAsContractor(sameAsOwner);
        setSelectedContractor(contractor);
        if (sameAsOwner) setClientNameSource('owner');
        setClientInfo(prev => ({
            ...prev,
            city: row.city ?? prev.city,
            state: row.state ?? prev.state,
            zipCode: row.zip_code ?? prev.zipCode,
            folio: row.folio_number ?? '',
            yearBuilt: row.year_built != null ? String(row.year_built) : '',
            propertyType: row.type === 'Commercial' ? 'Commercial' : 'Residential',
        }));
    }, []);

    // Prefill from ?project_id= on quote create (Create quote from QuoteFolderShow).
    useEffect(() => {
        if (isEditMode || urlProjectId == null) return;
        if (projectPrefillDoneRef.current === urlProjectId) return;
        let cancelled = false;

        async function loadProjectRow(
            projectId: number,
        ): Promise<{ row: EstimatorProjectByAddress; address: string; ownerFull: ContractorOwner | null; contractorFull: ContractorOwner | null; crmLead: Lead | null } | null> {
            const res = await authedFetch(`/projects/${projectId}`);
            if (!res.ok) throw new Error('project fetch failed');
            const project = (await res.json()) as {
                id: number;
                project_no: number | null;
                address: string;
                city: string | null;
                state: string | null;
                zip_code: string | null;
                folio_number: string | null;
                year_built: number | null;
                type: string | null;
                phase: string;
                contractor_id: number;
                contractor_name?: string | null;
                property_owner_id: number;
                property_owner_name?: string | null;
                lead_id?: number | null;
            };

            const ownerId = project.property_owner_id;
            const contractorId = project.contractor_id;
            const sameContact = Number(ownerId) === Number(contractorId);

            const [ownerFull, contractorFull, crmLead] = await Promise.all([
                ownerId
                    ? getContractorOwner(ownerId).catch(() => null)
                    : Promise.resolve(null),
                !sameContact && contractorId
                    ? getContractorOwner(contractorId).catch(() => null)
                    : Promise.resolve(null),
                project.lead_id
                    ? dataProvider.getLead(project.lead_id).catch(() => null)
                    : Promise.resolve(null),
            ]);

            const typeUi = /commercial/i.test(project.type ?? '')
                ? 'Commercial'
                : 'Residential';
            const row: EstimatorProjectByAddress = {
                id: project.id,
                project_no: project.project_no,
                city: project.city,
                state: project.state,
                zip_code: project.zip_code,
                folio_number: project.folio_number,
                contractor_id: project.contractor_id,
                property_owner_id: project.property_owner_id,
                client_name:
                    ownerFull?.name ||
                    project.property_owner_name ||
                    '',
                contractor_name:
                    (sameContact
                        ? ownerFull?.name
                        : contractorFull?.name) ||
                    project.contractor_name ||
                    '',
                email: ownerFull?.email || '',
                phone: ownerFull?.phone_number || '',
                year_built: project.year_built,
                type: typeUi,
                phase: project.phase,
            };

            return { row, address: project.address, ownerFull, contractorFull, crmLead };
        }

        (async () => {
            try {
                const [projectRow, quotePrefill] = await Promise.all([
                    loadProjectRow(urlProjectId).catch(() => null),
                    fetchFirstProjectQuoteDraft(urlProjectId),
                ]);

                if (cancelled) return;

                // Apply quote snapshot first (before project prefill so project identity wins)
                if (quotePrefill) {
                    try {
                        applyDraftToState(quotePrefill.draft);
                        notify('Prefilled from the first quote of this project.', {
                            type: 'info',
                            autoHideDuration: 10000,
                            action: (
                                <Button
                                    color="inherit"
                                    size="small"
                                    onClick={() =>
                                        window.open(
                                            `/#/quotes/${quotePrefill.quoteId}/show`,
                                            '_blank',
                                            'noopener,noreferrer',
                                        )
                                    }
                                >
                                    Open quote
                                </Button>
                            ),
                        });
                    } catch {
                        // unusable snapshot → plain new quote
                    }
                }

                // Then the existing project block, unchanged and in the same order
                if (projectRow) {
                    const { row, address, ownerFull, contractorFull, crmLead } = projectRow;
                    applyProjectRow(row);
                    setProjectSearchResults([row]);
                    setClientInfo(prev => ({
                        ...prev,
                        address: address?.trim() || prev.address,
                    }));
                    if (ownerFull) setSelectedLead(ownerFull);
                    // Only set contractor if it differs from owner (inverse of sameContact logic)
                    if (contractorFull && row.contractor_id !== row.property_owner_id) {
                        setSelectedContractor(contractorFull);
                        setUseAsContractor(false);
                    }
                    if (crmLead) setSelectedCrmLead(crmLead);
                } else if (!cancelled) {
                    notify('Could not load project from URL', { type: 'warning' });
                }

                if (!cancelled) {
                    projectPrefillDoneRef.current = urlProjectId;
                }
            } finally {
                if (!cancelled) {
                    setProjectPrefillLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isEditMode, urlProjectId, notify, applyProjectRow, applyDraftToState]);

    const applyCopyClientOnly = useCallback((row: EstimatorProjectByAddress) => {
        setClientInfoMode('create');
        setSelectedProjectId(null);
        setSelectedProjectNo(null);
        setSelectedProjectPhase(null);
        setLookupError('');
        const { owner, contractor, useAsContractor: sameAsOwner } = contactsFromProjectRow(row);
        setSelectedLead(owner);
        setUseAsContractor(sameAsOwner);
        setSelectedContractor(contractor);
        if (sameAsOwner) setClientNameSource('owner');
        setClientInfo(prev => ({
            ...prev,
            city: row.city ?? prev.city,
            state: row.state ?? prev.state,
            zipCode: row.zip_code ?? prev.zipCode,
            folio: row.folio_number ?? '',
            yearBuilt: row.year_built != null ? String(row.year_built) : '',
            propertyType: row.type === 'Commercial' ? 'Commercial' : 'Residential',
        }));
    }, []);

    const runProjectSearch = useCallback(async (address: string, gen: number) => {
        const trimmed = address.trim();
        if (!trimmed) {
            setProjectSearchResults([]);
            setProjectSearchError('');
            return;
        }
        setProjectSearchLoading(true);
        setProjectSearchError('');
        try {
            const params = new URLSearchParams({ address: trimmed });
            const res = await authedFetch(
                `/estimator/projects-by-address?${params}`,
            );
            if (gen !== projectSearchGenRef.current) return;
            if (!res.ok) {
                setProjectSearchResults([]);
                setProjectSearchError('Could not search projects for this address.');
                return;
            }
            const data: EstimatorProjectByAddress[] = await res.json();
            if (gen !== projectSearchGenRef.current) return;
            setProjectSearchResults(data);
            if (selectableProjectsByAddress(data).length === 0) {
                // Keep ?project_id= prefill if search misses; user address edits clear via AddressAutocomplete.
                setSelectedProjectId(current => {
                    if (current == null) setClientInfoMode('create');
                    return current;
                });
            }
        } catch {
            if (gen !== projectSearchGenRef.current) return;
            setProjectSearchResults([]);
            setProjectSearchError('Could not search projects for this address.');
        } finally {
            if (gen === projectSearchGenRef.current) setProjectSearchLoading(false);
        }
    }, []);

    const runPropertyLookup = useCallback(async (address: string, city: string, gen: number) => {
        setLookupLoading(true);
        setLookupError('');
        try {
            const params = new URLSearchParams({ address, city });
            const res    = await authedFetch(
                `/property/lookup?${params}`
            );
            if (gen !== lookupGenRef.current) return;
            if (!res.ok) {
                setLookupError('Address could not be found in county records.');
                return;
            }
            const data: { owner: string; folio: string; year_built: string; owner_address: string } = await res.json();
            if (gen !== lookupGenRef.current) return;
            setClientInfo(prev => ({
                ...prev,
                folio: data.folio,
                yearBuilt: data.year_built ?? '',
            }));
            const ownerName = (data.owner || '').trim();
            if (!ownerName) return;
            try {
                const person = await findOrCreateContractorOwnerByName(ownerName);
                if (gen !== lookupGenRef.current) return;
                setSelectedLead(person);
            } catch {
                if (gen !== lookupGenRef.current) return;
                // Folio/year still applied; owner stays unset if contractors_owner write fails.
                setLookupError('Property found, but owner could not be saved to contacts.');
            }
        } catch {
            if (gen !== lookupGenRef.current) return;
            setLookupError('Address could not be found in county records.');
        } finally {
            if (gen === lookupGenRef.current) setLookupLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!clientInfo.address.trim()) {
            setProjectSearchResults([]);
            return;
        }
        const gen = ++projectSearchGenRef.current;
        const timer = setTimeout(() => {
            runProjectSearch(clientInfo.address, gen);
        }, 500);
        return () => clearTimeout(timer);
    }, [clientInfo.address, runProjectSearch]);

    useEffect(() => {
        if (clientInfoMode !== 'create' || selectedProjectId != null) return;
        if (!clientInfo.address || !clientInfo.city) return;
        const gen = ++lookupGenRef.current;
        const timer = setTimeout(() => {
            runPropertyLookup(clientInfo.address, clientInfo.city, gen);
        }, 900);
        return () => clearTimeout(timer);
    }, [clientInfo.address, clientInfo.city, clientInfoMode, selectedProjectId, runPropertyLookup]);

    const clearDraft = () => {
        localStorage.removeItem(SAVE_KEY);
        resetEstimatorState();
    };

    const fromUrlProject = urlProjectId != null;
    const showClientDetailFields = clientInfoMode === 'create';
    // Prefill from ?project_id= should show city/folio/etc. like edit/create, not the collapsed search view.
    const showAllClientFields = showClientDetailFields || isEditMode || fromUrlProject;

    const showProjectSearchList = !!clientInfo.address.trim();

    const selectableProjectRows = selectableProjectsByAddress(projectSearchResults);
    const siteHistoryQuotes = aggregateSiteHistoryQuotes(projectSearchResults);
    const contractEstimateNumber: number | null = selectedProjectNo ?? null;

    if (isEditMode && quoteError) {
        return (
            <ResourceNotFound
                resourceLabel="Quote"
                listPath="/quotes"
                listLabel="Back to quotes"
            />
        );
    }

    if ((isEditMode && (quoteLoading || !editHydrated)) || (!isEditMode && projectPrefillLoading)) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 48px)' }}>
                <Stack spacing={2} alignItems="center">
                    <CircularProgress />
                    <Typography variant="body2" color="text.secondary">
                        Loading project and quote data…
                    </Typography>
                </Stack>
            </Box>
        );
    }

    // Superuser-only user picker: "Created by" on create, "Responsible" on edit.
    const createdByField = isAdmin ? (
        <Autocomplete
            options={
                createdByUser &&
                !createdByOptions.some(o => o.id === createdByUser.id)
                    ? [createdByUser, ...createdByOptions]
                    : createdByOptions
            }
            loading={createdByLoading}
            value={createdByUser}
            inputValue={createdByInput}
            onInputChange={(_, v) => setCreatedByInput(v)}
            onChange={(_, v) => {
                createdByDirty.current = true;
                setCreatedByUser(v);
            }}
            filterOptions={x => x}
            getOptionLabel={createdByLabel}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={params => (
                <TextField
                    {...params}
                    label={isEditMode ? 'Responsible' : 'Created by'}
                    placeholder="Search user by name"
                    size="small"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {createdByLoading ? (
                                    <CircularProgress color="inherit" size={16} />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    ) : null;

    return (
        <Box sx={{ display: 'flex', height: 'calc(100vh - 48px)', overflow: 'hidden' }}>


            {/* ══ LEFT PANEL ════════════════════════════════════════════════ */}
            <Box sx={{
                width: 370, flexShrink: 0, overflowY: 'auto', p: 2,
                borderRight: 1, borderColor: 'divider'
            }}>
                <Stack sx={{ mb: 2 }} spacing={0.5}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <RoofingIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                            <Typography variant="subtitle1" fontWeight={700}>Roofing Estimator</Typography>
                        </Stack>
                        {!isEditMode && (result || clientInfo.clientName) && (
                            <Button size="small" variant="outlined" color="error"
                                onClick={clearDraft}
                                sx={{ fontSize: 11, textTransform: 'none', py: 0.25 }}>
                                New Estimate
                            </Button>
                        )}
                    </Stack>
                    <Button
                        size="small"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => {
                            if (isEditMode && editingQuoteId != null) {
                                navigate(`/quotes/${editingQuoteId}/show`);
                                return;
                            }
                            const urlProjectId = Number(searchParams.get('project_id'));
                            if (Number.isFinite(urlProjectId) && urlProjectId > 0) {
                                navigate(
                                    projectPhasePath(
                                        urlProjectId,
                                        selectedProjectPhase ?? 'quoting',
                                    ),
                                );
                                return;
                            }
                            if (selectedProjectId != null) {
                                navigate(
                                    projectPhasePath(
                                        selectedProjectId,
                                        selectedProjectPhase,
                                    ),
                                );
                                return;
                            }
                            const urlLeadId = Number(searchParams.get('lead_id'));
                            if (Number.isFinite(urlLeadId) && urlLeadId > 0) {
                                navigate(`/leads/${urlLeadId}/show`);
                                return;
                            }
                            if (selectedCrmLead?.id) {
                                navigate(`/leads/${selectedCrmLead.id}/show`);
                                return;
                            }
                            navigate('/quotes');
                        }}
                        sx={{
                            alignSelf: 'flex-start',
                            textTransform: 'none',
                            fontSize: 12,
                            px: 0.5,
                            minWidth: 0,
                        }}
                    >
                        Back
                    </Button>
                    {isEditMode && (
                        <Stack spacing={0.25}>
                            <Typography variant="caption" color="text.secondary">
                                Quote ID: {editingQuoteId}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Project No:{' '}
                                {selectedProjectNo
                                    ?? quoteRecord?.folder_number
                                    ?? '—'}
                            </Typography>
                            {editFileName ? (
                                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                    File: {editFileName}
                                </Typography>
                            ) : null}
                        </Stack>
                    )}
                </Stack>

                {!isEditMode && (
                    <SectionCard step={1} title="Initial config">
                        <Stack spacing={1.5}>
                            {createdByField}
                            <CrmLeadInput
                                value={selectedCrmLead}
                                onChange={setSelectedCrmLead}
                            />
                        </Stack>
                    </SectionCard>
                )}

                {isEditMode && editHydrated && (
                    <SectionCard step={1} title="CRM Project">
                        <Stack spacing={1.5}>
                            {createdByField}
                            <CrmLeadInput
                                value={selectedCrmLead}
                                onChange={setSelectedCrmLead}
                                initialLeadId={
                                    (quoteRecord as Quote & { project?: { lead_id?: number | null } })
                                        ?.project?.lead_id ?? null
                                }
                            />
                        </Stack>
                    </SectionCard>
                )}

                {/* Step 2 — Client Info */}
                <SectionCard step={2} title="Client Information" complete={!!clientInfo.clientName}>
                    <Stack spacing={1.5}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            {isEditMode ? 'Project Address' : fromUrlProject ? 'Property address' : 'Project search'}
                        </Typography>

                        <AddressAutocomplete
                            label={isEditMode ? 'Project Address' : undefined}
                            value={clientInfo.address}
                            onChange={v => {
                                if (isEditMode) {
                                    setClientInfo(prev => ({ ...prev, address: v }));
                                } else {
                                    clearClientFieldsForAddressChange(v);
                                }
                                setLookupError('');
                            }}
                            onPlaceSelected={parts => {
                                setLookupError('');
                                if (isEditMode) {
                                    setClientInfo(prev => ({
                                        ...prev,
                                        address: parts.fullAddress,
                                        city: parts.city || prev.city,
                                        state: parts.state || prev.state,
                                        zipCode: parts.zipCode || prev.zipCode,
                                    }));
                                    return;
                                }
                                setSelectedProjectId(null);
                                setSelectedProjectNo(null);
                                setSelectedProjectPhase(null);
                                setClientInfoMode('search');
                                setClientInfo(prev => ({
                                    ...defaultClientInfo(),
                                    address: parts.fullAddress,
                                    city: parts.city || prev.city,
                                    state: parts.state || prev.state,
                                    zipCode: parts.zipCode || prev.zipCode,
                                }));
                                if (
                                    clientInfoMode === 'create' &&
                                    parts.fullAddress &&
                                    parts.city
                                ) {
                                    const gen = ++lookupGenRef.current;
                                    runPropertyLookup(parts.fullAddress, parts.city, gen);
                                }
                            }}
                        />

                        {showProjectSearchList && !isEditMode && selectedProjectId == null && (
                            <>
                                {projectSearchLoading && (
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <CircularProgress size={12} />
                                        <Typography variant="caption" color="text.disabled">
                                            Searching projects…
                                        </Typography>
                                    </Stack>
                                )}
                                {projectSearchError && (
                                    <Typography variant="caption" color="error.main">
                                        {projectSearchError}
                                    </Typography>
                                )}

                                {!projectSearchLoading && !projectSearchError && (
                                    <SiteHistoryPanel
                                        key={clientInfo.address.trim().toLowerCase()}
                                        quotes={siteHistoryQuotes}
                                        projectsByAddress={projectSearchResults}
                                        onCopyClient={applyCopyClientOnly}
                                    />
                                )}

                                <Divider />
                                <Typography variant="caption" color="text.secondary" fontWeight={600} align="left">Select existing pending project</Typography>
                                {!projectSearchLoading && !projectSearchError && (
                                    selectableProjectRows.length === 0 ? (
                                        <Typography variant="caption" color="error.main">
                                            No related projects at this address.
                                        </Typography>
                                    ) : (
                                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 160, overflowX: 'auto', paddingBottom: '15px' }}>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontSize: 11, py: 0.5 }}>No.</TableCell>
                                                        <TableCell sx={{ fontSize: 11, py: 0.5 }}>City</TableCell>
                                                        <TableCell sx={{ fontSize: 11, py: 0.5 }}>St</TableCell>
                                                        <TableCell sx={{ fontSize: 11, py: 0.5 }}>Zip</TableCell>
                                                        <TableCell sx={{ fontSize: 11, py: 0.5 }}>Folio</TableCell>
                                                        <TableCell sx={{ fontSize: 11, py: 0.5 }}>Client</TableCell>
                                                        <TableCell sx={{ fontSize: 11, py: 0.5 }}>Phase</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {selectableProjectRows.map(row => (
                                                        <TableRow
                                                            key={row.id}
                                                            hover
                                                            selected={selectedProjectId === row.id}
                                                            onClick={() => applyProjectRow(row)}
                                                            sx={{ cursor: 'pointer' }}
                                                        >
                                                            <TableCell sx={{ fontSize: 11, py: 0.5 }}>
                                                                #{row.project_no ?? '—'}
                                                            </TableCell>
                                                            <TableCell sx={{ fontSize: 11, py: 0.5 }}>{row.city ?? '—'}</TableCell>
                                                            <TableCell sx={{ fontSize: 11, py: 0.5 }}>{row.state ?? '—'}</TableCell>
                                                            <TableCell sx={{ fontSize: 11, py: 0.5 }}>{row.zip_code ?? '—'}</TableCell>
                                                            <TableCell sx={{ fontSize: 11, py: 0.5 }}>{row.folio_number ?? '—'}</TableCell>
                                                            <TableCell sx={{ fontSize: 11, py: 0.5 }}>{row.client_name}</TableCell>
                                                            <TableCell sx={{ fontSize: 11, py: 0.5 }}>{row.phase}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )
                                )}
                            </>
                        )}

                        {!isEditMode && !fromUrlProject && (
                            <Typography variant="caption" color="text.secondary" align="center">or</Typography>
                        )}

                        {!isEditMode && !fromUrlProject && clientInfoMode === 'search' && (
                            <Button
                                size="small"
                                variant="outlined"
                                fullWidth
                                startIcon={<AddCircleOutlineIcon />}
                                onClick={() => {
                                    setClientInfoMode('create');
                                    setSelectedProjectId(null);
                                    setSelectedProjectNo(null);
                                    setSelectedProjectPhase(null);
                                    setLookupError('');
                                }}
                                sx={{ textTransform: 'none', fontSize: 12 }}
                            >
                                Create project
                            </Button>
                        )}

                        {(showClientDetailFields || selectedProjectId != null) && (
                            <>
                                {!isEditMode && showClientDetailFields && (
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => {
                                            setClientInfoMode('search');
                                            setLookupError('');
                                        }}
                                        sx={{ textTransform: 'none', fontSize: 11, alignSelf: 'flex-start', p: 0, minHeight: 0 }}
                                    >
                                        Back to project search
                                    </Button>
                                )}
                                {showAllClientFields && (
                                    <Grid container spacing={1}>
                                        <Grid item xs={6}>
                                            <TextField label="City" size="small" fullWidth
                                                value={clientInfo.city}
                                                onChange={e => { setClient('city')(e.target.value); setLookupError(''); }} />
                                        </Grid>
                                        <Grid item xs={3}>
                                            <TextField label="State" size="small" fullWidth
                                                value={clientInfo.state}
                                                onChange={e => setClient('state')(e.target.value)} />
                                        </Grid>
                                        <Grid item xs={3}>
                                            <TextField label="Zip" size="small" fullWidth
                                                value={clientInfo.zipCode}
                                                onChange={e => setClient('zipCode')(e.target.value)} />
                                        </Grid>
                                    </Grid>
                                )}

                                {lookupLoading && clientInfoMode === 'create' && (
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <CircularProgress size={12} />
                                        <Typography variant="caption" color="text.disabled">
                                            Looking up county records…
                                        </Typography>
                                    </Stack>
                                )}

                                <ContractorOwnerAssignControl
                                    label="Owner"
                                    value={selectedLead}
                                    onChange={handleLeadChange}
                                    error={!!lookupError && !selectedLead && clientInfoMode === 'create'}
                                    helperText={
                                        lookupError && !selectedLead && clientInfoMode === 'create'
                                            ? lookupError
                                            : undefined
                                    }
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={useAsContractor}
                                            onChange={(_, checked) => {
                                                setUseAsContractor(checked);
                                                if (checked) {
                                                    setSelectedContractor(null);
                                                    setClientNameSource('owner');
                                                }
                                            }}
                                            size="small"
                                        />
                                    }
                                    label="Use as contractor"
                                />
                                {!useAsContractor && (
                                    <ContractorOwnerAssignControl
                                        label="Contractor"
                                        value={selectedContractor}
                                        onChange={setSelectedContractor}
                                    />
                                )}

                                {showAllClientFields && (
                                    <>
                                        <Grid container spacing={1}>
                                            <Grid item xs={8}>
                                                <TextField label="Folio #" size="small" fullWidth
                                                    value={clientInfo.folio}
                                                    onChange={e => setClient('folio')(e.target.value)}
                                                    error={!!lookupError && !clientInfo.folio && clientInfoMode === 'create'}
                                                    helperText={
                                                        lookupError && !clientInfo.folio && clientInfoMode === 'create'
                                                            ? lookupError
                                                            : ''
                                                    }
                                                    FormHelperTextProps={{ sx: { color: 'error.main', fontSize: 11 } }}
                                                />
                                            </Grid>
                                            <Grid item xs={4}>
                                                <TextField label="Year Built" size="small" fullWidth
                                                    value={clientInfo.yearBuilt}
                                                    onChange={e => setClient('yearBuilt')(e.target.value)} />
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12}>
                                                <Select size="small" fullWidth
                                                    value={clientInfo.propertyType}
                                                    onChange={e => setClient('propertyType')(e.target.value)}>
                                                    <MenuItem value="Residential">Residential</MenuItem>
                                                    <MenuItem value="Commercial">Commercial</MenuItem>
                                                </Select>
                                            </Grid>
                                        </Grid>
                                    </>
                                )}
                            </>
                        )}
                    </Stack>
                </SectionCard>

                {/* Step 3 — Job Description */}
                <SectionCard step={3} title="Job Description" complete={workType !== null && roofType !== null}>
                    <Stack spacing={1.5}>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Type of Work</Typography>
                            <Box sx={{ mt: 0.5 }}>
                                <ToggleButtonGroup value={workType} exclusive onChange={(_, v) => v && setWorkType(v)} size="small" fullWidth>
                                    {WORK_TYPES.map(w => (
                                        <ToggleButton key={w} value={w} sx={{ textTransform: 'none', fontSize: 12, py: 0.5, fontWeight: 600 }}>
                                            {w}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </Box>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Roof Type</Typography>
                            <Box sx={{ mt: 0.5 }}>
                                <ToggleButtonGroup value={roofType} exclusive size="small" fullWidth
                                    onChange={(_, v) => {
                                        if (!v) return;
                                        setRoofType(v);
                                        if (v === 'Flat Roof')  setSteepSystemId('NONE-STEEP');
                                        if (v === 'Slope Roof') setFlatSystemId('NONE-FLAT');
                                    }}>
                                    {ROOF_TYPES.map(r => (
                                        <ToggleButton key={r} value={r} sx={{ textTransform: 'none', fontSize: 12, py: 0.5, fontWeight: 600 }}>
                                            {r}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </Box>
                        </Box>
                        <TextField label="Stories" type="number" size="small" fullWidth
                            value={stories} onChange={e => setStories(parseInt(e.target.value) || 1)}
                            inputProps={{ min: 1, max: 10 }} />
                    </Stack>
                </SectionCard>

                {/* Step 4 — System */}
                {roofType && (
                    <SectionCard step={4} title="Roof System">
                        <Stack spacing={2}>
                            {showSteep && (
                                <Box>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>Steep Slope</Typography>
                                        <Button size="small" onClick={addSteepRoof} sx={{ textTransform: 'none', fontSize: 11 }}>
                                            + Add steep roof
                                        </Button>
                                    </Stack>
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1 }}>
                                        {steepRoofs.map((r, i) => (
                                            <Chip key={r.id}
                                                label={r.label}
                                                size="small"
                                                color={i === activeSteepIdx ? 'primary' : 'default'}
                                                onClick={() => setActiveSteepIdx(i)}
                                                onDelete={steepRoofs.length > 1 ? () => removeSteepRoof(i) : undefined}
                                            />
                                        ))}
                                    </Stack>
                                    <TextField label="Roof label" size="small" fullWidth sx={{ mb: 1 }}
                                        value={activeSteep.label}
                                        onChange={e => patchSteepAt(activeSteepIdx, { label: e.target.value })} />
                                    <Box sx={{ mt: 1 }}>
                                        <SystemPicker options={STEEP_SYSTEMS} value={steepSystemId}
                                            onChange={id => { setSteepSystemId(id); }} />
                                    </Box>
                                </Box>
                            )}

                            {/* 3.1 — Underlayment System (only when Shingle is selected) */}
                            {showSteep && steepSystemId === 'SHINGLE' && (
                                <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                                    <Typography variant="caption" color="primary.main" fontWeight={700} sx={{ display: 'block', mb: 1 }}>
                                        3.1 — Underlayment System
                                    </Typography>
                                    <Stack spacing={1}>
                                        {SHINGLE_UNDERLAYMENT_OPTIONS.map(opt => (
                                            <Box
                                                key={opt.id}
                                                onClick={() => { setShingleUnderlaymentId(opt.id); }}
                                                sx={{
                                                    p: 1.25,
                                                    borderRadius: 1,
                                                    border: '1px solid',
                                                    borderColor: shingleUnderlaymentId === opt.id ? 'primary.main' : 'divider',
                                                    bgcolor: shingleUnderlaymentId === opt.id ? 'primary.50' : 'background.paper',
                                                    cursor: 'pointer',
                                                    '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
                                                    transition: 'all 0.15s',
                                                }}
                                            >
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Box sx={{
                                                        width: 14, height: 14, borderRadius: '50%',
                                                        border: '2px solid',
                                                        borderColor: shingleUnderlaymentId === opt.id ? 'primary.main' : 'text.disabled',
                                                        bgcolor: shingleUnderlaymentId === opt.id ? 'primary.main' : 'transparent',
                                                        flexShrink: 0,
                                                    }} />
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={shingleUnderlaymentId === opt.id ? 700 : 400}>
                                                            {opt.label}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {opt.description}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            )}

                            {/* 3.1 — Underlayment System (only when Tile is selected) */}
                            {showSteep && steepSystemId === 'TILE' && (
                                <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                                    <Typography variant="caption" color="primary.main" fontWeight={700} sx={{ display: 'block', mb: 1 }}>
                                        3.1 — Underlayment System
                                    </Typography>
                                    <Stack spacing={1}>
                                        {TILE_UNDERLAYMENT_OPTIONS.map(opt => (
                                            <Box
                                                key={opt.id}
                                                onClick={() => { setTileUnderlaymentId(opt.id); }}
                                                sx={{
                                                    p: 1.25,
                                                    borderRadius: 1,
                                                    border: '1px solid',
                                                    borderColor: tileUnderlaymentId === opt.id ? 'primary.main' : 'divider',
                                                    bgcolor: tileUnderlaymentId === opt.id ? 'primary.50' : 'background.paper',
                                                    cursor: 'pointer',
                                                    '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
                                                    transition: 'all 0.15s',
                                                }}
                                            >
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Box sx={{
                                                        width: 14, height: 14, borderRadius: '50%',
                                                        border: '2px solid',
                                                        borderColor: tileUnderlaymentId === opt.id ? 'primary.main' : 'text.disabled',
                                                        bgcolor: tileUnderlaymentId === opt.id ? 'primary.main' : 'transparent',
                                                        flexShrink: 0,
                                                    }} />
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={tileUnderlaymentId === opt.id ? 700 : 400}>
                                                            {opt.label}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {opt.description}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            )}

                            {/* 3.2 — Tile Selection */}
                            {showSteep && steepSystemId === 'TILE' && (
                                <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'warning.main' }}>
                                    <Typography variant="caption" color="warning.dark" fontWeight={700} sx={{ display: 'block', mb: 1 }}>
                                        3.2 — Tile Selection
                                    </Typography>
                                    <Stack spacing={1}>
                                        {TILE_TYPE_OPTIONS.map(opt => (
                                            <Box
                                                key={opt.id}
                                                onClick={() => { setTileTypeId(opt.id); }}
                                                sx={{
                                                    p: 1.25,
                                                    borderRadius: 1,
                                                    border: '1px solid',
                                                    borderColor: tileTypeId === opt.id ? 'warning.main' : 'divider',
                                                    bgcolor: tileTypeId === opt.id ? 'warning.50' : 'background.paper',
                                                    cursor: 'pointer',
                                                    '&:hover': { borderColor: 'warning.light', bgcolor: 'action.hover' },
                                                    transition: 'all 0.15s',
                                                }}
                                            >
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Box sx={{
                                                        width: 14, height: 14, borderRadius: '50%',
                                                        border: '2px solid',
                                                        borderColor: tileTypeId === opt.id ? 'warning.main' : 'text.disabled',
                                                        bgcolor: tileTypeId === opt.id ? 'warning.main' : 'transparent',
                                                        flexShrink: 0,
                                                    }} />
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={tileTypeId === opt.id ? 700 : 400}>
                                                            {opt.label}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {opt.description}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            )}

                            {/* 3.3 — Flashing */}
                            {showSteep && steepSystemId === 'TILE' && (
                                <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'success.main' }}>
                                    <Typography variant="caption" color="success.dark" fontWeight={700} sx={{ display: 'block', mb: 1.5 }}>
                                        3.3 — Flashing
                                    </Typography>
                                    <Stack spacing={2}>
                                        {/* Metal type */}
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.75 }}>
                                                Metal Type
                                            </Typography>
                                            <Stack spacing={1}>
                                                {TILE_FLASHING_METALS.map(opt => (
                                                    <Box
                                                        key={opt.id}
                                                        onClick={() => { setTileFlashingMetal(opt.id); }}
                                                        sx={{
                                                            p: 1.25, borderRadius: 1, border: '1px solid',
                                                            borderColor: tileFlashingMetal === opt.id ? 'success.main' : 'divider',
                                                            bgcolor: tileFlashingMetal === opt.id ? 'success.50' : 'background.paper',
                                                            cursor: 'pointer',
                                                            '&:hover': { borderColor: 'success.light', bgcolor: 'action.hover' },
                                                            transition: 'all 0.15s',
                                                        }}
                                                    >
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <Box sx={{
                                                                width: 14, height: 14, borderRadius: '50%',
                                                                border: '2px solid',
                                                                borderColor: tileFlashingMetal === opt.id ? 'success.main' : 'text.disabled',
                                                                bgcolor: tileFlashingMetal === opt.id ? 'success.main' : 'transparent',
                                                                flexShrink: 0,
                                                            }} />
                                                            <Box>
                                                                <Typography variant="body2" fontWeight={tileFlashingMetal === opt.id ? 700 : 400}>
                                                                    {opt.label}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {opt.description}
                                                                </Typography>
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Box>
                                        {/* Drip edge size */}
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.75 }}>
                                                Drip Edge Size
                                            </Typography>
                                            <Stack direction="row" spacing={1}>
                                                {TILE_FLASHING_EDGE_SIZES.map(opt => (
                                                    <Box
                                                        key={opt.id}
                                                        onClick={() => { setTileFlashingSize(opt.id); }}
                                                        sx={{
                                                            flex: 1, p: 1.25, borderRadius: 1, border: '1px solid',
                                                            borderColor: tileFlashingSize === opt.id ? 'success.main' : 'divider',
                                                            bgcolor: tileFlashingSize === opt.id ? 'success.50' : 'background.paper',
                                                            cursor: 'pointer', textAlign: 'center',
                                                            '&:hover': { borderColor: 'success.light', bgcolor: 'action.hover' },
                                                            transition: 'all 0.15s',
                                                        }}
                                                    >
                                                        <Typography variant="body2" fontWeight={tileFlashingSize === opt.id ? 700 : 400}>
                                                            {opt.label}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {opt.description}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Box>
                                    </Stack>
                                </Box>
                            )}

                            {/* Metal Type subsection (only when Metal is selected) */}
                            {showSteep && steepSystemId === 'METAL' && (
                                <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'info.main' }}>
                                    <Typography variant="caption" color="info.dark" fontWeight={700} sx={{ display: 'block', mb: 1 }}>
                                        Metal Type
                                    </Typography>
                                    <Stack spacing={1}>
                                        {METAL_TYPE_OPTIONS.map(opt => (
                                            <Box
                                                key={opt.id}
                                                onClick={() => { if (opt.ready) { setMetalTypeId(opt.id); } }}
                                                sx={{
                                                    p: 1.25, borderRadius: 1, border: '1px solid',
                                                    borderColor: metalTypeId === opt.id ? 'info.main' : 'divider',
                                                    bgcolor: !opt.ready ? 'action.disabledBackground'
                                                           : metalTypeId === opt.id ? 'info.50' : 'background.paper',
                                                    cursor: opt.ready ? 'pointer' : 'default',
                                                    '&:hover': opt.ready ? { borderColor: 'info.light', bgcolor: 'action.hover' } : {},
                                                    transition: 'all 0.15s',
                                                }}
                                            >
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Box sx={{
                                                        width: 14, height: 14, borderRadius: '50%',
                                                        border: '2px solid',
                                                        borderColor: !opt.ready ? 'text.disabled'
                                                                   : metalTypeId === opt.id ? 'info.main' : 'text.disabled',
                                                        bgcolor: metalTypeId === opt.id && opt.ready ? 'info.main' : 'transparent',
                                                        flexShrink: 0,
                                                    }} />
                                                    <Box sx={{ flex: 1 }}>
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <Typography variant="body2" fontWeight={metalTypeId === opt.id ? 700 : 400}
                                                                color={opt.ready ? 'text.primary' : 'text.disabled'}>
                                                                {opt.label}
                                                            </Typography>
                                                            {!opt.ready && (
                                                                <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                                                                    Coming soon
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {opt.description}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            )}

                            {/* Metal Underlayment subsection */}
                            {showSteep && steepSystemId === 'METAL' && (
                                <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                                    <Typography variant="caption" color="primary.main" fontWeight={700} sx={{ display: 'block', mb: 1 }}>
                                        Underlayment System
                                    </Typography>
                                    <Stack spacing={1}>
                                        {METAL_UNDERLAYMENT_OPTIONS.map(opt => (
                                            <Box
                                                key={opt.id}
                                                onClick={() => { setMetalUnderlaymentId(opt.id); }}
                                                sx={{
                                                    p: 1.25, borderRadius: 1, border: '1px solid',
                                                    borderColor: metalUnderlaymentId === opt.id ? 'primary.main' : 'divider',
                                                    bgcolor: metalUnderlaymentId === opt.id ? 'primary.50' : 'background.paper',
                                                    cursor: 'pointer',
                                                    '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
                                                    transition: 'all 0.15s',
                                                }}
                                            >
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Box sx={{
                                                        width: 14, height: 14, borderRadius: '50%',
                                                        border: '2px solid',
                                                        borderColor: metalUnderlaymentId === opt.id ? 'primary.main' : 'text.disabled',
                                                        bgcolor: metalUnderlaymentId === opt.id ? 'primary.main' : 'transparent',
                                                        flexShrink: 0,
                                                    }} />
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={metalUnderlaymentId === opt.id ? 700 : 400}>
                                                            {opt.label}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {opt.description}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            )}

                            {/* Steep roof measurements (active roof) */}
                            {showSteep && (
                                <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'grey.400' }}>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                        Measurements — {activeSteep.label}
                                    </Typography>
                                    <Stack spacing={1}>
                                        <NumField label="Area" value={activeSteep.area} unit="sqft"
                                            onChange={v => patchSteepAt(activeSteepIdx, { area: v })}
                                            required={workType !== 'Repairs'} />
                                        <NumField label="Drip Edge" value={activeSteep.edge} unit="LF"
                                            onChange={v => patchSteepAt(activeSteepIdx, { edge: v })} />
                                        <NumField label="Ridge" value={activeSteep.ridge} unit="LF"
                                            onChange={v => patchSteepAt(activeSteepIdx, { ridge: v })} />
                                        <NumField label="Valley" value={activeSteep.valley} unit="LF"
                                            onChange={v => patchSteepAt(activeSteepIdx, { valley: v })} />
                                        <NumField label="Parapet/Wall" value={activeSteep.parapet} unit="LF"
                                            onChange={v => patchSteepAt(activeSteepIdx, { parapet: v })} />
                                        <Grid container spacing={1}>
                                            <Grid item xs={6}>
                                                <NumField label="Plumbing" value={activeSteep.plumbingProjections} unit="ea"
                                                    onChange={v => patchSteepAt(activeSteepIdx, { plumbingProjections: v })} />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <NumField label="Ventilation" value={activeSteep.ventilationProjections} unit="ea"
                                                    onChange={v => patchSteepAt(activeSteepIdx, { ventilationProjections: v })} />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <NumField label="Plywood" value={activeSteep.woodPlywoodSheets} unit="sheets"
                                                    onChange={v => patchSteepAt(activeSteepIdx, { woodPlywoodSheets: v })} />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <NumField label="2×4 Lumber" value={activeSteep.woodLumberPieces} unit="pcs"
                                                    onChange={v => patchSteepAt(activeSteepIdx, { woodLumberPieces: v })} />
                                            </Grid>
                                        </Grid>
                                    </Stack>
                                </Box>
                            )}

                            {/* Flat roof tabs + label */}
                            {showFlat && (
                                <Box>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>Flat Roof</Typography>
                                        <Button size="small" onClick={addFlatRoof} sx={{ textTransform: 'none', fontSize: 11 }}>
                                            + Add flat roof
                                        </Button>
                                    </Stack>
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1 }}>
                                        {flatRoofs.map((r, i) => (
                                            <Chip key={r.id}
                                                label={r.label}
                                                size="small"
                                                color={i === activeFlatIdx ? 'primary' : 'default'}
                                                onClick={() => setActiveFlatIdx(i)}
                                                onDelete={flatRoofs.length > 1 ? () => removeFlatRoof(i) : undefined}
                                            />
                                        ))}
                                    </Stack>
                                    <TextField label="Roof label" size="small" fullWidth sx={{ mb: 1 }}
                                        value={activeFlat.label}
                                        onChange={e => patchFlatAt(activeFlatIdx, { label: e.target.value })} />
                                </Box>
                            )}

                            {/* Flat substrate selector — shown first so system lock is visible immediately */}
                            {showFlat && (
                                <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'success.main' }}>
                                    <Typography variant="caption" color="success.dark" fontWeight={700} sx={{ display: 'block', mb: 1 }}>
                                        Deck Substrate
                                    </Typography>
                                    <Stack direction="row" spacing={1}>
                                        {FLAT_SUBSTRATE_OPTIONS.map(opt => (
                                            <Box
                                                key={opt.id}
                                                onClick={() => {
                                                    if (!opt.ready) return;
                                                    setFlatSubstrate(opt.id);
                                                    // TREMCO and HYDROSTOP are concrete-only — reset flat system if switching away
                                                    if (opt.id !== 'CONCRETE' && (flatSystemId === 'TREMCO' || flatSystemId === 'HYDROSTOP' || flatSystemId === 'WATERPROOF-SB' || flatSystemId === 'TPO')) {
                                                        setFlatSystemId('NONE-FLAT');
                                                    }
                                                }}
                                                sx={{
                                                    flex: 1, p: 1.25, borderRadius: 1, border: '1px solid',
                                                    borderColor: flatSubstrate === opt.id ? 'success.main' : 'divider',
                                                    bgcolor: !opt.ready ? 'action.disabledBackground'
                                                           : flatSubstrate === opt.id ? 'success.50' : 'background.paper',
                                                    cursor: opt.ready ? 'pointer' : 'default',
                                                    textAlign: 'center',
                                                    '&:hover': opt.ready ? { borderColor: 'success.light', bgcolor: 'action.hover' } : {},
                                                    transition: 'all 0.15s',
                                                }}
                                            >
                                                <Typography variant="body2"
                                                    fontWeight={flatSubstrate === opt.id ? 700 : 400}
                                                    color={opt.ready ? 'text.primary' : 'text.disabled'}>
                                                    {opt.label}
                                                </Typography>
                                                {!opt.ready && (
                                                    <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                                                        Soon
                                                    </Typography>
                                                )}
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            )}

                            {showFlat && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Low-Slope / Flat</Typography>
                                    <Box sx={{ mt: 1 }}>
                                        <SystemPicker
                                            options={FLAT_MEMBRANE_SYSTEMS}
                                            value={FLAT_MEMBRANE_SYSTEMS.some(s => s.id === flatSystemId) ? flatSystemId : ''}
                                            onChange={id => { setFlatSystemId(id); }}
                                            lockedIds={flatSubstrate !== 'CONCRETE'
                                                ? { TPO: 'Concrete deck only' }
                                                : {}}
                                            onLockedClick={() => {}}
                                        />
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mt: 1.5 }}>
                                        Low-Slope / Waterproofing
                                    </Typography>
                                    <Box sx={{ mt: 1 }}>
                                        <SystemPicker
                                            options={FLAT_WATERPROOFING_SYSTEMS}
                                            value={FLAT_WATERPROOFING_SYSTEMS.some(s => s.id === flatSystemId) ? flatSystemId : ''}
                                            onChange={id => { setFlatSystemId(id); }}
                                            lockedIds={flatSubstrate !== 'CONCRETE'
                                                ? {
                                                    TREMCO: 'Concrete deck only',
                                                    HYDROSTOP: 'Concrete deck only',
                                                    'WATERPROOF-SB': 'Concrete deck only',
                                                }
                                                : {}}
                                            onLockedClick={() => {}}
                                        />
                                    </Box>
                                </Box>
                            )}

                            {/* BUILDUP — 4.1 Insulation (NOA System 1 vs 2) */}
                            {showFlat && flatSystemId === 'BUILDUP' && (
                                <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'info.main' }}>
                                    <Typography variant="caption" color="info.dark" fontWeight={700} sx={{ display: 'block', mb: 1 }}>
                                        Insulation Layer
                                    </Typography>
                                    <Stack spacing={0.75}>
                                        {BUILDUP_INS_OPTIONS.map(opt => (
                                            <Box key={opt.id || 'none'}
                                                onClick={() => { setBuiltupInsulationId(opt.id); }}
                                                sx={{
                                                    p: 1.25, borderRadius: 1, border: '1px solid',
                                                    borderColor: builtupInsulationId === opt.id ? 'info.main' : 'divider',
                                                    bgcolor: builtupInsulationId === opt.id ? 'info.50' : 'background.paper',
                                                    cursor: 'pointer',
                                                    '&:hover': { borderColor: 'info.light', bgcolor: 'action.hover' },
                                                    transition: 'all 0.15s',
                                                }}
                                            >
                                                <Stack direction="row" alignItems="flex-start" spacing={1}>
                                                    <Box sx={{
                                                        width: 14, height: 14, borderRadius: '50%', mt: 0.25,
                                                        border: '2px solid',
                                                        borderColor: builtupInsulationId === opt.id ? 'info.main' : 'text.disabled',
                                                        bgcolor: builtupInsulationId === opt.id ? 'info.main' : 'transparent',
                                                        flexShrink: 0,
                                                    }} />
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={builtupInsulationId === opt.id ? 700 : 400}>
                                                            {opt.label}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {opt.id === ''
                                                                ? flatSubstrate === 'CONCRETE'
                                                                    ? 'Non-insulated — NOA System 1. Matrix 307 → inter-ply → cap.'
                                                                    : 'Non-insulated — NOA System 1. #75 → inter-ply → cap.'
                                                                : opt.description}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            )}

                            {/* BUILDUP — 4.1b Tapered Insulation */}
                            {showFlat && flatSystemId === 'BUILDUP' && (
                                <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'info.main' }}>
                                    <Typography variant="caption" color="info.dark" fontWeight={700} sx={{ display: 'block', mb: 1 }}>
                                        Tapered Insulation
                                    </Typography>
                                    <Stack spacing={0.75}>
                                        {[
                                            { id: '',          label: 'None',                    slope: 0,    description: 'No tapered layer — flat base Polyiso only (if selected above).' },
                                            ...TPO_INS_OPTIONS.filter(o => o.id !== 'NONE'),
                                        ].map(opt => {
                                            const isSelected = builtupTaperedInsId === opt.id;
                                            const autoTapered = opt.id === '' ? 0 : Math.ceil(inputs.flat_area / 16 * (1 + opt.slope));
                                            const autoFlat    = builtupFlatIsoAutoQty;
                                            const qtyHint = opt.id === ''
                                                ? null
                                                : `${autoTapered} tapered + ${autoFlat} flat base boards`;
                                            return (
                                                <Box key={opt.id || 'none'}
                                                    onClick={() => { setBuiltupTaperedInsId(opt.id); setBuiltupTaperedIsoQty(null); }}
                                                    sx={{
                                                        p: 1.25, borderRadius: 1, border: '1px solid',
                                                        borderColor: isSelected ? 'info.main' : 'divider',
                                                        bgcolor: isSelected ? 'info.50' : 'background.paper',
                                                        cursor: 'pointer',
                                                        '&:hover': { borderColor: 'info.light', bgcolor: 'action.hover' },
                                                        transition: 'all 0.15s',
                                                    }}
                                                >
                                                    <Stack direction="row" alignItems="flex-start" spacing={1}>
                                                        <Box sx={{
                                                            width: 14, height: 14, borderRadius: '50%', mt: 0.25,
                                                            border: '2px solid',
                                                            borderColor: isSelected ? 'info.main' : 'text.disabled',
                                                            bgcolor: isSelected ? 'info.main' : 'transparent',
                                                            flexShrink: 0,
                                                        }} />
                                                        <Box>
                                                            <Typography variant="body2" fontWeight={isSelected ? 700 : 400}>
                                                                {opt.label}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {opt.description}
                                                            </Typography>
                                                            {qtyHint && inputs.flat_area > 0 && (
                                                                <Typography variant="caption" color="info.dark" sx={{ display: 'block', fontWeight: 600 }}>
                                                                    Auto-qty: {qtyHint}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Stack>
                                                </Box>
                                            );
                                        })}
                                    </Stack>

                                    {/* Override fields — only shown when tapered is selected */}
                                    {builtupTaperedInsId && (
                                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <NumField
                                                    label={`Tapered ISO (auto: ${builtupTaperedIsoAutoQty})`}
                                                    value={resolvedBuiltupTaperedIsoQty}
                                                    unit="bds"
                                                    onChange={v => { setBuiltupTaperedIsoQty(v === builtupTaperedIsoAutoQty ? null : v); }}
                                                />
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <NumField
                                                    label={`Flat Base ISO (auto: ${builtupFlatIsoAutoQty})`}
                                                    value={resolvedBuiltupFlatIsoQty}
                                                    unit="bds"
                                                    onChange={v => { setBuiltupFlatIsoQty(v === builtupFlatIsoAutoQty ? null : v); }}
                                                />
                                            </Box>
                                        </Stack>
                                    )}
                                </Box>
                            )}

                            {/* BUILDUP — 4.2 Inter-Ply Membranes */}
                            {showFlat && flatSystemId === 'BUILDUP' && (
                                <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'warning.main' }}>
                                    <Typography variant="caption" color="warning.dark" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
                                        Inter-Ply Membranes
                                    </Typography>
                                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1 }}>
                                        Set quantity of each membrane type. Total plies ≥ 1.
                                    </Typography>
                                    <Stack spacing={1}>
                                        {[
                                            { opt: BUILDUP_INTERPLY_OPTIONS[0], qty: builtupR20Qty,    setQty: setBuiltupR20Qty },
                                            { opt: BUILDUP_INTERPLY_OPTIONS[1], qty: builtupSmoothQty, setQty: setBuiltupSmoothQty },
                                            { opt: BUILDUP_INTERPLY_OPTIONS[2], qty: builtupSm15Qty,   setQty: setBuiltupSm15Qty },
                                        ].map(({ opt, qty, setQty }) => (
                                            <Box key={opt.id} sx={{
                                                p: 1.25, borderRadius: 1, border: '1px solid',
                                                borderColor: qty > 0 ? 'warning.main' : 'divider',
                                                bgcolor: qty > 0 ? 'warning.50' : 'background.paper',
                                            }}>
                                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                                    <Box sx={{ flex: 1, mr: 1 }}>
                                                        <Typography variant="body2" fontWeight={qty > 0 ? 700 : 400}>{opt.label}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{opt.description}</Typography>
                                                    </Box>
                                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                                        <Box onClick={() => { setQty(Math.max(0, qty - 1)); }}
                                                            sx={{
                                                                width: 26, height: 26, borderRadius: 1,
                                                                border: '1px solid', borderColor: 'divider',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                cursor: qty > 0 ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: 16,
                                                                color: qty > 0 ? 'text.primary' : 'text.disabled',
                                                                '&:hover': qty > 0 ? { bgcolor: 'action.hover' } : {},
                                                            }}>−</Box>
                                                        <Typography variant="body2" fontWeight={700} sx={{ minWidth: 18, textAlign: 'center' }}>
                                                            {qty}
                                                        </Typography>
                                                        <Box onClick={() => { setQty(qty + 1); }}
                                                            sx={{
                                                                width: 26, height: 26, borderRadius: 1,
                                                                border: '1px solid', borderColor: 'divider',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                cursor: 'pointer', fontWeight: 700, fontSize: 16,
                                                                '&:hover': { bgcolor: 'action.hover' },
                                                            }}>+</Box>
                                                    </Stack>
                                                </Stack>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            )}

                            {/* BUILDUP — 4.3 Cap Sheet */}
                            {showFlat && flatSystemId === 'BUILDUP' && (
                                <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                                    <Typography variant="caption" color="primary.main" fontWeight={700} sx={{ display: 'block', mb: 1 }}>
                                        Cap Sheet
                                    </Typography>
                                    <Stack spacing={0.75}>
                                        {BUILDUP_CAP_OPTIONS.map(opt => (
                                            <Box key={opt.id}
                                                onClick={() => { setBuiltupCapId(opt.id); }}
                                                sx={{
                                                    p: 1.25, borderRadius: 1, border: '1px solid',
                                                    borderColor: builtupCapId === opt.id ? 'primary.main' : 'divider',
                                                    bgcolor: builtupCapId === opt.id ? 'primary.50' : 'background.paper',
                                                    cursor: 'pointer',
                                                    '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
                                                    transition: 'all 0.15s',
                                                }}
                                            >
                                                <Stack direction="row" alignItems="flex-start" spacing={1}>
                                                    <Box sx={{
                                                        width: 14, height: 14, borderRadius: '50%', mt: 0.25,
                                                        border: '2px solid',
                                                        borderColor: builtupCapId === opt.id ? 'primary.main' : 'text.disabled',
                                                        bgcolor: builtupCapId === opt.id ? 'primary.main' : 'transparent',
                                                        flexShrink: 0,
                                                    }} />
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={builtupCapId === opt.id ? 700 : 400}>
                                                            {opt.label}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {opt.description}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            )}

                            {/* BUILDUP — hint when no edge/parapet LF entered yet */}
                            {showFlat && flatSystemId === 'BUILDUP' && inputs.flat_edge === 0 && inputs.flat_parapet === 0 && (
                                <Box sx={{
                                    pl: 1, borderLeft: '3px solid', borderColor: 'divider',
                                    py: 1, opacity: 0.6,
                                }}>
                                    <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                                        Enter Flat Edge LF and/or Flat Parapet LF in measurements (Step 4) to configure termination options.
                                    </Typography>
                                </Box>
                            )}

                            {/* BUILDUP — 4.4 Drip Edge (only when flat_edge > 0) */}
                            {showFlat && flatSystemId === 'BUILDUP' && inputs.flat_edge > 0 && (
                                <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'success.main' }}>
                                    <Typography variant="caption" color="success.dark" fontWeight={700} sx={{ display: 'block', mb: 1.5 }}>
                                        Drip Edge — {inputs.flat_edge} LF
                                    </Typography>
                                    <Stack spacing={2}>
                                        {/* Metal type */}
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.75 }}>
                                                Metal Type
                                            </Typography>
                                            <Stack spacing={0.75}>
                                                {BUILDUP_EDGE_METALS.map(opt => (
                                                    <Box key={opt.id}
                                                        onClick={() => { setBuiltupEdgeMetal(opt.id); }}
                                                        sx={{
                                                            p: 1.25, borderRadius: 1, border: '1px solid',
                                                            borderColor: builtupEdgeMetal === opt.id ? 'success.main' : 'divider',
                                                            bgcolor: builtupEdgeMetal === opt.id ? 'success.50' : 'background.paper',
                                                            cursor: 'pointer',
                                                            '&:hover': { borderColor: 'success.light', bgcolor: 'action.hover' },
                                                            transition: 'all 0.15s',
                                                        }}
                                                    >
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <Box sx={{
                                                                width: 14, height: 14, borderRadius: '50%',
                                                                border: '2px solid',
                                                                borderColor: builtupEdgeMetal === opt.id ? 'success.main' : 'text.disabled',
                                                                bgcolor: builtupEdgeMetal === opt.id ? 'success.main' : 'transparent',
                                                                flexShrink: 0,
                                                            }} />
                                                            <Box>
                                                                <Typography variant="body2" fontWeight={builtupEdgeMetal === opt.id ? 700 : 400}>
                                                                    {opt.label}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {opt.description}
                                                                </Typography>
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Box>
                                        {/* Edge size */}
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.75 }}>
                                                Profile Size
                                            </Typography>
                                            <Stack direction="row" spacing={1}>
                                                {BUILDUP_EDGE_SIZES.map(opt => (
                                                    <Box key={opt.id}
                                                        onClick={() => { setBuiltupEdgeSize(opt.id); }}
                                                        sx={{
                                                            flex: 1, p: 1.25, borderRadius: 1, border: '1px solid',
                                                            borderColor: builtupEdgeSize === opt.id ? 'success.main' : 'divider',
                                                            bgcolor: builtupEdgeSize === opt.id ? 'success.50' : 'background.paper',
                                                            cursor: 'pointer', textAlign: 'center',
                                                            '&:hover': { borderColor: 'success.light', bgcolor: 'action.hover' },
                                                            transition: 'all 0.15s',
                                                        }}
                                                    >
                                                        <Typography variant="body2" fontWeight={builtupEdgeSize === opt.id ? 700 : 400}>
                                                            {opt.label}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {opt.description}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Box>
                                    </Stack>
                                </Box>
                            )}

                            {/* ── POLYGLASS — 4.A Inter-Ply Quantity ─────────────────────────────── */}
                            {showFlat && flatSystemId === 'POLYGLASS' && (
                                <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'warning.main' }}>
                                    <Typography variant="caption" color="warning.dark" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
                                        Inter-Ply Membranes
                                    </Typography>
                                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1 }}>
                                        Polyglass Elastoflex SA — 150 SQFT/roll. Select number of plies.
                                    </Typography>
                                    <Box sx={{
                                        p: 1.25, borderRadius: 1, border: '1px solid',
                                        borderColor: 'warning.main', bgcolor: 'warning.50',
                                    }}>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                                            <Box>
                                                <Typography variant="body2" fontWeight={700}>Polyglass Elastoflex</Typography>
                                                <Typography variant="caption" color="text.secondary">SBS-modified SA inter-ply — 150 SQFT/roll, $120.00/roll</Typography>
                                            </Box>
                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                <Box onClick={() => { setPolyglassIpQty(Math.max(0, polyglassIpQty - 1)); }}
                                                    sx={{
                                                        width: 26, height: 26, borderRadius: 1,
                                                        border: '1px solid', borderColor: 'divider',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        cursor: polyglassIpQty > 0 ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: 16,
                                                        color: polyglassIpQty > 0 ? 'text.primary' : 'text.disabled',
                                                        '&:hover': polyglassIpQty > 0 ? { bgcolor: 'action.hover' } : {},
                                                    }}>−</Box>
                                                <Typography variant="body2" fontWeight={700} sx={{ minWidth: 18, textAlign: 'center' }}>
                                                    {polyglassIpQty}
                                                </Typography>
                                                <Box onClick={() => { setPolyglassIpQty(polyglassIpQty + 1); }}
                                                    sx={{
                                                        width: 26, height: 26, borderRadius: 1,
                                                        border: '1px solid', borderColor: 'divider',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        cursor: 'pointer', fontWeight: 700, fontSize: 16,
                                                        '&:hover': { bgcolor: 'action.hover' },
                                                    }}>+</Box>
                                            </Stack>
                                        </Stack>
                                    </Box>
                                    <Box sx={{ mt: 1, p: 1.25, borderRadius: 1, border: '1px solid', borderColor: 'primary.main', bgcolor: 'primary.50' }}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Box sx={{
                                                width: 14, height: 14, borderRadius: '50%',
                                                border: '2px solid', borderColor: 'primary.main',
                                                bgcolor: 'primary.main', flexShrink: 0,
                                            }} />
                                            <Box>
                                                <Typography variant="body2" fontWeight={700}>Polyglass Polyflex (Cap Sheet)</Typography>
                                                <Typography variant="caption" color="text.secondary">SBS-modified SA granulated cap — 100 SQFT/roll, $89.00/roll. Fixed.</Typography>
                                            </Box>
                                        </Stack>
                                    </Box>
                                </Box>
                            )}

                            {/* POLYGLASS — hint when no edge LF entered yet */}
                            {showFlat && flatSystemId === 'POLYGLASS' && inputs.flat_edge === 0 && inputs.flat_parapet === 0 && (
                                <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'divider', py: 1, opacity: 0.6 }}>
                                    <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                                        Enter Flat Edge LF and/or Flat Parapet LF in measurements (Step 4) to configure termination options.
                                    </Typography>
                                </Box>
                            )}

                            {/* POLYGLASS — 4.B Drip Edge (only when flat_edge > 0) */}
                            {showFlat && flatSystemId === 'POLYGLASS' && inputs.flat_edge > 0 && (
                                <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'success.main' }}>
                                    <Typography variant="caption" color="success.dark" fontWeight={700} sx={{ display: 'block', mb: 1.5 }}>
                                        Drip Edge — {inputs.flat_edge} LF
                                    </Typography>
                                    <Stack spacing={2}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.75 }}>
                                                Metal Type
                                            </Typography>
                                            <Stack spacing={0.75}>
                                                {BUILDUP_EDGE_METALS.map(opt => (
                                                    <Box key={opt.id}
                                                        onClick={() => { setPolyglassEdgeMetal(opt.id); }}
                                                        sx={{
                                                            p: 1.25, borderRadius: 1, border: '1px solid',
                                                            borderColor: polyglassEdgeMetal === opt.id ? 'success.main' : 'divider',
                                                            bgcolor: polyglassEdgeMetal === opt.id ? 'success.50' : 'background.paper',
                                                            cursor: 'pointer',
                                                            '&:hover': { borderColor: 'success.light', bgcolor: 'action.hover' },
                                                            transition: 'all 0.15s',
                                                        }}
                                                    >
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <Box sx={{
                                                                width: 14, height: 14, borderRadius: '50%',
                                                                border: '2px solid',
                                                                borderColor: polyglassEdgeMetal === opt.id ? 'success.main' : 'text.disabled',
                                                                bgcolor: polyglassEdgeMetal === opt.id ? 'success.main' : 'transparent',
                                                                flexShrink: 0,
                                                            }} />
                                                            <Box>
                                                                <Typography variant="body2" fontWeight={polyglassEdgeMetal === opt.id ? 700 : 400}>{opt.label}</Typography>
                                                                <Typography variant="caption" color="text.secondary">{opt.description}</Typography>
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.75 }}>
                                                Profile Size
                                            </Typography>
                                            <Stack direction="row" spacing={1}>
                                                {BUILDUP_EDGE_SIZES.map(opt => (
                                                    <Box key={opt.id}
                                                        onClick={() => { setPolyglassEdgeSize(opt.id); }}
                                                        sx={{
                                                            flex: 1, p: 1.25, borderRadius: 1, border: '1px solid',
                                                            borderColor: polyglassEdgeSize === opt.id ? 'success.main' : 'divider',
                                                            bgcolor: polyglassEdgeSize === opt.id ? 'success.50' : 'background.paper',
                                                            cursor: 'pointer', textAlign: 'center',
                                                            '&:hover': { borderColor: 'success.light', bgcolor: 'action.hover' },
                                                            transition: 'all 0.15s',
                                                        }}
                                                    >
                                                        <Typography variant="body2" fontWeight={polyglassEdgeSize === opt.id ? 700 : 400}>{opt.label}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{opt.description}</Typography>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Box>
                                    </Stack>
                                </Box>
                            )}

                            {/* ── TREMCO — System Overview ───────────────── */}
                            {showFlat && flatSystemId === 'TREMCO' && (
                                <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'info.main' }}>
                                    <Typography variant="caption" color="info.dark" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
                                        System Overview
                                    </Typography>
                                    <Stack spacing={0.5}>
                                        {[
                                            { label: 'Tremco 171 Primer', detail: '$160/bucket — 1,200 sqft coverage' },
                                            { label: 'Tremco 350 Base Coat', detail: '$100/bucket — 250 sqft coverage' },
                                            { label: 'Tremco 351 Finish Coat', detail: '$220/bucket — 220 sqft coverage' },
                                            { label: 'Pressure Cleaning', detail: '$0.20/sqft (min $500)' },
                                            { label: 'Waterproofing Labor', detail: '$2.00/sqft' },
                                        ].map(item => (
                                            <Stack key={item.label} direction="row" spacing={1} alignItems="flex-start">
                                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'info.main', mt: 0.75, flexShrink: 0 }} />
                                                <Box>
                                                    <Typography variant="caption" fontWeight={600}>{item.label}</Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{item.detail}</Typography>
                                                </Box>
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Box>
                            )}

                            {/* ── HYDROSTOP — System Overview ────────────── */}
                            {showFlat && flatSystemId === 'HYDROSTOP' && (
                                <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'secondary.main' }}>
                                    <Typography variant="caption" color="secondary.dark" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
                                        System Overview
                                    </Typography>
                                    <Stack spacing={0.5}>
                                        {[
                                            { label: 'GAF Hydrostop Primer',       detail: '$160/bucket — 2,000 sqft' },
                                            { label: 'Multipurpose Primer',        detail: '$100/bucket — 2,000 sqft' },
                                            { label: 'Barrier Guard',              detail: '$220/bucket — 799 sqft' },
                                            { label: 'Portland Cement',            detail: '$15/bag — 1 bag per 10 LF parapet' },
                                            { label: 'Sure Bond',                  detail: '$210/unit — 1,000 sqft' },
                                            { label: 'Base Coat × 2',             detail: '$200/bucket — 400 sqft each coat' },
                                            { label: 'Premium Coat Fabric',        detail: '$168/roll — 900 sqft' },
                                            { label: 'Topcoat × 3',               detail: '$200/bucket — 400 sqft each coat' },
                                            { label: 'Elastomeric Sealant + Silicone + Accessories', detail: 'Flat fees per job' },
                                            { label: 'Pressure Cleaning',          detail: '$0.20/sqft (min $500)' },
                                            { label: 'Scaraface Labor',            detail: '$0.80/sqft (required)' },
                                            { label: 'Waterproofing Labor',        detail: '$1.20/sqft' },
                                        ].map(item => (
                                            <Stack key={item.label} direction="row" spacing={1} alignItems="flex-start">
                                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'secondary.main', mt: 0.75, flexShrink: 0 }} />
                                                <Box>
                                                    <Typography variant="caption" fontWeight={600}>{item.label}</Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{item.detail}</Typography>
                                                </Box>
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Box>
                            )}

                            {/* WP SB ──────────────────────────────────────────────────── */}
                            {showFlat && flatSystemId === 'WATERPROOF-SB' && (
                                <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'teal' }}>
                                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 700, color: 'teal' }}>
                                        Waterproofing Surface Seal SB — Concrete Only
                                    </Typography>

                                    {/* Warranty term */}
                                    <Box sx={{ mb: 1.5 }}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={700}
                                            sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Warranty Term
                                        </Typography>
                                        <Stack direction="row" spacing={1}>
                                            {([
                                                { id: '10', label: '10 Year', desc: 'Surface Seal @ 160 SF/unit · WP labor $1.20/sqft' },
                                                { id: '20', label: '20 Year', desc: 'Surface Seal @ 100 SF/unit · WP labor $2.00/sqft' },
                                            ] as const).map(opt => (
                                                <Box key={opt.id}
                                                    onClick={() => { setWpSbWarranty(opt.id); }}
                                                    sx={{
                                                        flex: 1, p: 1.25, borderRadius: 1, border: '1px solid',
                                                        borderColor: wpSbWarranty === opt.id ? 'teal' : 'divider',
                                                        bgcolor: wpSbWarranty === opt.id ? 'rgba(0, 128, 128, 0.08)' : 'background.paper',
                                                        cursor: 'pointer',
                                                        '&:hover': { borderColor: 'teal.light', bgcolor: 'action.hover' },
                                                        transition: 'all 0.15s',
                                                    }}
                                                >
                                                    <Typography variant="body2" fontWeight={wpSbWarranty === opt.id ? 700 : 400}>
                                                        {opt.label}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {opt.desc}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Box>

                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                                        Primer · GAF Mesh · Flexseal · Pressure Cleaning · Scaraface $0.80/sqft
                                    </Typography>
                                </Box>
                            )}

                            {/* GAF TPO ───────────────────────────────────────────────────── */}
                            {showFlat && flatSystemId === 'TPO' && (
                                <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'primary.dark' }}>
                                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 700, color: 'primary.dark' }}>
                                        GAF EverGuard TPO — Concrete Deck Only
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontStyle: 'italic' }}>
                                        Labor: HM No Cover $1.90 · HM W/Cover $2.10 · OB No Cover $2.00 · OB W/Cover $2.40
                                    </Typography>

                                    {/* Q1: Method of Attachment */}
                                    <Box sx={{ mb: 1.5 }}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={700}
                                            sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            1. Method of Attachment
                                        </Typography>
                                        <Stack direction="row" spacing={1}>
                                            {([
                                                { id: 'HM', label: 'Hot-Mop' },
                                                { id: 'OB', label: 'Adhered (Olybond)' },
                                            ] as const).map(opt => (
                                                <Box key={opt.id}
                                                    onClick={() => { setTpoAttachment(opt.id); }}
                                                    sx={{
                                                        flex: 1, textAlign: 'center', px: 1.5, py: 0.75,
                                                        borderRadius: 1.5, border: 2, cursor: 'pointer',
                                                        borderColor: tpoAttachment === opt.id ? 'primary.main' : 'divider',
                                                        bgcolor: tpoAttachment === opt.id ? 'primary.main' : 'background.paper',
                                                        color: tpoAttachment === opt.id ? 'white' : 'text.primary',
                                                        transition: 'all 0.12s',
                                                    }}>
                                                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13 }}>{opt.label}</Typography>
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Box>

                                    {/* Q2: Coverboard */}
                                    <Box sx={{ mb: 1.5 }}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={700}
                                            sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            2. Coverboard
                                        </Typography>
                                        <Stack direction="row" spacing={1}>
                                            {([
                                                { val: false, label: 'No Coverboard' },
                                                { val: true,  label: 'With Coverboard' },
                                            ] as const).map(opt => (
                                                <Box key={String(opt.val)}
                                                    onClick={() => { setTpoCoverboard(opt.val); }}
                                                    sx={{
                                                        flex: 1, textAlign: 'center', px: 1.5, py: 0.75,
                                                        borderRadius: 1.5, border: 2, cursor: 'pointer',
                                                        borderColor: tpoCoverboard === opt.val ? 'primary.main' : 'divider',
                                                        bgcolor: tpoCoverboard === opt.val ? 'primary.main' : 'background.paper',
                                                        color: tpoCoverboard === opt.val ? 'white' : 'text.primary',
                                                        transition: 'all 0.12s',
                                                    }}>
                                                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13 }}>{opt.label}</Typography>
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Box>

                                    {/* Q3a: Flat Base Polyiso thickness (mirrors BUILDUP) */}
                                    <Box sx={{ mb: 1.5 }}>
                                        <Typography variant="caption" color="primary.dark" fontWeight={700}
                                            sx={{ display: 'block', mb: 0.75 }}>
                                            Flat Base Polyiso
                                        </Typography>
                                        <Stack spacing={0.75}>
                                            {BUILDUP_INS_OPTIONS.map(opt => {
                                                const isSelected = tpoFlatBaseInsId === opt.id;
                                                const autoQty    = Math.ceil(inputs.flat_area / 16);
                                                return (
                                                    <Box key={opt.id || 'none'}
                                                        onClick={() => { setTpoFlatBaseInsId(opt.id); }}
                                                        sx={{
                                                            p: 1.25, borderRadius: 1, border: '1px solid',
                                                            borderColor: isSelected ? 'primary.main' : 'divider',
                                                            bgcolor: isSelected ? 'primary.50' : 'background.paper',
                                                            cursor: 'pointer',
                                                            '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
                                                            transition: 'all 0.15s',
                                                        }}
                                                    >
                                                        <Stack direction="row" alignItems="flex-start" spacing={1}>
                                                            <Box sx={{
                                                                width: 14, height: 14, borderRadius: '50%', mt: 0.25,
                                                                border: '2px solid',
                                                                borderColor: isSelected ? 'primary.main' : 'text.disabled',
                                                                bgcolor: isSelected ? 'primary.main' : 'transparent',
                                                                flexShrink: 0,
                                                            }} />
                                                            <Box>
                                                                <Typography variant="body2" fontWeight={isSelected ? 700 : 400}>
                                                                    {opt.label}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {opt.id === ''
                                                                        ? 'No flat base Polyiso layer.'
                                                                        : opt.description}
                                                                </Typography>
                                                                {opt.id !== '' && inputs.flat_area > 0 && (
                                                                    <Typography variant="caption" color="primary.dark" sx={{ display: 'block', fontWeight: 600 }}>
                                                                        Auto-qty: {autoQty} boards
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    </Box>

                                    {/* Q3b: Tapered Insulation (card selector — mirrors BUILDUP) */}
                                    <Box sx={{ mb: 1.5 }}>
                                        <Typography variant="caption" color="primary.dark" fontWeight={700}
                                            sx={{ display: 'block', mb: 0.75 }}>
                                            Insulation Layer
                                        </Typography>
                                        <Stack spacing={0.75}>
                                            {TPO_INS_OPTIONS.map(opt => {
                                                const autoTapered = opt.id === 'NONE' ? 0 : Math.ceil(inputs.flat_area / 32 * (1 + opt.slope));
                                                const autoFlat    = tpoFlatIsoAutoQty;
                                                const isSelected  = tpoInsulationId === opt.id;
                                                const qtyHint = opt.id === 'NONE'
                                                    ? `${autoFlat} flat base boards`
                                                    : `${autoTapered} tapered + ${autoFlat} flat base boards`;
                                                return (
                                                    <Box key={opt.id}
                                                        onClick={() => { setTpoInsulationId(opt.id); setTpoTaperedIsoQty(null); }}
                                                        sx={{
                                                            p: 1.25, borderRadius: 1, border: '1px solid',
                                                            borderColor: isSelected ? 'primary.main' : 'divider',
                                                            bgcolor: isSelected ? 'primary.50' : 'background.paper',
                                                            cursor: 'pointer',
                                                            '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
                                                            transition: 'all 0.15s',
                                                        }}
                                                    >
                                                        <Stack direction="row" alignItems="flex-start" spacing={1}>
                                                            <Box sx={{
                                                                width: 14, height: 14, borderRadius: '50%', mt: 0.25,
                                                                border: '2px solid',
                                                                borderColor: isSelected ? 'primary.main' : 'text.disabled',
                                                                bgcolor: isSelected ? 'primary.main' : 'transparent',
                                                                flexShrink: 0,
                                                            }} />
                                                            <Box>
                                                                <Typography variant="body2" fontWeight={isSelected ? 700 : 400}>
                                                                    {opt.label}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {opt.description}
                                                                </Typography>
                                                                {inputs.flat_area > 0 && (
                                                                    <Typography variant="caption" color="primary.dark" sx={{ display: 'block', fontWeight: 600 }}>
                                                                        Auto-qty: {qtyHint}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                );
                                            })}
                                        </Stack>

                                        {/* Override fields */}
                                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                            {tpoInsulationId !== 'NONE' && (
                                                <Box sx={{ flex: 1 }}>
                                                    <NumField
                                                        label={`Tapered ISO (auto: ${tpoTaperedIsoAutoQty})`}
                                                        value={resolvedTpoTaperedIsoQty}
                                                        unit="bds"
                                                        onChange={v => { setTpoTaperedIsoQty(v === tpoTaperedIsoAutoQty ? null : v); }}
                                                    />
                                                </Box>
                                            )}
                                            <Box sx={{ flex: 1 }}>
                                                <NumField
                                                    label={`Flat Base ISO (auto: ${tpoFlatIsoAutoQty})`}
                                                    value={resolvedTpoFlatIsoQty}
                                                    unit="bds"
                                                    onChange={v => { setTpoFlatIsoQty(v === tpoFlatIsoAutoQty ? null : v); }}
                                                />
                                            </Box>
                                        </Stack>
                                        {inputs.flat_area === 0 && (
                                            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                                                Enter flat area above to see auto-calculated board counts.
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Round penetrations — uses Plumbing count from Step 4 */}
                                    <Box sx={{ p: 1.25, borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={700}
                                            sx={{ display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Round Penetrations (Pipe Boots)
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                            Same as Plumbing in Measurements (Step 4) — each round pipe gets a GAF TPO Vent Boot and Water Block Mastic.
                                        </Typography>
                                        <Typography variant="body2" fontWeight={700} color="primary.dark">
                                            {inputs.plumbing_projections} ea
                                            {inputs.plumbing_projections === 0 && (
                                                <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 1, fontWeight: 400 }}>
                                                    — enter count under Plumbing below
                                                </Typography>
                                            )}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {/* TPO — Drip Edge (only when flat_edge > 0) */}
                            {showFlat && flatSystemId === 'TPO' && inputs.flat_edge > 0 && (
                                <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'success.main' }}>
                                    <Typography variant="caption" color="success.dark" fontWeight={700} sx={{ display: 'block', mb: 1.5 }}>
                                        Drip Edge — {inputs.flat_edge} LF
                                    </Typography>
                                    <Stack spacing={2}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.75 }}>
                                                Metal Type
                                            </Typography>
                                            <Stack spacing={0.75}>
                                                {BUILDUP_EDGE_METALS.map(opt => (
                                                    <Box key={opt.id}
                                                        onClick={() => { setTpoEdgeMetal(opt.id); }}
                                                        sx={{
                                                            p: 1.25, borderRadius: 1, border: '1px solid',
                                                            borderColor: tpoEdgeMetal === opt.id ? 'success.main' : 'divider',
                                                            bgcolor: tpoEdgeMetal === opt.id ? 'success.50' : 'background.paper',
                                                            cursor: 'pointer',
                                                            '&:hover': { borderColor: 'success.light', bgcolor: 'action.hover' },
                                                            transition: 'all 0.15s',
                                                        }}
                                                    >
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <Box sx={{
                                                                width: 14, height: 14, borderRadius: '50%',
                                                                border: '2px solid',
                                                                borderColor: tpoEdgeMetal === opt.id ? 'success.main' : 'text.disabled',
                                                                bgcolor: tpoEdgeMetal === opt.id ? 'success.main' : 'transparent',
                                                                flexShrink: 0,
                                                            }} />
                                                            <Box>
                                                                <Typography variant="body2" fontWeight={tpoEdgeMetal === opt.id ? 700 : 400}>{opt.label}</Typography>
                                                                <Typography variant="caption" color="text.secondary">{opt.description}</Typography>
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.75 }}>
                                                Profile Size
                                            </Typography>
                                            <Stack direction="row" spacing={1}>
                                                {BUILDUP_EDGE_SIZES.map(opt => (
                                                    <Box key={opt.id}
                                                        onClick={() => { setTpoEdgeSize(opt.id); }}
                                                        sx={{
                                                            flex: 1, p: 1.25, borderRadius: 1, border: '1px solid',
                                                            borderColor: tpoEdgeSize === opt.id ? 'success.main' : 'divider',
                                                            bgcolor: tpoEdgeSize === opt.id ? 'success.50' : 'background.paper',
                                                            cursor: 'pointer', textAlign: 'center',
                                                            '&:hover': { borderColor: 'success.light', bgcolor: 'action.hover' },
                                                            transition: 'all 0.15s',
                                                        }}
                                                    >
                                                        <Typography variant="body2" fontWeight={tpoEdgeSize === opt.id ? 700 : 400}>{opt.label}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{opt.description}</Typography>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Box>
                                    </Stack>
                                </Box>
                            )}

                            {/* TPO — Parapet Termination (only when flat_parapet > 0) */}
                            {showFlat && flatSystemId === 'TPO' && inputs.flat_parapet > 0 && (
                                <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'warning.main' }}>
                                    <Typography variant="caption" color="warning.dark" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
                                        Parapet Termination — {inputs.flat_parapet} LF
                                    </Typography>
                                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1.25, fontStyle: 'italic' }}>
                                        L-Flashing + Counter Flashing always included.
                                    </Typography>
                                    <Stack spacing={1.5}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.75 }}>
                                                Flashing Metal
                                            </Typography>
                                            <Stack direction="row" spacing={1}>
                                                {BUILDUP_PARAPET_METALS.map(opt => (
                                                    <Box key={opt.id}
                                                        onClick={() => { setTpoParapetMetal(opt.id); }}
                                                        sx={{
                                                            flex: 1, p: 1.25, borderRadius: 1, border: '1px solid',
                                                            textAlign: 'center',
                                                            borderColor: tpoParapetMetal === opt.id ? 'warning.main' : 'divider',
                                                            bgcolor: tpoParapetMetal === opt.id ? 'warning.50' : 'background.paper',
                                                            cursor: 'pointer',
                                                            '&:hover': { borderColor: 'warning.light', bgcolor: 'action.hover' },
                                                            transition: 'all 0.15s',
                                                        }}
                                                    >
                                                        <Typography variant="body2" fontWeight={tpoParapetMetal === opt.id ? 700 : 400}>{opt.label}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{opt.description}</Typography>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.75 }}>
                                                Coping Cap
                                            </Typography>
                                            <Stack direction="row" spacing={1}>
                                                {[
                                                    { value: false, label: 'No Coping',     desc: 'L-Flashing & Counter Flashing only.' },
                                                    { value: true,  label: 'Include Coping', desc: 'Add coping cap over parapet wall.' },
                                                ].map(opt => (
                                                    <Box key={String(opt.value)}
                                                        onClick={() => { setTpoParapetCoping(opt.value); }}
                                                        sx={{
                                                            flex: 1, p: 1.25, borderRadius: 1, border: '1px solid',
                                                            textAlign: 'center',
                                                            borderColor: tpoParapetCoping === opt.value ? 'warning.main' : 'divider',
                                                            bgcolor: tpoParapetCoping === opt.value ? 'warning.50' : 'background.paper',
                                                            cursor: 'pointer',
                                                            '&:hover': { borderColor: 'warning.light', bgcolor: 'action.hover' },
                                                            transition: 'all 0.15s',
                                                        }}
                                                    >
                                                        <Typography variant="body2" fontWeight={tpoParapetCoping === opt.value ? 700 : 400}>{opt.label}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{opt.desc}</Typography>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Box>
                                    </Stack>
                                </Box>
                            )}

                            {/* BUILDUP — 4.5 Parapet Termination (only when flat_parapet > 0) */}
                            {showFlat && flatSystemId === 'BUILDUP' && inputs.flat_parapet > 0 && (
                                <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'warning.main' }}>
                                    <Typography variant="caption" color="warning.dark" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
                                        Parapet Termination — {inputs.flat_parapet} LF
                                    </Typography>
                                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1.25, fontStyle: 'italic' }}>
                                        L-Flashing + Counter Flashing always included.
                                    </Typography>
                                    <Stack spacing={1.5}>
                                        {/* Metal type */}
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.75 }}>
                                                Flashing Metal
                                            </Typography>
                                            <Stack direction="row" spacing={1}>
                                                {BUILDUP_PARAPET_METALS.map(opt => (
                                                    <Box key={opt.id}
                                                        onClick={() => setBuiltupParapetMetal(opt.id)}
                                                        sx={{
                                                            flex: 1, p: 1.25, borderRadius: 1, border: '1px solid',
                                                            textAlign: 'center',
                                                            borderColor: builtupParapetMetal === opt.id ? 'warning.main' : 'divider',
                                                            bgcolor: builtupParapetMetal === opt.id ? 'warning.50' : 'background.paper',
                                                            cursor: 'pointer',
                                                            '&:hover': { borderColor: 'warning.light', bgcolor: 'action.hover' },
                                                            transition: 'all 0.15s',
                                                        }}
                                                    >
                                                        <Typography variant="body2" fontWeight={builtupParapetMetal === opt.id ? 700 : 400}>
                                                            {opt.label}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {opt.description}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Box>

                                        {/* Coping cap toggle */}
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.75 }}>
                                                Coping Cap
                                            </Typography>
                                            <Stack direction="row" spacing={1}>
                                                {[
                                                    { value: false, label: 'No Coping',      desc: 'L-Flashing & Counter Flashing only.' },
                                                    { value: true,  label: 'Include Coping',  desc: 'Add coping cap over parapet wall.' },
                                                ].map(opt => (
                                                    <Box key={String(opt.value)}
                                                        onClick={() => setBuiltupParapetCoping(opt.value)}
                                                        sx={{
                                                            flex: 1, p: 1.25, borderRadius: 1, border: '1px solid',
                                                            textAlign: 'center',
                                                            borderColor: builtupParapetCoping === opt.value ? 'warning.main' : 'divider',
                                                            bgcolor: builtupParapetCoping === opt.value ? 'warning.50' : 'background.paper',
                                                            cursor: 'pointer',
                                                            '&:hover': { borderColor: 'warning.light', bgcolor: 'action.hover' },
                                                            transition: 'all 0.15s',
                                                        }}
                                                    >
                                                        <Typography variant="body2" fontWeight={builtupParapetCoping === opt.value ? 700 : 400}>
                                                            {opt.label}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {opt.desc}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Box>
                                    </Stack>
                                </Box>
                            )}

                            {/* Flat roof measurements (active roof) */}
                            {showFlat && (
                                <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'grey.400' }}>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                        Measurements — {activeFlat.label}
                                    </Typography>
                                    <Stack spacing={1}>
                                        <NumField label="Flat Area" value={activeFlat.area} unit="sqft"
                                            onChange={v => patchFlatAt(activeFlatIdx, { area: v })}
                                            required={workType !== 'Repairs'} />
                                        <NumField label="Flat Edge" value={activeFlat.edge} unit="LF"
                                            onChange={v => patchFlatAt(activeFlatIdx, { edge: v })} />
                                        <NumField label="Flat Parapet" value={activeFlat.parapet} unit="LF"
                                            onChange={v => patchFlatAt(activeFlatIdx, { parapet: v })} />
                                        <Grid container spacing={1}>
                                            <Grid item xs={6}>
                                                <NumField label="Scuppers" value={activeFlat.scuppers} unit="ea"
                                                    onChange={v => patchFlatAt(activeFlatIdx, { scuppers: v })} />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <NumField label="Drains" value={activeFlat.drains} unit="ea"
                                                    onChange={v => patchFlatAt(activeFlatIdx, { drains: v })} />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <NumField label="Plumbing" value={activeFlat.plumbingProjections} unit="ea"
                                                    onChange={v => patchFlatAt(activeFlatIdx, { plumbingProjections: v })} />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <NumField label="Ventilation" value={activeFlat.ventilationProjections} unit="ea"
                                                    onChange={v => patchFlatAt(activeFlatIdx, { ventilationProjections: v })} />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <NumField label="Plywood" value={activeFlat.woodPlywoodSheets} unit="sheets"
                                                    onChange={v => patchFlatAt(activeFlatIdx, { woodPlywoodSheets: v })} />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <NumField label="2×4 Lumber" value={activeFlat.woodLumberPieces} unit="pcs"
                                                    onChange={v => patchFlatAt(activeFlatIdx, { woodLumberPieces: v })} />
                                            </Grid>
                                        </Grid>
                                    </Stack>
                                </Box>
                            )}


                            {/* Machinery (Optional) — all roof systems */}
                            {((showSteep && steepRoofs.some(r => r.systemId !== 'NONE-STEEP')) ||
                              (showFlat && flatRoofs.some(r => r.systemId !== 'NONE-FLAT'))) && (
                                <Box sx={{ mt: 1, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={700}
                                        sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Machinery (Optional)
                                    </Typography>
                                    <Stack spacing={0.75}>
                                        {MACHINERY_OPTIONS.map(item => {
                                            const toggle = {
                                                scaraface:   { state: flatUseScaraface,   set: setFlatUseScaraface },
                                                telehandler: { state: flatUseTelehandler, set: setFlatUseTelehandler },
                                                boom:        { state: flatUseBoomLift,    set: setFlatUseBoomLift },
                                                bathroom:    { state: flatUseBathroom,    set: setFlatUseBathroom },
                                            }[item.key];
                                            return (
                                                <Box key={item.key}
                                                    onClick={() => { toggle.set(!toggle.state); }}
                                                    sx={{
                                                        p: 1.25, borderRadius: 1, border: '1px solid',
                                                        borderColor: toggle.state ? 'primary.main' : 'divider',
                                                        bgcolor: toggle.state ? 'primary.50' : 'background.paper',
                                                        cursor: 'pointer',
                                                        '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
                                                        transition: 'all 0.15s',
                                                    }}
                                                >
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <Box sx={{
                                                            width: 14, height: 14, borderRadius: 0.5,
                                                            border: '2px solid',
                                                            borderColor: toggle.state ? 'primary.main' : 'text.disabled',
                                                            bgcolor: toggle.state ? 'primary.main' : 'transparent',
                                                            flexShrink: 0,
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        }}>
                                                            {toggle.state && (
                                                                <Typography sx={{ color: 'white', fontSize: 9, fontWeight: 900, lineHeight: 1 }}>✓</Typography>
                                                            )}
                                                        </Box>
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="body2" fontWeight={toggle.state ? 700 : 400}>{item.label}</Typography>
                                                        </Box>
                                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>{item.price}</Typography>
                                                    </Stack>
                                                </Box>
                                            );
                                        })}
                                    </Stack>
                                </Box>
                            )}

                            {showFlat && (
                                <Box sx={{ mt: 1, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={700}
                                        sx={{ display: 'block', mb: 0.75 }}>
                                        Flat Margin
                                    </Typography>
                                    <Grid container spacing={1}>
                                        <Grid item xs={6}>
                                            <TextField label="Overhead" type="number" size="small"
                                                value={Math.round(activeFlat.overhead_pct * 100)}
                                                onChange={e => patchFlatAt(activeFlatIdx, { overhead_pct: parseFloat(e.target.value) / 100 || 0 })}
                                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                                inputProps={{ min: 0, max: 100 }} fullWidth />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField label="Profit" type="number" size="small"
                                                value={Math.round(activeFlat.profit_pct * 100)}
                                                onChange={e => patchFlatAt(activeFlatIdx, { profit_pct: parseFloat(e.target.value) / 100 || 0 })}
                                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                                inputProps={{ min: 0, max: 200 }} fullWidth />
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}


                            {showSteep && (
                                <Box sx={{ mt: 1, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={700}
                                        sx={{ display: 'block', mb: 0.75 }}>
                                        Steep Margin
                                    </Typography>
                                    <Grid container spacing={1}>
                                        <Grid item xs={6}>
                                            <TextField label="Overhead" type="number" size="small"
                                                value={Math.round(activeSteep.overhead_pct * 100)}
                                                onChange={e => patchSteepAt(activeSteepIdx, { overhead_pct: parseFloat(e.target.value) / 100 || 0 })}
                                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                                inputProps={{ min: 0, max: 100 }} fullWidth />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField label="Profit" type="number" size="small"
                                                value={Math.round(activeSteep.profit_pct * 100)}
                                                onChange={e => patchSteepAt(activeSteepIdx, { profit_pct: parseFloat(e.target.value) / 100 || 0 })}
                                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                                inputProps={{ min: 0, max: 200 }} fullWidth />
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                        </Stack>
                    </SectionCard>
                )}

                {/* Step 5 — Margin & Calculate */}
                {roofType && (
                    <SectionCard step={5} title="Margin & Calculate">
                        <Stack spacing={1.5}>
                            <Typography variant="caption" fontWeight={700} color="primary.main">Commission</Typography>
                            <TextField label="Commission" type="number" size="small"
                                value={Math.round(commissionPct * 100)}
                                onChange={e => setCommissionPct(parseFloat(e.target.value) / 100 || 0)}
                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                inputProps={{ min: 0, max: 50 }} fullWidth />

                            {!canCalculate && workType && roofType && (
                                (!selectedSteepSystem?.ready && showSteep) || !isRepairs
                            ) && (
                                <Alert severity="info" sx={{ fontSize: 12 }}>
                                    {!selectedSteepSystem?.ready && showSteep
                                        ? `${selectedSteepSystem?.label} pricing coming soon.`
                                        : 'Enter area on at least one roof to calculate.'}
                                </Alert>
                            )}

                            {needsRecalc && (
                                <Alert severity="warning" sx={{ fontSize: 12 }}>
                                    Inputs changed — click Update to refresh the Cost Sheet.
                                </Alert>
                            )}

                            <Button variant="contained" fullWidth size="large"
                                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CalculateIcon />}
                                onClick={handleCalculate}
                                disabled={!canCalculate || loading}
                                sx={{ fontWeight: 700, py: 1.2 }}>
                                {loading
                                    ? (result ? 'Updating…' : 'Calculating…')
                                    : (result ? 'Update' : 'Calculate')}
                            </Button>
                        </Stack>
                    </SectionCard>
                )}
            </Box>

            {/* ══ RIGHT PANEL ═══════════════════════════════════════════════ */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#f5f6fa' }}>

                {/* Tabs bar */}
                {showRightPanel && (
                    <Box sx={(theme) => ({
                        borderBottom: 1,
                        borderColor: 'divider',
                        px: 2,
                        [theme.breakpoints.down(768)]: {
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            flexWrap: 'nowrap',
                            overflow: 'hidden',
                        },
                    })}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={(theme) => ({
                                width: '100%',
                                [theme.breakpoints.down(768)]: {
                                    flex: 1,
                                    gap: 3,
                                    paddingBottom: 3,
                                    minWidth: 0,
                                    flexDirection: 'column',
                                    flexWrap: 'nowrap',
                                },
                            })}
                        >
                            <Tabs
                                value={rightTab}
                                onChange={(_, v: RightTabId) => setRightTab(v)}
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={(theme) => ({
                                    minHeight: 44,
                                    [theme.breakpoints.down(768)]: {
                                        flex: 1,
                                        minWidth: 0,
                                    },
                                })}
                            >
                                <Tab
                                    value={TAB_ENGINEER}
                                    icon={<ArchitectureIcon sx={{ fontSize: 16 }} />}
                                    iconPosition="start"
                                    label="Measurements"
                                    sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44, fontSize: 13 }}
                                />
                                {/* MUI Tabs rejects Fragment children — keep each Tab a direct child */}
                                {result && (
                                    <Tab
                                        value={TAB_COST}
                                        icon={<TableChartIcon sx={{ fontSize: 16 }} />}
                                        iconPosition="start"
                                        label="Cost Sheet"
                                        sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44, fontSize: 13 }}
                                    />
                                )}
                                {result && (
                                    <Tab
                                        value={TAB_ATTACHMENTS}
                                        icon={<AttachFileIcon sx={{ fontSize: 16 }} />}
                                        iconPosition="start"
                                        label="Attachments"
                                        sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44, fontSize: 13 }}
                                    />
                                )}
                                {result && (
                                    <Tab
                                        value={TAB_CLIENT_ESTIMATE}
                                        icon={<ArticleIcon sx={{ fontSize: 16 }} />}
                                        iconPosition="start"
                                        label="Client Estimate"
                                        sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44, fontSize: 13 }}
                                    />
                                )}
                                {result && (
                                    <Tab
                                        value={TAB_RELATED_PEOPLE}
                                        icon={<PeopleIcon sx={{ fontSize: 16 }} />}
                                        iconPosition="start"
                                        label="Related People"
                                        sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44, fontSize: 13 }}
                                    />
                                )}
                                {result && showFlat && flatSystemId === 'BUILDUP' && (
                                    <Tab
                                        value={TAB_SYSTEM_SPEC}
                                        icon={<LayersIcon sx={{ fontSize: 16 }} />}
                                        iconPosition="start"
                                        label="System Spec"
                                        sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44, fontSize: 13 }}
                                    />
                                )}
                            </Tabs>
                        </Stack>
                    </Box>
                )}

                {/* Content */}
                <Box sx={{ flex: 1, overflowY: 'auto', p: rightTab === TAB_ENGINEER ? 0 : 2, display: 'flex', flexDirection: 'column', minHeight: 0 }}>

                    {/* Empty state */}
                    {!showRightPanel && (
                        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'text.disabled', gap: 2 }}>
                            <RoofingIcon sx={{ fontSize: 80, opacity: 0.15 }} />
                            <Typography variant="body1" color="text.disabled">Fill in the form and hit Calculate</Typography>
                            <Typography variant="caption" color="text.disabled">Results will appear here</Typography>
                        </Box>
                    )}

                    {/* Engineer Software */}
                    {rightTab === TAB_ENGINEER && (
                        <ArchitectEngineerTab
                            files={viewFiles}
                            onFilesChange={setViewFiles}
                            initialProject={architectProject}
                            onChange={setArchitectProject}
                        />
                    )}

                    {/* Tab — Cost Sheet */}
                    {result && rightTab === TAB_COST && (
                        <Paper variant="outlined" sx={{
                            p: 3, bgcolor: 'transparent', border: 'none', width: '100%',
                            fontFamily: 'Arial, sans-serif',
                        }}>
                            <Stack spacing={2}>
                                {needsRecalc && (
                                    <Alert severity="warning" sx={{ fontSize: 12 }}>
                                        Inputs changed — click Update to refresh the Cost Sheet.
                                    </Alert>
                                )}
                                {/* Banner */}
                                <Card variant="outlined" sx={{ bgcolor: 'primary.main', color: 'white' }}>
                                    <CardContent sx={{ py: '12px !important' }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Box>
                                                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                                    {workType} · {roofType} · {stories} {stories === 1 ? 'Story' : 'Stories'}
                                                </Typography>
                                                <Typography variant="h5" fontWeight={700} fontFamily="monospace">
                                                    ${clientPrice.toLocaleString()}
                                                </Typography>
                                                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                                    Client price (rounded up, no cents)
                                                </Typography>
                                            </Box>
                                            <Stack alignItems="flex-end" spacing={0.5}>
                                                <Chip label={`Hard Cost: ${fmt(adjustedHardCost)}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: 12 }} />
                                                <Chip label={`Margin: ${clientPrice > 0 ? Math.round(((clientPrice - adjustedHardCost) / clientPrice) * 100) : 0}%`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: 12 }} />
                                                <Chip label={`Net: ${fmt(netToCompany)}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: 12 }} />
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </Card>

                                {/* Line items */}
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>Cost Breakdown</Typography>
                                        {(result.steep_roofs ?? (result.steep ? [result.steep] : [])).map(section => {
                                            const margin = marginForRoofId(section.roof_id, steepRoofs, flatRoofs);
                                            return (
                                            <EditableSectionBreakdown
                                                key={section.roof_id ?? section.system_id}
                                                section={section}
                                                title={section.label || 'Steep Roof'}
                                                sectionKey="steep"
                                                overrides={lineOverrides} onOverride={overrideQty}
                                                unitCostOverrides={unitCostOverrides} onUnitCostOverride={overrideUnitCost}
                                                onResetLine={resetLine}
                                                disabledItemIds={disabledItemIds}
                                                onDisableCatalog={disableCatalogLine}
                                                onEnableCatalog={enableCatalogLine}
                                                extras={extraLines}
                                                onAdd={() => addExtraLine('steep')}
                                                onUpdate={updateExtraLine}
                                                onRemove={removeExtraLine}
                                                overhead_pct={margin.overhead_pct}
                                                profit_pct={margin.profit_pct}
                                                areaSqft={areaForRoofId(section.roof_id, steepRoofs, flatRoofs)}
                                                commissionPct={commissionPct}
                                            />
                                            );
                                        })}
                                        {(result.flat_roofs ?? (result.flat ? [result.flat] : [])).map(section => {
                                            const margin = marginForRoofId(section.roof_id, steepRoofs, flatRoofs);
                                            return (
                                            <EditableSectionBreakdown
                                                key={section.roof_id ?? section.system_id}
                                                section={section}
                                                title={section.label || 'Flat Roof'}
                                                sectionKey="flat"
                                                overrides={lineOverrides} onOverride={overrideQty}
                                                unitCostOverrides={unitCostOverrides} onUnitCostOverride={overrideUnitCost}
                                                onResetLine={resetLine}
                                                disabledItemIds={disabledItemIds}
                                                onDisableCatalog={disableCatalogLine}
                                                onEnableCatalog={enableCatalogLine}
                                                extras={extraLines}
                                                onAdd={() => addExtraLine('flat')}
                                                onUpdate={updateExtraLine}
                                                onRemove={removeExtraLine}
                                                overhead_pct={margin.overhead_pct}
                                                profit_pct={margin.profit_pct}
                                                areaSqft={areaForRoofId(section.roof_id, steepRoofs, flatRoofs)}
                                                commissionPct={commissionPct}
                                            />
                                            );
                                        })}
                                        {result.job && (
                                            <EditableSectionBreakdown
                                                section={result.job}
                                                title="Job Site"
                                                sectionKey="other"
                                                overrides={lineOverrides} onOverride={overrideQty}
                                                unitCostOverrides={unitCostOverrides} onUnitCostOverride={overrideUnitCost}
                                                onResetLine={resetLine}
                                                disabledItemIds={disabledItemIds}
                                                onDisableCatalog={disableCatalogLine}
                                                onEnableCatalog={enableCatalogLine}
                                                extras={extraLines}
                                                onAdd={() => addExtraLine('other')}
                                                onUpdate={updateExtraLine}
                                                onRemove={removeExtraLine}
                                                overhead_pct={jobOtherMargin.overhead_pct}
                                                profit_pct={jobOtherMargin.profit_pct}
                                            />
                                        )}
                                        {/* General extra lines not tied to a section */}
                                        {otherExtras.length > 0 && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="caption" fontWeight={700} color="text.secondary"
                                                    sx={{ mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}>
                                                    Additional Items
                                                </Typography>
                                                <TableContainer component={Paper} variant="outlined">
                                                    <Table size="small">
                                                        <TableBody>
                                                            {otherExtras.map(extra => (
                                                                <TableRow key={extra.id} sx={{ bgcolor: 'success.50' }}>
                                                                    <TableCell><TextField size="small" variant="standard" placeholder="Description" value={extra.name} onChange={e => updateExtraLine(extra.id, 'name', e.target.value)} inputProps={{ style: { fontSize: 12 } }} sx={{ minWidth: 160 }} /></TableCell>
                                                                    <TableCell><TextField size="small" type="number" variant="outlined" value={extra.quantity} onChange={e => updateExtraLine(extra.id, 'quantity', parseFloat(e.target.value) || 0)} inputProps={{ min: 0, step: 1, style: { textAlign: 'right', padding: '2px 6px', fontSize: 12, width: 60 } }} /></TableCell>
                                                                    <TableCell><TextField size="small" variant="standard" placeholder="unit" value={extra.unit} onChange={e => updateExtraLine(extra.id, 'unit', e.target.value)} inputProps={{ style: { fontSize: 11, width: 34 } }} /></TableCell>
                                                                    <TableCell><TextField size="small" type="number" variant="outlined" value={extra.unit_cost} onChange={e => updateExtraLine(extra.id, 'unit_cost', parseFloat(e.target.value) || 0)} inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', padding: '2px 6px', fontSize: 12, width: 72 } }} /></TableCell>
                                                                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: 'success.dark' }}>{fmt(extra.quantity * extra.unit_cost)}</TableCell>
                                                                    <TableCell><IconButton size="small" onClick={() => removeExtraLine(extra.id)} sx={{ p: 0.25, color: 'error.light' }}><DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton></TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                                <Stack spacing={0.5} sx={{ mt: 1, px: 0.5 }}>
                                                    <SummaryRow
                                                        label={`Overhead (${Math.round(jobOtherMargin.overhead_pct * 100)}%)`}
                                                        value={otherOverhead}
                                                    />
                                                    <SummaryRow
                                                        label={`Profit (${Math.round(jobOtherMargin.profit_pct * 100)}%)`}
                                                        value={otherProfit}
                                                    />
                                                </Stack>
                                            </Box>
                                        )}
                                        <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={() => addExtraLine('other')}
                                            sx={{ fontSize: 12, color: 'success.main', textTransform: 'none', mb: 1 }}>
                                            Add general line item
                                        </Button>
                                    </CardContent>
                                </Card>

                                {/* Internal summary */}
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Internal Summary</Typography>
                                        <Stack spacing={0.75}>
                                            <SummaryRow label="Hard Cost"                                                 value={adjustedHardCost} />
                                            <SummaryRow label="Overhead"                                                 value={adjustedOverhead} />
                                            <SummaryRow label="Profit"                                                   value={adjustedProfit} />
                                            <Divider />
                                            <SummaryRow label="Price Before Commission" value={priceBeforeCommission} />
                                            <SummaryRow label={`Commission (${Math.round(commissionPct * 100)}%)`} value={commissionAmount} />
                                            <Divider />
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography variant="h6" fontWeight={700} color="primary.main">Price to Client</Typography>
                                                <Typography variant="h6" fontWeight={700} fontFamily="monospace" color="primary.main">
                                                    ${clientPrice.toLocaleString()}
                                                </Typography>
                                            </Stack>
                                            {((showSteep ? totalSteepArea(steepRoofs) : 0) + (showFlat ? totalFlatArea(flatRoofs) : 0)) > 0 && (
                                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: -0.5 }}>
                                                    <Typography variant="caption" color="text.disabled">Price / sqft ({((showSteep ? totalSteepArea(steepRoofs) : 0) + (showFlat ? totalFlatArea(flatRoofs) : 0))})</Typography>
                                                    <Typography variant="caption" fontFamily="monospace" color="text.disabled" fontWeight={600}>
                                                        ${(clientPrice / ((showSteep ? totalSteepArea(steepRoofs) : 0) + (showFlat ? totalFlatArea(flatRoofs) : 0))).toFixed(2)} / sqft
                                                    </Typography>
                                                </Stack>
                                            )}
                                            <Divider />
                                            <SummaryRow label="Net to Company" value={netToCompany} bold />
                                        </Stack>
                                    </CardContent>
                                </Card>

                                {/* Payment schedule */}
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Payment Schedule</Typography>
                                        <Stack spacing={0.75}>
                                            {[
                                                { label: '30% — Signing of Contract', pct: 0.30 },
                                                { label: '20% — Project Start',       pct: 0.20 },
                                                { label: '30% — Mid-Inspection',      pct: 0.30 },
                                                { label: '20% — Final Inspection',    pct: 0.20 },
                                            ].map(row => (
                                                <Stack key={row.label} direction="row" justifyContent="space-between">
                                                    <Typography variant="body2" color="text.secondary">{row.label}</Typography>
                                                    <Typography variant="body2" fontFamily="monospace" fontWeight={600}>{fmt(clientPrice * row.pct)}</Typography>
                                                </Stack>
                                            ))}
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Stack>
                        </Paper>
                    )}

                    {/* Tab 1 — Attachments */}
                    {result && rightTab === TAB_ATTACHMENTS && (
                        <QuoteAttachmentsTab
                            clientEstimateAttachments={attachments}
                            onClientEstimateChange={setAttachments}
                            relatedQuoteFiles={relatedQuoteFiles}
                            onRelatedQuoteChange={setRelatedQuoteFiles}
                            viewFiles={viewFiles}
                            onViewFilesChange={setViewFiles}
                            quoteId={editingQuoteId}
                            isLegacy={!!quoteRecord?.is_legacy}
                        />
                    )}

                    {/* Tab — Related People */}
                    {result && rightTab === TAB_RELATED_PEOPLE && (
                        <QuoteRelatedPeopleTab projectId={selectedProjectId} />
                    )}

                    {/* Tab 2 — Client Estimate */}
                    {result && rightTab === TAB_CLIENT_ESTIMATE && (
                        <ClientEstimateDoc
                            result={result}
                            clientPrice={clientPrice}
                            clientInfo={clientInfo}
                            workType={workType!}
                            roofType={roofType!}
                            stories={stories}
                            steepSystemId={steepSystemId}
                            flatSystemId={flatSystemId}
                            showSteep={showSteep}
                            showFlat={showFlat}
                            steepRoofs={steepRoofs}
                            flatRoofs={flatRoofs}
                            inputs={inputs}
                            flatSubstrate={flatSubstrate}
                            shingleUnderlaymentId={shingleUnderlaymentId}
                            tileUnderlaymentId={tileUnderlaymentId}
                            tileTypeId={tileTypeId}
                            tileFlashingMetal={tileFlashingMetal}
                            tileFlashingSize={tileFlashingSize}
                            metalUnderlaymentId={metalUnderlaymentId}
                            metalTypeId={METAL_TYPE_OPTIONS.find(m => m.id === metalTypeId)?.systemId ?? 'METAL-TYPE-GAL'}
                            builtupInsulationId={builtupInsulationId}
                            builtupTaperedInsId={builtupTaperedInsId}
                            builtupTaperedIsoQty={resolvedBuiltupTaperedIsoQty}
                            builtupFlatIsoQty={resolvedBuiltupFlatIsoQty}
                            builtupR20Qty={builtupR20Qty}
                            builtupSmoothQty={builtupSmoothQty}
                            builtupSm15Qty={builtupSm15Qty}
                            builtupCapId={builtupCapId}
                            builtupEdgeMetal={builtupEdgeMetal}
                            builtupEdgeSize={builtupEdgeSize}
                            contractEstimateNumber={contractEstimateNumber}
                            polyglassIpQty={polyglassIpQty}
                            polyglassEdgeMetal={polyglassEdgeMetal}
                            polyglassEdgeSize={polyglassEdgeSize}
                            flatUseScaraface={flatUseScaraface}
                            flatUseTelehandler={flatUseTelehandler}
                            flatUseBoomLift={flatUseBoomLift}
                            flatUseBathroom={flatUseBathroom}
                            wpSbWarranty={wpSbWarranty}
                            tpoAttachment={tpoAttachment}
                            tpoCoverboard={tpoCoverboard}
                            tpoTaperedIsoQty={resolvedTpoTaperedIsoQty}
                            tpoFlatIsoQty={resolvedTpoFlatIsoQty}
                            attachments={attachments}
                            scopeHtml={scopeHtml}
                            scopeEditing={scopeEditing}
                            pendingScopeHtml={pendingScopeHtml}
                            onScopeEditStart={() => {
                                if (!scopeHtml) {
                                    const seeded = scopeLinesToHtml(buildClientEstimateScopeLines({
                                        steepRoofs, flatRoofs,
                                        flatUseScaraface, flatUseTelehandler, flatUseBoomLift, flatUseBathroom,
                                    }));
                                    setScopeHtml(seeded);
                                    setGeneratedScopeHtml(seeded);
                                }
                                setScopeEditing(true);
                            }}
                            onScopeEditSave={html => {
                                setScopeHtml(html);
                                setScopeEditing(false);
                                if (pendingScopeHtml && html === pendingScopeHtml) {
                                    setPendingScopeHtml(null);
                                }
                            }}
                            onScopeEditCancel={() => setScopeEditing(false)}
                            onApplyPendingScope={() => {
                                if (!pendingScopeHtml) return;
                                setScopeHtml(pendingScopeHtml);
                                setGeneratedScopeHtml(pendingScopeHtml);
                                setPendingScopeHtml(null);
                            }}
                            woodHtml={woodHtml}
                            woodEditing={woodEditing}
                            pendingWoodHtml={pendingWoodHtml}
                            onWoodEditStart={() => {
                                if (!woodHtml) {
                                    const wood = totalWoodQuantities(
                                        steepRoofs, flatRoofs,
                                        { includeSteep: showSteep, includeFlat: showFlat },
                                    );
                                    const seeded = defaultWoodHtml(wood.plywoodSheets, wood.lumberPieces);
                                    setWoodHtml(seeded);
                                    setGeneratedWoodHtml(seeded);
                                }
                                setWoodEditing(true);
                            }}
                            onWoodEditSave={html => {
                                setWoodHtml(html);
                                setWoodEditing(false);
                                if (pendingWoodHtml && html === pendingWoodHtml) {
                                    setPendingWoodHtml(null);
                                }
                            }}
                            onWoodEditCancel={() => setWoodEditing(false)}
                            onApplyPendingWood={() => {
                                if (!pendingWoodHtml) return;
                                setWoodHtml(pendingWoodHtml);
                                setGeneratedWoodHtml(pendingWoodHtml);
                                setPendingWoodHtml(null);
                            }}
                            permitsHtml={permitsHtml}
                            permitsHidden={permitsHidden}
                            permitsEditing={permitsEditing}
                            pendingPermitsHtml={pendingPermitsHtml}
                            onPermitsEditStart={() => {
                                if (!permitsHtml) {
                                    const seeded = defaultPermitsHtml();
                                    setPermitsHtml(seeded);
                                    setGeneratedPermitsHtml(seeded);
                                }
                                setPermitsEditing(true);
                            }}
                            onPermitsEditSave={html => {
                                setPermitsHtml(html);
                                setPermitsEditing(false);
                                if (pendingPermitsHtml && html === pendingPermitsHtml) {
                                    setPendingPermitsHtml(null);
                                }
                            }}
                            onPermitsEditCancel={() => setPermitsEditing(false)}
                            onApplyPendingPermits={() => {
                                if (!pendingPermitsHtml) return;
                                setPermitsHtml(pendingPermitsHtml);
                                setGeneratedPermitsHtml(pendingPermitsHtml);
                                setPendingPermitsHtml(null);
                            }}
                            onPermitsHiddenChange={setPermitsHidden}
                            completionDays={completionDays}
                            onCompletionDaysChange={setCompletionDays}
                            clientNameSource={clientNameSource}
                            onClientNameSourceChange={setClientNameSource}
                            ownerName={selectedLead?.name ?? ''}
                            contractorName={
                                (useAsContractor ? selectedLead : selectedContractor)?.name ?? ''
                            }
                        />
                    )}

                    {/* Tab 3 — System Spec (BUILDUP only) */}
                    {result && rightTab === TAB_SYSTEM_SPEC && showFlat && flatSystemId === 'BUILDUP' && (
                        <BuildupSystemCard
                            flatSystemId={flatSystemId}
                            flatSubstrate={flatSubstrate}
                            insId={builtupInsulationId}
                            r20Qty={builtupR20Qty}
                            smoothQty={builtupSmoothQty}
                            sm15Qty={builtupSm15Qty}
                            capId={builtupCapId}
                            edgeMetal={builtupEdgeMetal}
                            edgeSize={builtupEdgeSize}
                            parapetMetal={builtupParapetMetal}
                            parapetCoping={builtupParapetCoping}
                            flatEdge={inputs.flat_edge}
                            flatParapet={inputs.flat_parapet}
                            plumbingProjections={inputs.plumbing_projections}
                        />
                    )}
                </Box>

                {showSaveFooter && (
                    <Box
                        sx={{
                            flexShrink: 0,
                            borderTop: 1,
                            borderColor: 'divider',
                            bgcolor: 'white',
                            pl: 2,
                            pr: 10,
                            py: 1.5,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            gap: 1.5,
                        }}
                    >
                        {result && (
                            <Chip
                                label={`Client: $${clientPrice.toLocaleString()}`}
                                color="primary"
                                sx={{ flexShrink: 0, fontFamily: 'monospace', fontWeight: 700, fontSize: 15 }}
                            />
                        )}
                        {!result ? (
                            showTemporalSave && (
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={savingQuote ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                                    onClick={() => requestSaveQuote(quoteStatuses.pending_finish_quoting)}
                                    disabled={!canTemporalSave || savingQuote}
                                    sx={{ textTransform: 'none', fontWeight: 600 }}
                                >
                                    {savingQuote ? 'Saving…' : 'Temporal Save'}
                                </Button>
                            )
                        ) : !showTemporalSave ? (
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={savingQuote ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                                onClick={() => requestSaveQuote(quoteStatuses.pending)}
                                disabled={!canSaveQuote || savingQuote}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                {savingQuote ? 'Saving…' : 'Save quote'}
                            </Button>
                        ) : (
                            <>
                                <ButtonGroup
                                    variant="contained"
                                    color="secondary"
                                    disabled={!canSaveQuote || savingQuote}
                                    sx={{ textTransform: 'none', fontWeight: 600 }}
                                >
                                    <Button
                                        startIcon={savingQuote ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                                        onClick={() => requestSaveQuote(quoteStatuses.pending)}
                                        sx={{ textTransform: 'none', fontWeight: 600 }}
                                    >
                                        {savingQuote ? 'Saving…' : 'Save quote'}
                                    </Button>
                                    <Button
                                        size="small"
                                        aria-label="More save options"
                                        aria-haspopup="menu"
                                        aria-expanded={saveMenuAnchor ? 'true' : undefined}
                                        onClick={(e) => setSaveMenuAnchor(e.currentTarget)}
                                        sx={{ px: 0.75 }}
                                    >
                                        <ArrowDropDownIcon />
                                    </Button>
                                </ButtonGroup>
                                <Menu
                                    anchorEl={saveMenuAnchor}
                                    open={Boolean(saveMenuAnchor)}
                                    onClose={() => setSaveMenuAnchor(null)}
                                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                    transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                >
                                    <MenuItem
                                        disabled={!canTemporalSave || savingQuote}
                                        onClick={() => requestSaveQuote(quoteStatuses.pending_finish_quoting)}
                                    >
                                        Temporal Save
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                    </Box>
                )}
            </Box>
            <Confirm
                isOpen={voidSigningConfirmOpen}
                title="Contract signing in progress"
                content={CONTRACT_SIGNING_VOID_MESSAGE}
                confirm="Continue"
                onConfirm={() => {
                    const pending = voidSigningPending;
                    setVoidSigningConfirmOpen(false);
                    setVoidSigningPending(null);
                    if (pending) {
                        void handleSaveQuote(pending.targetStatus, pending.priceChangeReason, true);
                    }
                }}
                onClose={() => {
                    setVoidSigningConfirmOpen(false);
                    setVoidSigningPending(null);
                }}
            />
            <Dialog
                open={priceReasonOpen}
                onClose={() => !savingQuote && setPriceReasonOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Price change reason</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Total price changed from{' '}
                        <strong>
                            ${(savedPriceBaselineRef.current ?? 0).toLocaleString()}
                        </strong>
                        {' '}to{' '}
                        <strong>${clientPrice.toLocaleString()}</strong>.
                        Enter a short reason (3–32 characters) explaining why.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Reason"
                        value={priceReason}
                        onChange={(e) => {
                            setPriceReason(e.target.value);
                            if (priceReasonError) setPriceReasonError('');
                        }}
                        error={!!priceReasonError}
                        helperText={priceReasonError || '3–32 characters'}
                        disabled={savingQuote}
                        inputProps={{ maxLength: PRICE_CHANGE_REASON_MAX }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                confirmPriceChangeReason();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setPriceReasonOpen(false)}
                        disabled={savingQuote}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={confirmPriceChangeReason}
                        disabled={savingQuote}
                    >
                        Continue save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
